import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";

import { toast } from "react-toastify";
import StandardDatePicker from "./StandardDatePicker";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { formatDate, formatDateTime } from "../helper/DateHelper";
import usePermissions from "../hook/userPermission";
import {
  getPresentExpos,
  getExpoById,
  getStallsByExpo,
  deleteExpo,
} from "../Apis/ExpoAPIs";
import { getStallTypes } from "../Apis/StallTypeAPIs";
import { getVisitorEnquiriesByExpo, downloadVisitorEnquiriesExcel, sendBulkVisitorSMS } from "../Apis/VisitorEnquiryAPIs";
import { getKnownSources } from "../Apis/KnownSourceAPIs";
import {
  getStallEnquiriesByExpo,
  saveStallEnquiry,
  updateStallEnquiryStatus,
  downloadEnquirySample,
  bulkUploadEnquiries,
} from "../Apis/StallEnquiryAPIs";
import { getCategories } from "../Apis/CategoryAPIs";
import { getProducts } from "../Apis/ProductAPIs";
import { uploadFile, getManagers } from "../Apis/ManagerAPIs";
import { getImageUrl } from "../Apis/api";
import { getClients, getClientFollowups, updateClientFollowupStatus, editStallFinancials, getClientFollowupHistory, getClientFollowupPayments, verifyPaymentStatus, submitAdminPayment, submitDirectStallBooking, bulkAssignFollowups } from "../Apis/ClientAPIs";
import { getLocations } from "../Apis/MasterAPIs";
import { getClientGroups } from "../Apis/ClientGroupAPIs";
import TablePagination from "./TablePagination";

const formatIndianNumber = (val) => {
  if (!val && val !== 0) return "";
  let str = val.toString().replace(/,/g, "");
  if (isNaN(str)) return str;

  let parts = str.split(".");
  let lastThree = parts[0].substring(parts[0].length - 3);
  let otherNumbers = parts[0].substring(0, parts[0].length - 3);
  if (otherNumbers !== "") {
    lastThree = "," + lastThree;
  }
  let res = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
  if (parts.length > 1) {
    res += "." + parts[1];
  }
  return res;
};

// ── Stall Enquiry Options ───────────────────────────────────────────────────
const ENQUIRY_STATUS_OPTIONS = [
  { value: "Enquiry", label: "Enquiry" },
  { value: "Followup", label: "Followup" },
  { value: "Rejected", label: "Rejected" },
  { value: "Booked", label: "Booked" },
];

// MultiSelectProduct has been replaced by the standard react-select component for consistency.

// ───────────────────────────────────────────────────────────────────────────

const PresentExpoLayer = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeView, setActiveView] = useState("grid");
  // ── Client Followups State ────────────────────────────────────────────────
  const [clientFollowupsList, setClientFollowupsList] = useState([]);
  const [clientFollowupStatus, setClientFollowupStatus] = useState("Assignes");
  const [managersList, setManagersList] = useState([]);
  const [clientGroupsList, setClientGroupsList] = useState([]); // eslint-disable-line no-unused-vars
  const [selectedManagerFilter, setSelectedManagerFilter] = useState(null);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState(null);
  const [clientFollowupSearchTerm, setClientFollowupSearchTerm] = useState("");
  const [clientFollowupsTotal, setClientFollowupsTotal] = useState(0);
  const [clientFollowupsPage, setClientFollowupsPage] = useState(0);
  const [clientFollowupsLimit, setClientFollowupsLimit] = useState(10);
  const [clientFollowupsLoading, setClientFollowupsLoading] = useState(false);
  const [showClientDetailModal, setShowClientDetailModal] = useState(false);
  const [showClientHistoryModal, setShowClientHistoryModal] = useState(false);
  const [selectedClientForModal, setSelectedClientForModal] = useState(null);
  const [clientFollowupHistory, setClientFollowupHistory] = useState([]);
  const [showBlockDetailModal, setShowBlockDetailModal] = useState(false);
  const [selectedBlockItem, setSelectedBlockItem] = useState(null);
  const [selectedBlockPayments, setSelectedBlockPayments] = useState([]);
  const [adminPaymentMethod, setAdminPaymentMethod] = useState("");
  const [adminPaymentAmount, setAdminPaymentAmount] = useState("");
  const [adminPaymentProofFile, setAdminPaymentProofFile] = useState(null);
  const [adminPaymentRemarks, setAdminPaymentRemarks] = useState("");
  const [adminPaymentTransactionId, setAdminPaymentTransactionId] = useState("");
  const [adminPaymentBankName, setAdminPaymentBankName] = useState("");
  const [adminPaymentBranchName, setAdminPaymentBranchName] = useState("");
  const [adminPaymentSubmitting, setAdminPaymentSubmitting] = useState(false);

  const handleAdminPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBlockItem) return;

    if (adminPaymentMethod === "UPI" && !adminPaymentProofFile) {
      toast.error("Payment proof image is required for UPI payments");
      return;
    }

    if (adminPaymentMethod === "Cash" && (!adminPaymentRemarks || adminPaymentRemarks.trim() === "")) {
      toast.error("Remarks are required for Cash payments");
      return;
    }

    if (adminPaymentMethod === "Cheque") {
      if (!adminPaymentTransactionId || adminPaymentTransactionId.trim() === "") {
        toast.error("Cheque No / Transaction ID is required");
        return;
      }
      if (!adminPaymentBankName || adminPaymentBankName.trim() === "") {
        toast.error("Bank Name is required");
        return;
      }
      if (!adminPaymentBranchName || adminPaymentBranchName.trim() === "") {
        toast.error("Branch Name is required");
        return;
      }
    }

    setAdminPaymentSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("clientFollowupId", selectedBlockItem._id);
      formData.append("amountPaid", adminPaymentAmount);
      formData.append("paymentMethod", adminPaymentMethod);
      if (adminPaymentRemarks) {
        formData.append("remarks", adminPaymentRemarks);
      }
      if (adminPaymentProofFile) {
        formData.append("proof", adminPaymentProofFile);
      }
      if (adminPaymentMethod === "Cheque") {
        formData.append("transactionId", adminPaymentTransactionId);
        formData.append("bankName", adminPaymentBankName);
        formData.append("branchName", adminPaymentBranchName);
      }

      const res = await submitAdminPayment(formData);
      if (res.status) {
        toast.success("Payment submitted successfully");
        // Reset form fields
        setAdminPaymentMethod("");
        setAdminPaymentAmount("");
        setAdminPaymentProofFile(null);
        setAdminPaymentRemarks("");
        setAdminPaymentTransactionId("");
        setAdminPaymentBankName("");
        setAdminPaymentBranchName("");
        // Reset file input element if any
        const fileInput = document.getElementById("adminPaymentProofInput");
        if (fileInput) fileInput.value = "";

        // Reload payments and client lists
        const pRes = await getClientFollowupPayments(selectedBlockItem._id);
        if (pRes.status) {
          setSelectedBlockPayments(pRes.response?.data || pRes.response || []);
        }

        // Fetch fresh block item data
        // Let's refetch list
        fetchClientFollowupsList(
          selectedExpo?._id || selectedExpo?.id,
          clientFollowupStatus,
          clientFollowupsPage,
          clientFollowupsLimit,
          clientFollowupSearchTerm,
          selectedManagerFilter,
          selectedGroupFilter
        );
      } else {
        toast.error(res.response?.message || "Failed to submit payment");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while submitting payment");
    } finally {
      setAdminPaymentSubmitting(false);
    }
  };

  const handleMobileChange = async (e) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setNewEnquiryData(prev => ({ ...prev, mobileNo: val }));

    if (val.length === 10) {
      try {
        const res = await getClients({ mobileNumber: val });
        if (res.status && res.response?.data?.length > 0) {
          const client = res.response.data[0];
          setNewEnquiryData(prev => {
            const fetchedProducts = Array.isArray(client.products) && client.products.length > 0
              ? client.products.map(p => p._id || p.id || p)
              : prev.interestedProduct;
            const fetchedLocations = Array.isArray(client.interestedLocations) && client.interestedLocations.length > 0
              ? client.interestedLocations.map(l => l._id || l.id || l)
              : prev.interestedLocations;

            return {
              ...prev,
              name: client.keyPerson || "",
              companyName: client.companyName || "",
              email: client.email || "",
              designation: client.designation || "",
              city: client.city || "",
              interestedProduct: fetchedProducts,
              interestedLocations: fetchedLocations,
            };
          });
          toast.success("Client details fetched successfully!");
        } else {
          toast.info("No existing client found with this mobile number. You can enter new details.");
        }
      } catch (err) {
        console.error("Error fetching client by mobile:", err);
      }
    }
  };

  const handleDirectBookingSubmit = async (e) => {
    e.preventDefault();
    if (!newEnquiryData.mobileNo || newEnquiryData.mobileNo.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!newEnquiryData.name) {
      toast.error("Please enter name");
      return;
    }
    if (!newEnquiryData.stallNo) {
      toast.error("Please select a stall");
      return;
    }

    setDirectBookingSubmitting(true);
    try {
      const payload = {
        Mobileno: newEnquiryData.mobileNo,
        name: newEnquiryData.name,
        company: newEnquiryData.companyName,
        email: newEnquiryData.email,
        productOfIntrest: newEnquiryData.interestedProduct,
        intrestLocation: newEnquiryData.interestedLocations,
        designation: newEnquiryData.designation,
        city: newEnquiryData.city,
        stallNo: newEnquiryData.stallNo,
        stallType: newEnquiryData.stallType,
        totalAmount: newEnquiryData.totalAmount,
        discount: newEnquiryData.discount,
        sqm: newEnquiryData.sqm,
        expoId: selectedExpo?._id || selectedExpo?.id,
        remarks: newEnquiryData.remark || `Stall booked directly by Admin`,
      };

      if ((bookingCreatorType === "Manager" || bookingCreatorType === "Overall") && bookingCreatorManagerId) {
        payload.createdBy = bookingCreatorManagerId;
      }

      const res = await submitDirectStallBooking(payload);
      if (res.status) {
        toast.success("Stall booked and approved successfully!");
        setShowDirectBookingModal(false);
        fetchClientFollowupsList(
          selectedExpo?._id || selectedExpo?.id,
          clientFollowupStatus,
          clientFollowupsPage,
          rowsPerPage,
          clientFollowupSearchTerm,
          selectedManagerFilter,
          selectedGroupFilter
        );
      } else {
        toast.error(res.response?.message || "Failed to book stall");
      }
    } catch (err) {
      console.error("Error submitting direct booking:", err);
      toast.error("An error occurred during submission");
    } finally {
      setDirectBookingSubmitting(false);
    }
  };

  const [nextFollowupDateFilter, setNextFollowupDateFilter] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [newClientFollowupData, setNewClientFollowupData] = useState({
    remarks: "",
    status: "Assignes",
  });
  const [selectedExpo, setSelectedExpo] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [detailSearchTerm, setDetailSearchTerm] = useState("");
  const [mainTab, setMainTab] = useState("stall_enquiry");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [stallEnquiries, setStallEnquiries] = useState([]);
  const [visitorEnquiries, setVisitorEnquiries] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(""); // eslint-disable-line no-unused-vars
  const [bookingCreatorType, setBookingCreatorType] = useState("Myself");
  const [bookingCreatorManagerId, setBookingCreatorManagerId] = useState("");
  const [selectedAssigns, setSelectedAssigns] = useState([]);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignManagerId, setBulkAssignManagerId] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [expoToMove, setExpoToMove] = useState(null); // eslint-disable-line no-unused-vars
  const [hoveredExpoId, setHoveredExpoId] = useState(null);
  const [hoveredVenueExpoId, setHoveredVenueExpoId] = useState(null);
  const [stallTypeOptions, setStallTypeOptions] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expoToDelete, setExpoToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]); // eslint-disable-line no-unused-vars
  const [productOptions, setProductOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [visitorTotal, setVisitorTotal] = useState(0);
  const [stallTotal, setStallTotal] = useState(0);
  const [visitorSourceCounts, setVisitorSourceCounts] = useState({ all: 0, website: 0, adminApp: 0, userApp: 0 });
  const [knownSourceFilter, setKnownSourceFilter] = useState("All");
  const [visitorKnownSourceCounts, setVisitorKnownSourceCounts] = useState({ All: 0 });
  const [dbKnownSources, setDbKnownSources] = useState([]);

  const [selectedVisitorIds, setSelectedVisitorIds] = useState([]);
  const [sendingSMS, setSendingSMS] = useState(false);

  // Bulk Upload Enquiries States
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedExcelFile, setSelectedExcelFile] = useState(null);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const isPropertyExpo = React.useMemo(() => {
    if (!selectedExpo) return false;
    const name = (selectedExpo.expoName || selectedExpo.name || "").toLowerCase();
    const brand = (selectedExpo.expoBrandId?.name || selectedExpo.expoBrandName || "").toLowerCase();
    const cat = (selectedExpo.category?.name || selectedExpo.categoryName || "").toLowerCase();
    return name.includes("property") || brand.includes("property") || cat.includes("property");
  }, [selectedExpo]);

  const handleSendBulkSMS = async () => {
    if (selectedVisitorIds.length === 0) {
      toast.warning("Please select at least one visitor to send SMS");
      return;
    }

    const selectedVisitors = visitorEnquiries.filter((v) =>
      selectedVisitorIds.includes(v._id || v.id)
    );
    const numbers = selectedVisitors
      .map((v) => v.mobileNo || v.phone || v.mobile1)
      .filter(Boolean);

    if (numbers.length === 0) {
      toast.warning("No valid mobile numbers found for selected visitors");
      return;
    }

    try {
      setSendingSMS(true);
      const res = await sendBulkVisitorSMS({ numbers });
      if (res?.status) {
        toast.success(res.response?.message || `SMS sent successfully to ${numbers.length} visitor(s)!`);
        setSelectedVisitorIds([]);
      } else {
        toast.error(res?.response?.message || "Failed to send SMS");
      }
    } catch (err) {
      console.error("Error sending SMS:", err);
      toast.error("An error occurred while sending SMS");
    } finally {
      setSendingSMS(false);
    }
  };

  useEffect(() => {
    const loadKnownSources = async () => {
      try {
        const res = await getKnownSources();
        if (res?.status) {
          const list = res.response?.data || res.response || [];
          setDbKnownSources(list.filter((item) => item.isActive !== false));
        }
      } catch (err) {
        console.error("Failed to load known sources in PresentExpo:", err);
      }
    };
    loadKnownSources();
  }, []);

  const knownSourceList = React.useMemo(() => {
    const list = ["All"];
    dbKnownSources.forEach((ks) => {
      if (ks.name && !list.includes(ks.name)) {
        list.push(ks.name);
      }
    });
    Object.keys(visitorKnownSourceCounts).forEach((key) => {
      if (!list.includes(key) && key !== "All") {
        list.push(key);
      }
    });
    return list;
  }, [dbKnownSources, visitorKnownSourceCounts]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showDirectBookingModal, setShowDirectBookingModal] = useState(false);
  const [directBookingSubmitting, setDirectBookingSubmitting] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [pendingApproveItem, setPendingApproveItem] = useState(null);
  const [showStallDetailConfirm, setShowStallDetailConfirm] = useState(false);
  const [stallDetailConfirmData, setStallDetailConfirmData] = useState(null);

  const [showForceRejectModal, setShowForceRejectModal] = useState(false);
  const [forceRejectData, setForceRejectData] = useState(null);
  const [forceRejectInput, setForceRejectInput] = useState("");
  const [forceRejectReason, setForceRejectReason] = useState("");
  const [editingStallNo, setEditingStallNo] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editRemarks, setEditRemarks] = useState("");
  const [editAddons, setEditAddons] = useState([]);
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(detailSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [detailSearchTerm]);

  const [newEnquiryData, setNewEnquiryData] = useState({
    name: "",
    companyName: "",
    mobileNo: "",
    email: "",
    category: "",
    interestedProduct: [],
    segment: "B2B",
    designation: "",
    interestedLocations: [],
    address: "",
    city: "",
    state: "",
    pincode: "",
    status: "Enquiry",
    remark: "",
    followUpDate: "",
    stallNo: "",
    sqft: "",
    sqm: "",
    totalAmount: "",
    amountPaid: "",
    stallType: "",
    manualCost: "",
    paymentMethod: "",
    paymentStatus: "",
    paymentScreenshot: null,
  });

  const [inlineAction, setInlineAction] = useState("");
  const [inlineFollowupData, setInlineFollowupData] = useState({
    date: "",
    remarks: "",
  });

  const [bookingList, setBookingList] = useState([]); // eslint-disable-line no-unused-vars
  // const [statusCounts, setStatusCounts] = useState({
  //   all: 0,
  //   enquiry: 0,
  //   followup: 0,
  //   rejected: 0,
  // });
  // const [paymentCounts, setPaymentCounts] = useState({
  //   all: 0,
  //   paid: 0,
  //   partially: 0,
  // });

  useEffect(() => {
    const fetchStallTypes = async () => {
      const res = await getStallTypes();
      if (res.status) {
        const types = res.response?.data || res.response || [];
        setStallTypeOptions(
          types.map((t) => ({ value: t._id || t.id, label: t.stallTypeName })),
        );
      }
    };
    fetchStallTypes();

    const fetchCategories = async () => {
      const res = await getCategories();
      if (res.status) {
        const data = res.response?.data || res.response || [];
        setCategoryOptions(
          data.map((c) => ({
            value: c._id || c.id,
            label: c.name || c.categoryName,
          })),
        );
      }
    };
    fetchCategories();

    const fetchLocations = async () => {
      const res = await getLocations({ limit: 0 });
      if (res.status) {
        const data = res.response?.data || res.response || [];
        setLocationOptions(
          data.map((l) => ({
            value: l._id || l.id,
            label: l.name || l.locationName,
          })),
        );
      }
    };
    fetchLocations();

    const fetchManagers = async () => {
      const res = await getManagers({ limit: 1000 });
      if (res.status) {
        const data = res.response?.data || res.response || [];
        setManagersList(
          data.map((m) => ({ value: m._id || m.id, label: m.name })),
        );
      }
    };
    fetchManagers();

    const fetchGroups = async () => {
      const res = await getClientGroups({ limit: 0 });
      if (res.status) {
        const data = res.response?.data || res.response || [];
        setClientGroupsList(
          data.map((g) => ({ value: g._id || g.id, label: g.groupName })),
        );
      }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      // If category is not selected, we might want to fetch all or none.
      // Usually, it's better to fetch only if category is selected if that's the requirement.
      const params = {};
      if (newEnquiryData.category) {
        params.categoryId = newEnquiryData.category;
      }

      const res = await getProducts(params);
      if (res.status) {
        const data = res.response?.data || res.response || [];
        const options = data.map((p) => ({
          value: p._id || p.id,
          label: p.name || p.productName,
        }));
        setProductOptions(options);

        // Auto select all product IDs if category is selected
        if (newEnquiryData.category) {
          setNewEnquiryData((prev) => ({
            ...prev,
            interestedProduct: options.map((opt) => opt.value),
          }));
          // Clear interestedProduct error if it exists — read from state inline to avoid dep warning
          setEnquiryErrors((prev) => (
            prev.interestedProduct ? { ...prev, interestedProduct: null } : prev
          ));
        }
      }
    };
    fetchProducts();
  }, [newEnquiryData.category]);

  const [stallOptions, setStallOptions] = useState([]);
  const [currentSqmRate, setCurrentSqmRate] = useState(0);
  const [showAddEnquiryModal, setShowAddEnquiryModal] = useState(false);

  // const handleOpenAddEnquiryModal = () => {
  //   setNewEnquiryData((prev) => ({
  //     ...prev,
  //     category: selectedExpo?.category?._id || selectedExpo?.category || "",
  //   }));
  //   setShowAddEnquiryModal(true);
  // };

  const handleCloseAddEnquiryModal = () => {
    setShowAddEnquiryModal(false);
    setEnquiryErrors({});
    setNewEnquiryData({
      name: "",
      companyName: "",
      mobileNo: "",
      email: "",
      category: "",
      interestedProduct: [],
      segment: "B2B",
      interestedLocations: [],
      address: "",
      city: "",
      state: "",
      pincode: "",
      status: "Enquiry",
      remark: "",
      followUpDate: "",
      stallNo: "",
      sqft: "",
      sqm: "",
      totalAmount: "",
      amountPaid: "",
      stallType: "",
      manualCost: "",
      paymentMethod: "",
      paymentStatus: "",
      paymentScreenshot: null,
    });
  };

  const searchTimeout = useRef(null);
  // const detailSearchTimeout = useRef(null);
  const mainSearchTimeout = useRef(null);

  const handleMainSearch = (val) => {
    setSearchTerm(val);
    if (mainSearchTimeout.current) clearTimeout(mainSearchTimeout.current);
    mainSearchTimeout.current = setTimeout(() => {
      fetchPresentExpos(0, val);
    }, 500);
  };

  const handleDetailSearch = (val) => {
    setDetailSearchTerm(val);
    setCurrentPage(0);
  };

  const fetchExpoStalls = async (search = "") => {
    const expoId = selectedExpo?._id || selectedExpo?.id;
    if (!expoId) return;

    const res = await getStallsByExpo(expoId, {
      availableOnly: true,
      search: search,
    });

    if (res.status) {
      const responseBody = res.response || {};
      let stalls = [];
      if (responseBody.data && Array.isArray(responseBody.data.stalls)) {
        stalls = responseBody.data.stalls;
      } else if (Array.isArray(responseBody.stalls)) {
        stalls = responseBody.stalls;
      } else if (Array.isArray(responseBody.data)) {
        stalls = responseBody.data;
      } else if (Array.isArray(responseBody)) {
        stalls = responseBody;
      }

      setStallOptions(
        stalls
          .map((s) => ({
            value: s.stallNo || s.stall_no || s._id || "",
            label: s.stallNo || s.stall_no || "Stall " + s._id,
            sqm: s.sqm || 0,
            sqft: s.sqft || 0,
            type: s.type?._id || s.type || "",
            status: s.status || "Pending",
            stallValue: s.value || 0,
            withgstvalue: s.withgstvalue || 0,
          }))
          .filter((opt) => opt.value),
      );

      // Only fetch rate once if not already set or when modal opens
      if (currentSqmRate === 0) {
        const expoRes = await getExpoById(expoId);
        if (expoRes.status) {
          const expoData = expoRes.response?.data || expoRes.response;
          setCurrentSqmRate(expoData?.sqmRate || 0);
        }
      }
    } else {
      toast.error("Failed to load stall list");
    }
  };

  useEffect(() => {
    if (showAddEnquiryModal || showClientHistoryModal) {
      const expoId = selectedExpo?._id || selectedExpo?.id;
      if (!expoId) return;
      getStallsByExpo(expoId, { availableOnly: true, search: "" }).then((res) => {
        if (res.status) {
          const responseBody = res.response || {};
          let stalls = [];
          if (responseBody.data && Array.isArray(responseBody.data.stalls)) stalls = responseBody.data.stalls;
          else if (Array.isArray(responseBody.stalls)) stalls = responseBody.stalls;
          else if (Array.isArray(responseBody.data)) stalls = responseBody.data;
          else if (Array.isArray(responseBody)) stalls = responseBody;
          setStallOptions(
            stalls.map((s) => ({
              value: s.stallNo || s.stall_no || s._id || "",
              label: s.stallNo || s.stall_no || "Stall " + s._id,
              sqm: s.sqm || 0,
              sqft: s.sqft || 0,
              type: s.type?._id || s.type || "",
              status: s.status || "Pending",
              stallValue: s.value || 0,
              withgstvalue: s.withgstvalue || 0,
            })).filter((opt) => opt.value)
          );
          if (currentSqmRate === 0) {
            getExpoById(expoId).then((expoRes) => {
              if (expoRes.status) {
                const expoData = expoRes.response?.data || expoRes.response;
                setCurrentSqmRate(expoData?.sqmRate || 0);
              }
            });
          }
        } else {
          toast.error("Failed to load stall list");
        }
      });
    }
  }, [selectedExpo, showAddEnquiryModal, showClientHistoryModal, currentSqmRate]);

  const handleStallSearch = (inputValue) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchExpoStalls(inputValue);
    }, 500);
  };

  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [showMoveFollowupModal, setShowMoveFollowupModal] = useState(false);
  const [currentFollowup, setCurrentFollowup] = useState(null); // eslint-disable-line no-unused-vars
  const [followupData, setFollowupData] = useState({ date: "", remarks: "" });
  const [moveFollowupData, setMoveFollowupData] = useState({
    date: "",
    remarks: "",
  });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [rejectRemark, setRejectRemark] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [bookingPaymentInfo, setBookingPaymentInfo] = useState({
    amount: "",
    stallNo: "",
    sqft: "",
    screenshot: null,
    paymentMethod: "",
    paymentStatus: "",
    remark: "",
  });

  const [enquiryErrors, setEnquiryErrors] = useState({});
  const [paymentErrors, setPaymentErrors] = useState({});
  const [followupErrors, setFollowupErrors] = useState({});
  const [moveFollowupErrors, setMoveFollowupErrors] = useState({});
  const [showInlinePaymentUpdate, setShowInlinePaymentUpdate] = useState(false);
  const [inlinePaymentData, setInlinePaymentData] = useState({
    amount: "",
    paymentMethod: "",
    remark: "",
    screenshot: null,
  });

  const fetchClientByMobile = async (mobile) => {
    try {
      const res = await getClients({ page: 0, limit: 10, mobileNumber: mobile, isActive: true });
      if (res.status) {
        const clientList = res.response?.data || res.response || [];
        if (clientList.length > 0) {
          const client = clientList[0];
          setNewEnquiryData((prev) => ({
            ...prev,
            name: client.keyPerson || prev.name,
            companyName: client.companyName || prev.companyName,
            email: client.email || prev.email,
            address: client.address || prev.address,
            city: client.city || prev.city,
            state: client.state || prev.state,
            pincode: client.pincode || prev.pincode,
          }));
          setEnquiryErrors((prev) => ({
            ...prev,
            name: client.keyPerson ? null : prev.name,
            email: client.email ? null : prev.email,
          }));
          toast.success("Client details autofilled!");
        }
      }
    } catch (err) {
      console.error("Error fetching client by mobile:", err);
    }
  };

  const handleEnquiryChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
      const capitalizedValue =
        filteredValue.charAt(0).toUpperCase() + filteredValue.slice(1);
      setNewEnquiryData((prev) => ({ ...prev, [name]: capitalizedValue }));
      return;
    }
    if (name === "mobileNo") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setNewEnquiryData((prev) => ({ ...prev, [name]: digitsOnly }));
      if (digitsOnly.length === 10) {
        fetchClientByMobile(digitsOnly);
      }
      return;
    }
    if (name === "pincode") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
      setNewEnquiryData((prev) => ({ ...prev, [name]: digitsOnly }));
      return;
    }
    if (e.target.type === "number" && value < 0) return;
    setNewEnquiryData((prev) => ({ ...prev, [name]: value }));
  };

  // const handlePaymentInfoChange = (e) => {
  //   const { name, value } = e.target;
  //   if (e.target.type === "number" && value < 0) return;
  //   setBookingPaymentInfo((prev) => ({ ...prev, [name]: value }));
  // };

  // Pagination & Image states
  // Image states
  const [visitorImage, setVisitorImage] = useState(null); // eslint-disable-line no-unused-vars

  const sourceCounts = React.useMemo(() => {
    if (mainTab === "stall_enquiry") {
      const list = stallEnquiries;
      const website = list.filter(item => item.source === "website").length;
      const adminApp = list.filter(item => item.source === "mobile-admin").length;
      const userApp = list.filter(item => item.source === "user-app").length;
      return {
        all: list.length,
        website,
        adminApp,
        userApp
      };
    } else {
      return visitorSourceCounts;
    }
  }, [mainTab, stallEnquiries, visitorSourceCounts]);

  // const filteredList = mainTab === "visitor_enquiry" ? visitorEnquiries : (
  //   stallEnquiries.filter((item) => {
  //     // Apply source filters
  //     if (sourceFilter === "Website" && item.source !== "website") return false;
  //     if (sourceFilter === "Admin-app" && item.source !== "mobile-admin") return false;
  //     if (sourceFilter === "User-app" && item.source !== "user-app") return false;
  //
  //     // Apply search search filter
  //     const search = detailSearchTerm.toLowerCase();
  //     return (
  //       item.name?.toLowerCase().includes(search) ||
  //       item.companyName?.toLowerCase().includes(search) ||
  //       item.mobileNo?.includes(search) ||
  //       item.phone?.includes(search) ||
  //       item.stallNo?.toLowerCase().includes(search) ||
  //       item.email?.toLowerCase().includes(search)
  //     );
  //   })
  // );

  const totalPages = mainTab === "visitor_enquiry"
    ? (Math.ceil(visitorTotal / rowsPerPage) || 1)
    : (Math.ceil(stallTotal / rowsPerPage) || 1);

  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [mainTab, visitorTotal, stallTotal, rowsPerPage, totalPages, currentPage]);

  const paginatedList = mainTab === "visitor_enquiry" ? visitorEnquiries : stallEnquiries;

  // const handleVisitorImageUpload = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;
  //   const formData = new FormData();
  //   formData.append("file", file);
  //   formData.append("folder", "visitors");
  //   const res = await uploadFile(formData);
  //   if (res.status) {
  //     setVisitorImage(res.response?.data?.path || res.response);
  //     toast.success("Visitor Image Uploaded Successfully");
  //   }
  // };

  const [presentExpos, setPresentExpos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exposPage, setExposPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalExpos, setTotalExpos] = useState(0); // eslint-disable-line no-unused-vars

  const fetchPresentExpos = async (page = 0, search = "") => {
    setLoading(true);
    try {
      const res = await getPresentExpos({ page, limit: 10, search });
      if (res.status) {
        const resData = res.response;
        const newData = Array.isArray(resData?.data)
          ? resData.data
          : Array.isArray(resData)
            ? resData
            : [];

        const total = resData?.total || 0;
        setTotalExpos(total);

        if (page === 0) {
          setPresentExpos(newData);
        } else {
          setPresentExpos((prev) => [...prev, ...newData]);
        }
        setHasMore(newData.length > 0 && (page + 1) * 10 < total);
      }
    } catch (err) {
      console.error("Error fetching present expos:", err);
    } finally {
      setLoading(false);
    }
  };
  const [clientSearchInput, setClientSearchInput] = useState("");
  const clientSearchTimeout = useRef(null);

  const fetchClientFollowupsList = async (
    expoId,
    status,
    page = 0,
    limit = 10,
    searchVal = "",
    managerVal = null,
    groupVal = null
  ) => {
    setClientFollowupsLoading(true);
    try {
      const params = {
        page,
        limit,
        status,
        expoId,
      };

      if (searchVal) params.search = searchVal;
      if (managerVal) params.managerId = managerVal.value || managerVal;
      if (groupVal) params.groupId = groupVal.value || groupVal;

      const res = await getClientFollowups(params);
      if (res.status) {

        const data = Array.isArray(res.response?.data)
          ? res.response.data
          : (res.response?.data?.docs || res.response?.docs || []);
        const total = typeof res.response?.total === 'number'
          ? res.response.total
          : (res.response?.data?.total || res.response?.total || 0);

        setClientFollowupsList(data);
        setClientFollowupsTotal(total);
      }
    } catch (err) {
      console.error("Error fetching client followups:", err);
    } finally {
      setClientFollowupsLoading(false);
    }
  };

  const handleClientFollowupSearch = (val) => {
    setClientSearchInput(val);
    if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current);
    clientSearchTimeout.current = setTimeout(() => {
      setClientFollowupSearchTerm(val);
      setClientFollowupsPage(0);
    }, 500);
  };

  useEffect(() => {
    setSelectedAssigns([]);
  }, [activeView, selectedExpo, clientFollowupStatus]);

  useEffect(() => {
    const expoId = selectedExpo?._id || selectedExpo?.id;
    if (activeView === "client-followup-list" && expoId) {
      fetchClientFollowupsList(
        expoId,
        clientFollowupStatus,
        clientFollowupsPage,
        clientFollowupsLimit,
        clientFollowupSearchTerm,
        selectedManagerFilter,
        selectedGroupFilter
      );
    }
  }, [
    activeView,
    selectedExpo,
    clientFollowupStatus,
    clientFollowupsPage,
    clientFollowupsLimit,
    clientFollowupSearchTerm,
    selectedManagerFilter,
    selectedGroupFilter,
  ]);

  useEffect(() => {
    fetchPresentExpos(0);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openModal = params.get("openModal");
    const expoId = params.get("expoId");

    if (openModal === "true" && expoId) {
      const loadExpoAndOpen = async () => {
        try {
          const res = await getExpoById(expoId);
          if (res.status) {
            const expoData = res.response?.data || res.response;
            setSelectedExpo(expoData);
            setActiveView("detail");

            setNewEnquiryData((prev) => ({
              ...prev,
              name: params.get("name") || "",
              mobileNo: params.get("mobile") || "",
              email: params.get("email") || "",
              companyName: params.get("companyName") || "",
              status: "Enquiry",
            }));

            setShowAddEnquiryModal(true);

            // Clean URL params to avoid reopening on subsequent changes
            navigate("/present-expo", { replace: true });
          }
        } catch (err) {
          console.error("Error auto-opening stall enquiry modal:", err);
        }
      };
      loadExpoAndOpen();
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      if (activeView !== "grid" || !hasMore || loading) return;

      const threshold = 150; // trigger loading slightly before reaching the bottom
      const totalHeight = document.documentElement.scrollHeight;
      const currentScroll = window.innerHeight + window.scrollY;

      if (totalHeight - currentScroll <= threshold) {
        const nextPage = exposPage + 1;
        setExposPage(nextPage);
        fetchPresentExpos(nextPage, searchTerm);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeView, hasMore, loading, exposPage, searchTerm]);

  // const handleLoadMore = () => {
  //   const nextPage = exposPage + 1;
  //   setExposPage(nextPage);
  //   fetchPresentExpos(nextPage, searchTerm);
  // };

  // const updateCounts = async (expoId) => {
  //   try {
  //     const res = await getStallEnquiriesByExpo(expoId, "", 0, 0, "", "");
  //     if (res.status) {
  //       const raw = res.response?.data || res.response || [];
  //       const data = Array.isArray(raw) ? raw : [];
  //
  //       const enqCount = data.filter(
  //         (item) => item.status === "Enquiry",
  //       ).length;
  //       const folCount = data.filter(
  //         (item) => item.status === "Followup",
  //       ).length;
  //       const rejCount = data.filter(
  //         (item) => item.status === "Rejected",
  //       ).length;
  //       setStatusCounts({
  //         all: enqCount + folCount + rejCount,
  //         enquiry: enqCount,
  //         followup: folCount,
  //         rejected: rejCount,
  //       });
  //
  //       const bookedItems = data.filter((item) => item.status === "Booked");
  //       setPaymentCounts({
  //         all: bookedItems.length,
  //         paid: bookedItems.filter(
  //           (b) =>
  //             b.paymentStatus === "Paid" || b.paymentStatus === "Fully Paid",
  //         ).length,
  //         partially: bookedItems.filter(
  //           (b) =>
  //             b.paymentStatus === "Partially" ||
  //             b.paymentStatus === "Partially Paid",
  //         ).length,
  //       });
  //     }
  //   } catch (err) {
  //     console.error("Error updating counts:", err);
  //   }
  // };

  const fetchVisitorCounts = async (expoId, searchVal = "") => {
    try {
      const res = await getVisitorEnquiriesByExpo(expoId, searchVal, undefined, 10000);
      if (res.status) {
        const raw = res.response?.data || res.response || [];
        const data = Array.isArray(raw) ? raw : [];
        const website = data.filter(item => (item.source || "").toLowerCase().trim() === "website").length;
        const adminApp = data.filter(item => {
          const s = (item.source || "").toLowerCase().trim();
          return s === "mobile-admin" || s === "admin-app";
        }).length;
        const userApp = data.filter(item => (item.source || "").toLowerCase().trim() === "user-app").length;
        setVisitorSourceCounts({
          all: data.length,
          website,
          adminApp,
          userApp
        });

        // Compute counts for Known Sources
        const ksCounts = { All: data.length };
        dbKnownSources.forEach((ks) => {
          if (ks.name) ksCounts[ks.name] = 0;
        });

        data.forEach((item) => {
          const ksVal = (
            item.sourceId?.name ||
            item.knowAboutExhibition ||
            item.statusDetails?.[0]?.knowAboutExhibition ||
            item.statusUpdateDetails?.[0]?.knowAboutExhibition ||
            ""
          ).trim();
          if (!ksVal) return;

          const matched = dbKnownSources.find(
            (d) =>
              (d.name && d.name.toLowerCase() === ksVal.toLowerCase()) ||
              d._id === ksVal ||
              d.sourceId === ksVal
          );
          const targetKey = matched ? matched.name : ksVal;
          ksCounts[targetKey] = (ksCounts[targetKey] || 0) + 1;
        });

        setVisitorKnownSourceCounts(ksCounts);
      }
    } catch (err) {
      console.error("Error fetching visitor counts:", err);
    }
  };

  const fetchVisitorEnquiries = async (
    expoId,
    searchInput = null,
    pageNum = currentPage,
    limitVal = rowsPerPage,
    currentSourceFilter = sourceFilter,
    currentKnownSourceFilter = knownSourceFilter
  ) => {
    try {
      const searchVal = typeof searchInput === "string" ? searchInput : detailSearchTerm || "";
      let apiSource = "";
      if (currentSourceFilter === "Website") apiSource = "website";
      else if (currentSourceFilter === "Admin-app") apiSource = "mobile-admin";
      else if (currentSourceFilter === "User-app") apiSource = "user-app";

      const apiKnownSource = currentKnownSourceFilter === "All" ? "" : currentKnownSourceFilter;

      const res = await getVisitorEnquiriesByExpo(expoId, searchVal, pageNum, limitVal, apiSource, apiKnownSource);
      if (res.status) {
        const raw = res.response?.data || res.response || [];
        const data = Array.isArray(raw) ? raw : [];
        setVisitorEnquiries(data);
        setVisitorTotal(res.response?.total || data.length);
      }
    } catch (err) {
      console.error("Error fetching visitor enquiries:", err);
    }
  };

  const [exportingExcel, setExportingExcel] = useState(false);

  const handleExportVisitorExcel = async () => {
    if (!selectedExpo) return;
    const expoId = selectedExpo._id || selectedExpo.id;
    setExportingExcel(true);
    try {
      const apiKnownSource = knownSourceFilter === "All" ? "" : knownSourceFilter;
      const res = await downloadVisitorEnquiriesExcel(
        expoId,
        detailSearchTerm || "",
        sourceFilter,
        apiKnownSource
      );
      if (res.status) {
        const url = window.URL.createObjectURL(new Blob([res.response]));
        const link = document.createElement("a");
        link.href = url;
        const sanitizedExpoName = (selectedExpo?.expoName || "Expo").replace(/[^a-zA-Z0-9_-]/g, "_");
        link.setAttribute("download", `${sanitizedExpoName}_Visitor_Enquiries.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Excel downloaded successfully");
      }
    } catch (err) {
      console.error("Error downloading excel:", err);
      toast.error("Failed to download excel");
    } finally {
      setExportingExcel(false);
    }
  };

  const handleDownloadSampleExcel = async () => {
    try {
      const type = mainTab === "visitor_enquiry" ? "visitor" : "stall";
      const res = await downloadEnquirySample(type);
      if (res.status) {
        const url = window.URL.createObjectURL(new Blob([res.response]));
        const link = document.createElement("a");
        link.href = url;
        const filename = type === "visitor" ? "Sample_Visitor_Enquiries.xlsx" : "Sample_Stall_Enquiries.xlsx";
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Sample template downloaded successfully");
      }
    } catch (err) {
      console.error("Error downloading sample template:", err);
      toast.error("Failed to download sample template");
    }
  };

  const handleBulkUploadEnquiriesSubmit = async () => {
    if (!selectedExcelFile) {
      toast.warning("Please select an Excel or CSV file to upload");
      return;
    }
    if (!selectedExpo) {
      toast.error("No expo selected");
      return;
    }

    const expoId = selectedExpo._id || selectedExpo.id;
    const type = mainTab === "visitor_enquiry" ? "visitor" : "stall";

    setIsBulkUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedExcelFile);
      formData.append("expoId", expoId);
      formData.append("type", type);

      const res = await bulkUploadEnquiries(formData);
      if (res.status) {
        const data = res.response?.data || res.response || {};
        setUploadResult(data);
        toast.success(res.response?.message || "Enquiries uploaded successfully");

        // Refresh enquiry list & counts
        if (mainTab === "stall_enquiry") {
          fetchStallEnquiries(expoId, debouncedSearchTerm, currentPage, rowsPerPage, sourceFilter);
        } else {
          fetchVisitorEnquiries(expoId, debouncedSearchTerm, currentPage, rowsPerPage, sourceFilter, knownSourceFilter);
          fetchVisitorCounts(expoId, debouncedSearchTerm);
        }

        // Auto close modal if no errors
        if (!data.errors || data.errors.length === 0) {
          setTimeout(() => {
            setShowBulkUploadModal(false);
            setSelectedExcelFile(null);
            setUploadResult(null);
          }, 2500);
        }
      } else {
        toast.error(res.response?.message || "Failed to upload enquiries");
      }
    } catch (err) {
      console.error("Error uploading enquiries:", err);
      toast.error("An error occurred during upload");
    } finally {
      setIsBulkUploading(false);
    }
  };

  const fetchStallEnquiries = async (
    expoId,
    searchInput = null,
    pageNum = currentPage,
    limitVal = rowsPerPage,
    currentSourceFilter = sourceFilter
  ) => {
    try {
      const searchVal = typeof searchInput === "string" ? searchInput : debouncedSearchTerm || "";
      let apiSource = "";
      if (currentSourceFilter === "Website") apiSource = "website";
      else if (currentSourceFilter === "Admin-app") apiSource = "mobile-admin";
      else if (currentSourceFilter === "User-app") apiSource = "user-app";

      const res = await getStallEnquiriesByExpo(expoId, searchVal, pageNum, limitVal, apiSource);
      if (res.status) {
        const raw = res.response?.data || res.response || [];
        const data = Array.isArray(raw) ? raw : [];
        setStallEnquiries(data);
        setStallTotal(res.response?.total || data.length);
      }
    } catch (err) {
      console.error("Error fetching stall enquiries:", err);
    }
  };

  useEffect(() => {
    if (selectedExpo && mainTab === "stall_enquiry") {
      const expoId = selectedExpo._id || selectedExpo.id;
      fetchStallEnquiries(expoId, debouncedSearchTerm, currentPage, rowsPerPage, sourceFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExpo, mainTab, currentPage, rowsPerPage, sourceFilter, debouncedSearchTerm]);

  useEffect(() => {
    if (selectedExpo && mainTab === "visitor_enquiry") {
      const expoId = selectedExpo._id || selectedExpo.id;
      fetchVisitorEnquiries(expoId, debouncedSearchTerm, currentPage, rowsPerPage, sourceFilter, knownSourceFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExpo, mainTab, currentPage, rowsPerPage, sourceFilter, knownSourceFilter, debouncedSearchTerm]);

  useEffect(() => {
    if (selectedExpo && mainTab === "visitor_enquiry") {
      const expoId = selectedExpo._id || selectedExpo.id;
      fetchVisitorCounts(expoId, debouncedSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExpo, mainTab, debouncedSearchTerm, dbKnownSources]);

  const handleView = (expo) => {
    setSelectedExpo(expo);
    setActiveView("detail");
  };

  const handleAddEnquirySubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!newEnquiryData.name.trim()) newErrors.name = "Name is Required";
    if (newEnquiryData.email && newEnquiryData.email.trim()) {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(newEnquiryData.email)) {
        newErrors.email =
          "Invalid email address (must have at least 2 chars after .)";
      }
    }
    if (!newEnquiryData.mobileNo.trim()) {
      newErrors.mobileNo = "Mobile Number is Required";
    } else if (newEnquiryData.mobileNo.length !== 10) {
      newErrors.mobileNo = "Mobile Number must be exactly 10 digits";
    }

    if (!newEnquiryData.stallNo?.trim()) {
      newErrors.stallNo = "Stall No is Required";
    }

    if (newEnquiryData.pincode && newEnquiryData.pincode.length !== 6) {
      newErrors.pincode = "Pincode must be exactly 6 digits";
    }

    if (!newEnquiryData.category) {
      newErrors.category = "Exhibition Type is Required";
    }

    if (
      !newEnquiryData.interestedProduct ||
      newEnquiryData.interestedProduct.length === 0
    ) {
      newErrors.interestedProduct = "Please select at least one product";
    }

    if (!newEnquiryData.remark?.trim()) {
      newErrors.remark = "Remark is Required";
    }

    if (newEnquiryData.status === "Booked") {
      if (!newEnquiryData.amountPaid || newEnquiryData.amountPaid === "0") {
        newErrors.amountPaid = "Amount Paid is Required for Booking";
      }
      if (!newEnquiryData.paymentStatus) {
        newErrors.paymentStatus = "Payment Status is Required";
      }
      if (!newEnquiryData.paymentMethod) {
        newErrors.paymentMethod = "Payment Method is Required";
      }
      if (
        newEnquiryData.paymentMethod === "UPI" &&
        !newEnquiryData.paymentScreenshot
      ) {
        newErrors.paymentScreenshot = "Payment Screenshot is Required for UPI";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setEnquiryErrors(newErrors);
      return;
    }

    const entry = {
      expoId: selectedExpo._id || selectedExpo.id,
      name: newEnquiryData.name,
      companyName: newEnquiryData.companyName,
      mobileNo: newEnquiryData.mobileNo,
      email: newEnquiryData.email,
      category: newEnquiryData.category,
      stallType: newEnquiryData.stallType || undefined,
      stallNo: newEnquiryData.stallNo,
      sqm: newEnquiryData.sqm,
      products: newEnquiryData.interestedProduct,
      segment: newEnquiryData.segment,
      designation: newEnquiryData.designation,
      interestedLocations: [],
      address: newEnquiryData.address,
      city: newEnquiryData.city,
      state: "",
      pincode: "",
      remark: newEnquiryData.remark,
      status:
        newEnquiryData.status === "Interested"
          ? "Enquiry"
          : newEnquiryData.status,
      date:
        newEnquiryData.status === "Followup"
          ? newEnquiryData.followUpDate
          : new Date().toISOString().split("T")[0],
      // Additional fields for booking if needed
      totalAmount: newEnquiryData.totalAmount,
      amount: newEnquiryData.amountPaid,
      paymentMethod: newEnquiryData.paymentMethod,
      paymentStatus: newEnquiryData.paymentStatus,
      paymentScreenshot: newEnquiryData.paymentScreenshot,
    };

    const submitEnquiry = async () => {
      const res = await saveStallEnquiry(entry);
      if (res.status) {
        toast.success("Enquiry Added Successfully");
        setNewEnquiryData({
          name: "",
          companyName: "",
          mobileNo: "",
          email: "",
          category: "",
          interestedProduct: [],
          segment: "B2B",
          interestedLocations: [],
          address: "",
          city: "",
          state: "",
          pincode: "",
          status: "Interested",
          remark: "",
          followUpDate: "",
          stallNo: "",
          stallType: "",
          sqm: "",
          totalAmount: "",
          amountPaid: "",
          paymentMethod: "",
          paymentStatus: "",
          paymentScreenshot: null,
        });
        setVisitorImage(null);
        setEnquiryErrors({});
        setShowAddEnquiryModal(false);
        fetchStallEnquiries(selectedExpo._id || selectedExpo.id);
      }
    };
    submitEnquiry();
  };

  const moveEntry = (item, from, to) => {
    if (to === "Rejected") {
      setPendingMove({ item, from, to });
      setRejectRemark("");
      setRejectError("");
      setShowRejectConfirm(true);
      return;
    }

    if (to === "Next Followup") {
      setPendingMove({ item, from, to });
      setMoveFollowupData({ date: "", remarks: "" });
      setShowMoveFollowupModal(true);
      return;
    }

    if (to === "Booking") {
      setPendingMove({ item, from, to });
      setBookingPaymentInfo({
        amount: item.bookingAmount || "",
        stallNo: item.stallNo || "",
        sqft: item.sqft || item.sqm || "",
        screenshot: item.screenshot || null,
        paymentMethod: "",
        paymentStatus: "",
        remark: item.remark || "",
      });
      setShowPaymentModal(true);
      return;
    }

    executeMove(item, from, to);
  };

  const handleMoveFollowupConfirm = () => {
    const newErrors = {};
    if (!moveFollowupData.date) {
      newErrors.date = "Next call date is Required";
    }
    if (!moveFollowupData.remarks?.trim()) {
      newErrors.remarks = "Remarks are mandatory";
    }

    if (Object.keys(newErrors).length > 0) {
      setMoveFollowupErrors(newErrors);
      return;
    }

    executeMove(pendingMove.item, pendingMove.from, pendingMove.to, {
      date: moveFollowupData.date,
      remarks: moveFollowupData.remarks,
      history: [
        {
          id: Date.now(),
          date: moveFollowupData.date,
          remark: moveFollowupData.remarks,
        },
      ],
    });
    setMoveFollowupErrors({});
    setShowMoveFollowupModal(false);
    setPendingMove(null);
  };

  const executeMove = async (item, from, to, extraData = {}) => {
    const statusMap = {
      Enquiry: "Enquiry",
      "Next Followup": "Followup",
      Booking: "Booked",
      Rejected: "Rejected",
    };

    const payload = extraData.status
      ? { ...extraData }
      : {
        status: statusMap[to] || to,
        date: new Date().toISOString().split("T")[0],
        ...extraData,
      };

    const res = await updateStallEnquiryStatus(item._id || item.id, payload);

    if (res.status) {
      toast.success(`Moved to ${to.charAt(0).toUpperCase() + to.slice(1)}`);
      fetchStallEnquiries(selectedExpo._id || selectedExpo.id);
    }
    return res;
  };

  const handleBookingConfirm = () => {
    const { amount, sqft, screenshot, paymentMethod, paymentStatus, remark } =
      bookingPaymentInfo;
    const newErrors = {};

    if (!paymentStatus) {
      newErrors.paymentStatus = "Payment Status is Required";
    }

    const isPaid =
      paymentStatus === "Paid" ||
      paymentStatus === "Fully Paid" ||
      paymentStatus === "Partially Paid" ||
      paymentStatus === "Partially";

    if (isPaid) {
      if (!amount) newErrors.amount = "Booking amount is Required";
      if (!paymentMethod) newErrors.paymentMethod = "Payment Type is Required";
      if (!remark?.trim()) newErrors.remark = "Remark is Required";
      if (paymentMethod === "UPI" && !screenshot) {
        newErrors.screenshot = "Payment screenshot is Required for UPI";
      }
    }

    if (!sqft) {
      newErrors.sqft = "Stall SQFT is Required";
    }

    if (Object.keys(newErrors).length > 0) {
      setPaymentErrors(newErrors);
      return;
    }

    // Build the payload matching PATCH /api/admin/stall-enquiry/status/:id
    const bookingPayload = {
      remark: remark || "",
      paymentStatus: paymentStatus,
    };

    if (isPaid) {
      bookingPayload.paymentMethod = paymentMethod; // backend expects "paymentMethod"
      bookingPayload.amount = Number(amount) || 0;
      bookingPayload.stallNo = bookingPaymentInfo.stallNo;
      bookingPayload.sqft = bookingPaymentInfo.sqft;
      if (paymentMethod === "UPI" && screenshot) {
        bookingPayload.paymentScreenshot = screenshot; // backend expects "paymentScreenshot"
      }
    } else {
      bookingPayload.amount = 0;
      bookingPayload.paymentMethod = "Cash";
      bookingPayload.stallNo = bookingPaymentInfo.stallNo;
      bookingPayload.sqft = bookingPaymentInfo.sqft;
    }

    executeMove(
      pendingMove.item,
      pendingMove.from,
      pendingMove.to,
      bookingPayload,
    );
    setPaymentErrors({});
    setShowPaymentModal(false);
    setPendingMove(null);
  };

  const handleRejectConfirm = () => {
    if (!rejectRemark.trim()) {
      setRejectError("Reject reason/remark is required");
      return;
    }
    executeMove(pendingMove.item, pendingMove.from, pendingMove.to, {
      remark: rejectRemark,
      remarks: rejectRemark,
      history: [
        {
          id: Date.now(),
          date: new Date().toISOString().split("T")[0],
          remark: rejectRemark,
        },
      ],
    });
    setShowRejectConfirm(false);
    setPendingMove(null);
  };

  const handleScreenShotUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "payments");
    const res = await uploadFile(formData);
    if (res.status) {
      const path =
        res.response?.data?.path || res.response?.path || res.response;
      setBookingPaymentInfo((prev) => ({ ...prev, screenshot: path }));
      toast.success("Screenshot Uploaded Successfully");
    }
  };

  // const handleOpenFollowup = (item) => {
  //   setCurrentFollowup(item);
  //   setFollowupData({ date: item.date || "", remarks: item.remarks || "" });
  //   setShowFollowupModal(true);
  // };

  const saveFollowup = async () => {
    const newErrors = {};
    if (!followupData.date) {
      newErrors.date = "Next followup date is Required";
    }
    if (!followupData.remarks?.trim()) {
      newErrors.remarks = "Remarks/Action Points are mandatory";
    }

    if (Object.keys(newErrors).length > 0) {
      setFollowupErrors(newErrors);
      return;
    }

    // Matches PATCH /api/admin/stall-enquiry/status/:id
    const res = await updateStallEnquiryStatus(
      currentFollowup._id || currentFollowup.id,
      {
        status: "Followup",
        date: followupData.date,
        remark: followupData.remarks,
      },
    );

    if (res.status) {
      setFollowupErrors({});
      setShowFollowupModal(false);
      toast.success("Followup Updated Successfully");
      fetchStallEnquiries(selectedExpo._id || selectedExpo.id);
    }
  };

  const handleDeleteClick = (expo) => {
    setExpoToDelete(expo);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (expoToDelete) {
      setIsDeleting(true);
      const res = await deleteExpo(expoToDelete._id || expoToDelete.id);
      if (res.status) {
        toast.success("Expo Deleted Successfully");
        fetchPresentExpos(0, searchTerm);
        setShowDeleteModal(false);
        setExpoToDelete(null);
      } else {
        toast.error("Failed to delete expo");
      }
      setIsDeleting(false);
    }
  };

  // const handleSaveClientFollowup = async (e) => {
  //   e.preventDefault();
  //   const expoId = selectedExpo?._id || selectedExpo?.id;
  //
  //   if (newClientFollowupData.status === "Block" || newClientFollowupData.status === "Blocked") {
  //     const tariff = parseFloat(newEnquiryData.totalAmount) || 0;
  //     const finalPrice = parseFloat(newEnquiryData.discount) || tariff;
  //     const actualDeduction = parseFloat((tariff - finalPrice).toFixed(2));
  //     const payload = {
  //       clientId: selectedClientForModal._id || selectedClientForModal.id,
  //       expoId,
  //       status: "Blocked",
  //       remarks: newEnquiryData.remark,
  //       date: new Date().toISOString().split("T")[0],
  //       name: newEnquiryData.name,
  //       companyName: newEnquiryData.companyName,
  //       mobileNo: newEnquiryData.mobileNo,
  //       email: newEnquiryData.email,
  //       segment: newEnquiryData.segment,
  //       designation: newEnquiryData.designation,
  //       products: newEnquiryData.interestedProduct,
  //       interestedLocations: [],
  //       city: newEnquiryData.city,
  //       state: "",
  //       pincode: "",
  //       address: newEnquiryData.address,
  //       stallNo: newEnquiryData.stallNo,
  //       stallType: newEnquiryData.stallType || undefined,
  //       sqm: newEnquiryData.sqm,
  //       totalAmount: tariff,
  //       discount: actualDeduction,
  //       amount: finalPrice,
  //       approvalStatus: "Pending"
  //     };
  //
  //     const res = await updateClientFollowupStatus(payload);
  //     if (res.status) {
  //       toast.success("Stall blocked successfully.");
  //       setShowClientHistoryModal(false);
  //       fetchPresentExpos(0, searchTerm);
  //     }
  //   }
  // };

  const filteredExpos = presentExpos.filter(
    (item) =>
      (item.name || item.expoName || "")
        .toLowerCase()
        .includes((searchTerm || "").toLowerCase()) ||
      (item.venue || "")
        .toLowerCase()
        .includes((searchTerm || "").toLowerCase()),
  );

  const sharedModals = (
    <>
      {/* Followup Modal */}
      {showFollowupModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background:
                    "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                }}
              >
                <h6 className="modal-title text-white mb-0">
                  Followup: {currentFollowup?.name}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowFollowupModal(false)}
                ></button>
              </div>
              <div className="modal-body p-24">
                <div className="row gy-4 align-items-end">
                  <div className="col-md-5">
                    <label className="form-label fw-bold">
                      Next Followup Date <span className="text-danger">*</span>
                    </label>
                    <StandardDatePicker
                      value={followupData.date}
                      name="date"
                      minDate="today"
                      className={`form-control radius-8 ${followupErrors.date ? "is-invalid" : ""}`}
                      onChange={(e) => {
                        setFollowupData({
                          ...followupData,
                          date: e.target.value,
                        });
                        if (followupErrors.date)
                          setFollowupErrors((prev) => ({
                            ...prev,
                            date: null,
                          }));
                      }}
                    />
                    {followupErrors.date && (
                      <div className="invalid-feedback">
                        {followupErrors.date}
                      </div>
                    )}
                  </div>
                  <div className="col-md-7">
                    <label className="form-label fw-bold">
                      Remarks <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control radius-8 border-secondary-light ${followupErrors.remarks ? "is-invalid" : ""}`}
                      rows="1"
                      placeholder="Enter followup points..."
                      value={followupData.remarks}
                      onChange={(e) => {
                        setFollowupData({
                          ...followupData,
                          remarks: e.target.value,
                        });
                        if (followupErrors.remarks)
                          setFollowupErrors((prev) => ({
                            ...prev,
                            remarks: null,
                          }));
                      }}
                      style={{ height: "44px" }}
                    ></textarea>
                    {followupErrors.remarks && (
                      <div className="invalid-feedback">
                        {followupErrors.remarks}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-24 pt-24 border-top">
                  <h6 className="text-sm fw-bold mb-16 text-primary-600">
                    Followup History
                  </h6>
                  <div
                    className="table-responsive shadow-sm radius-8 border scroll-sm"
                    style={{ height: "150px", overflowY: "auto" }}
                  >
                    <table className="table table-sm mb-0">
                      <thead
                        className="bg-light"
                        style={{ position: "sticky", top: 0, zIndex: 1 }}
                      >
                        <tr>
                          <th className="text-xs fw-bold px-12 py-8">S.No</th>
                          <th className="text-xs fw-bold px-12 py-8">Date</th>
                          <th className="text-xs fw-bold px-12 py-8">Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentFollowup?.history &&
                          currentFollowup.history.length > 0 ? (
                          currentFollowup.history
                            .slice()
                            .reverse()
                            .map((h, i) => (
                              <tr key={h.id}>
                                <td className="text-xs px-12 py-8">{i + 1}</td>
                                <td className="text-xs px-12 py-8">
                                  {formatDate(h.date)}
                                </td>
                                <td className="text-xs px-12 py-8">
                                  {h.remark}
                                </td>
                              </tr>
                            ))
                        ) : (
                          <tr>
                            <td
                              colSpan="3"
                              className="text-center text-xs py-12 text-secondary-light"
                            >
                              No previous history
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24 font-weight-bold"
                  onClick={() => {
                    setShowFollowupModal(false);
                    setFollowupErrors({});
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn radius-8 px-24 text-white fw-bold"
                  style={{ backgroundColor: "#ed1c24" }}
                  onClick={saveFollowup}
                >
                  Save Action
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move to Followup Modal */}
      {showMoveFollowupModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background:
                    "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                }}
              >
                <h6 className="modal-title text-white mb-0">
                  Move to Next Followup
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowMoveFollowupModal(false)}
                ></button>
              </div>
              <div className="modal-body p-24">
                <div className="row gy-4">
                  <div className="col-12">
                    <label className="form-label fw-bold small text-secondary-light uppercase mb-4">
                      Current Expo
                    </label>
                    <div
                      className="form-control radius-8 bg-neutral-50 fw-bold border-secondary-light"
                      style={{ backgroundColor: "#f8fafc" }}
                    >
                      {selectedExpo?.name}
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold small text-secondary-light uppercase mb-4">
                      Next Call Date <span className="text-danger">*</span>
                    </label>
                    <StandardDatePicker
                      value={moveFollowupData.date}
                      minDate="today"
                      className={`form-control radius-8 ${moveFollowupErrors.date ? "is-invalid" : ""}`}
                      onChange={(e) => {
                        setMoveFollowupData({
                          ...moveFollowupData,
                          date: e.target.value,
                        });
                        if (moveFollowupErrors.date)
                          setMoveFollowupErrors((prev) => ({
                            ...prev,
                            date: null,
                          }));
                      }}
                    />
                    {moveFollowupErrors.date && (
                      <div className="invalid-feedback">
                        {moveFollowupErrors.date}
                      </div>
                    )}
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold small text-secondary-light uppercase mb-4">
                      Remark / Action Points{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control radius-8 border-secondary-light ${moveFollowupErrors.remarks ? "is-invalid" : ""}`}
                      rows="3"
                      placeholder="Enter what needs to be discussed..."
                      value={moveFollowupData.remarks}
                      onChange={(e) => {
                        setMoveFollowupData({
                          ...moveFollowupData,
                          remarks: e.target.value,
                        });
                        if (moveFollowupErrors.remarks)
                          setMoveFollowupErrors((prev) => ({
                            ...prev,
                            remarks: null,
                          }));
                      }}
                    ></textarea>
                    {moveFollowupErrors.remarks && (
                      <div className="invalid-feedback">
                        {moveFollowupErrors.remarks}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24 font-weight-bold"
                  onClick={() => {
                    setShowMoveFollowupModal(false);
                    setMoveFollowupErrors({});
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn radius-8 px-32 text-white fw-bold shadow-sm"
                  style={{ backgroundColor: "#ed1c24" }}
                  onClick={handleMoveFollowupConfirm}
                >
                  Move to Followup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Enquiry Modal */}
      {showAddEnquiryModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-2xl text-start">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background:
                    "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                }}
              >
                <h6 className="modal-title text-white mb-0 text-start w-100">
                  ADD STALL ENQUIRY
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleCloseAddEnquiryModal}
                ></button>
              </div>
              <form onSubmit={handleAddEnquirySubmit}>
                <div
                  className="modal-body p-24 overflow-auto"
                  style={{ maxHeight: "70vh" }}
                >
                  <div className="row gy-4">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="mobileNo"
                        className={`form-control radius-8 ${enquiryErrors.mobileNo ? "is-invalid" : ""}`}
                        placeholder="Enter Mobile"
                        required
                        value={newEnquiryData.mobileNo}
                        onChange={(e) => {
                          handleEnquiryChange(e);
                          if (enquiryErrors.mobileNo)
                            setEnquiryErrors((prev) => ({
                              ...prev,
                              mobileNo: null,
                            }));
                        }}
                      />
                      {enquiryErrors.mobileNo && (
                        <div className="invalid-feedback">
                          {enquiryErrors.mobileNo}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        className={`form-control radius-8 ${enquiryErrors.name ? "is-invalid" : ""}`}
                        placeholder="Enter Name"
                        required
                        value={newEnquiryData.name}
                        onChange={(e) => {
                          handleEnquiryChange(e);
                          if (enquiryErrors.name)
                            setEnquiryErrors((prev) => ({
                              ...prev,
                              name: null,
                            }));
                        }}
                      />
                      {enquiryErrors.name && (
                        <div className="invalid-feedback">
                          {enquiryErrors.name}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Company Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        className="form-control radius-8"
                        placeholder="Enter Company"
                        required
                        value={newEnquiryData.companyName}
                        onChange={handleEnquiryChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Status <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={ENQUIRY_STATUS_OPTIONS}
                        value={ENQUIRY_STATUS_OPTIONS.find(
                          (opt) => opt.value === newEnquiryData.status,
                        )}
                        onChange={(selected) =>
                          setNewEnquiryData({
                            ...newEnquiryData,
                            status: selected.value,
                          })
                        }
                        styles={selectStyles()}
                        placeholder="Select Status"
                      />
                    </div>
                    {newEnquiryData.status === "Followup" && (
                      <div className="col-md-6">
                        <label className="form-label fw-bold">
                          Next Followup Date{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <StandardDatePicker
                          value={newEnquiryData.followUpDate}
                          minDate="today"
                          onChange={(e) =>
                            setNewEnquiryData({
                              ...newEnquiryData,
                              followUpDate: e.target.value,
                            })
                          }
                          placeholder="Select Date"
                          className="form-control radius-8"
                        />
                      </div>
                    )}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Designation
                      </label>
                      <input
                        type="text"
                        name="designation"
                        className="form-control radius-8"
                        placeholder="Enter Designation"
                        value={newEnquiryData.designation || ""}
                        onChange={(e) =>
                          setNewEnquiryData({
                            ...newEnquiryData,
                            designation: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Email</label>
                      <input
                        type="email"
                        name="email"
                        className={`form-control radius-8 ${enquiryErrors.email ? "is-invalid" : ""}`}
                        placeholder="Enter Email"
                        value={newEnquiryData.email}
                        onChange={(e) => {
                          handleEnquiryChange(e);
                          if (enquiryErrors.email)
                            setEnquiryErrors((prev) => ({
                              ...prev,
                              email: null,
                            }));
                        }}
                      />
                      {enquiryErrors.email && (
                        <div className="invalid-feedback">
                          {enquiryErrors.email}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold font-weight-bold">
                        Stall No <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={stallOptions}
                        value={stallOptions.find(
                          (opt) => opt.value === newEnquiryData.stallNo,
                        )}
                        onChange={(sel) => {
                          const sqm = sel?.sqm || 0;
                          const totalAmount = sel?.stallValue || 0;
                          setNewEnquiryData({
                            ...newEnquiryData,
                            stallNo: sel?.value || "",
                            sqm: sqm,
                            totalAmount: totalAmount,
                            stallType: sel?.type || "",
                          });
                          if (enquiryErrors.stallNo)
                            setEnquiryErrors((prev) => ({
                              ...prev,
                              stallNo: null,
                            }));
                        }}
                        onInputChange={handleStallSearch}
                        formatOptionLabel={(option) => (
                          <div className="d-flex align-items-center justify-content-between w-100">
                            <span
                              className={
                                option.status === "Followup" ? "fw-bold" : ""
                              }
                            >
                              {option.label}
                            </span>
                            {option.status === "Followup" && (
                              <span className="badge bg-warning-focus text-warning-main radius-pill px-8 py-4 text-xs">
                                Followup
                              </span>
                            )}
                          </div>
                        )}
                        styles={{
                          ...selectStyles(),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor:
                              state.data.status === "Followup"
                                ? state.isSelected
                                  ? "#ed1c24"
                                  : state.isFocused
                                    ? "#ffd6d6"
                                    : "#fff0f0"
                                : state.isSelected
                                  ? "#ed1c24"
                                  : state.isFocused
                                    ? "#f8d7da"
                                    : "white",
                            color: state.isSelected
                              ? "white"
                              : state.data.status === "Followup"
                                ? "#ed1c24"
                                : "black",
                            cursor: "pointer",
                            "&:active": {
                              backgroundColor: "#ed1c24",
                              color: "white",
                            },
                          }),
                        }}
                        placeholder="Select or Search Stall No"
                        isSearchable
                      />
                      {enquiryErrors.stallNo && (
                        <div className="text-danger mt-1 small">
                          {enquiryErrors.stallNo}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Stall Type</label>
                      <input
                        type="text"
                        className="form-control radius-8 bg-light"
                        placeholder="Auto fetch"
                        value={
                          stallTypeOptions.find(
                            (opt) => opt.value === newEnquiryData.stallType,
                          )?.label || ""
                        }
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Sqm</label>
                      <input
                        type="text"
                        className="form-control radius-8 bg-light"
                        placeholder="Auto fetch"
                        value={newEnquiryData.sqm}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Total Amount</label>
                      <input
                        type="text"
                        className="form-control radius-8 bg-light"
                        placeholder="Auto fetch"
                        value={formatIndianNumber(newEnquiryData.totalAmount)}
                        readOnly
                      />
                    </div>
                    {newEnquiryData.status === "Booked" && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            Payment Method{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div
                            className={
                              enquiryErrors.paymentMethod ? "is-invalid" : ""
                            }
                          >
                            <Select
                              options={[
                                { value: "Cash", label: "Cash" },

                                { value: "UPI", label: "UPI" },
                              ]}
                              value={
                                [
                                  { value: "Cash", label: "Cash" },
                                  { value: "UPI", label: "UPI" },
                                ].find(
                                  (opt) =>
                                    opt.value === newEnquiryData.paymentMethod,
                                ) || null
                              }
                              onChange={(sel) => {
                                setNewEnquiryData({
                                  ...newEnquiryData,
                                  paymentMethod: sel.value,
                                  paymentScreenshot: null,
                                });
                                if (enquiryErrors.paymentMethod)
                                  setEnquiryErrors((prev) => ({
                                    ...prev,
                                    paymentMethod: null,
                                  }));
                              }}
                              placeholder="Select Method"
                              styles={selectStyles()}
                            />
                          </div>
                          {enquiryErrors.paymentMethod && (
                            <div className="text-danger mt-1 small">
                              {enquiryErrors.paymentMethod}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            Amount Paid <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control radius-8 ${enquiryErrors.amountPaid ? "is-invalid" : ""}`}
                            placeholder="Enter Amount Paid"
                            value={formatIndianNumber(
                              newEnquiryData.amountPaid,
                            )}
                            onChange={(e) => {
                              const rawVal = e.target.value.replace(/,/g, "");
                              if (!isNaN(rawVal) || rawVal === "") {
                                setNewEnquiryData({
                                  ...newEnquiryData,
                                  amountPaid: rawVal,
                                });
                                if (enquiryErrors.amountPaid)
                                  setEnquiryErrors((prev) => ({
                                    ...prev,
                                    amountPaid: null,
                                  }));
                              }
                            }}
                          />
                          {enquiryErrors.amountPaid && (
                            <div className="invalid-feedback">
                              {enquiryErrors.amountPaid}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            Payment Status{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div
                            className={
                              enquiryErrors.paymentStatus ? "is-invalid" : ""
                            }
                          >
                            <Select
                              options={[
                                { value: "Paid", label: "Fully Paid" },
                                {
                                  value: "Partially",
                                  label: "Partially Paid",
                                },
                              ]}
                              value={
                                [
                                  { value: "Paid", label: "Fully Paid" },
                                  {
                                    value: "Partially",
                                    label: "Partially Paid",
                                  },
                                ].find(
                                  (opt) =>
                                    opt.value === newEnquiryData.paymentStatus,
                                ) || null
                              }
                              onChange={(sel) => {
                                setNewEnquiryData({
                                  ...newEnquiryData,
                                  paymentStatus: sel.value,
                                });
                                if (enquiryErrors.paymentStatus)
                                  setEnquiryErrors((prev) => ({
                                    ...prev,
                                    paymentStatus: null,
                                  }));
                              }}
                              styles={selectStyles()}
                            />
                          </div>
                          {enquiryErrors.paymentStatus && (
                            <div className="invalid-feedback d-block">
                              {enquiryErrors.paymentStatus}
                            </div>
                          )}
                        </div>
                        {newEnquiryData.paymentMethod === "UPI" && (
                          <div className="col-md-6">
                            <label className="form-label fw-bold">
                              Upload Screenshot{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="file"
                              className="form-control radius-8"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const formData = new FormData();
                                  formData.append("file", file);
                                  formData.append("folder", "payments");
                                  const res = await uploadFile(formData);
                                  if (res.status) {
                                    setNewEnquiryData({
                                      ...newEnquiryData,
                                      paymentScreenshot:
                                        res.response?.data?.path ||
                                        res.response,
                                    });
                                    toast.success(
                                      "Screenshot Uploaded Successfully",
                                    );
                                  }
                                }
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div className="col-md-12">
                      <label className="form-label fw-bold">
                        Products <span className="text-danger">*</span>
                      </label>
                      <div
                        className={
                          enquiryErrors.interestedProduct ? "is-invalid" : ""
                        }
                      >
                        <Select
                          isMulti
                          options={productOptions}
                          value={productOptions.filter((opt) =>
                            newEnquiryData.interestedProduct?.includes(
                              opt.value,
                            ),
                          )}
                          onChange={(selected) => {
                            const values = selected
                              ? selected.map((opt) => opt.value)
                              : [];
                            setNewEnquiryData({
                              ...newEnquiryData,
                              interestedProduct: values,
                            });
                            if (enquiryErrors.interestedProduct)
                              setEnquiryErrors((prev) => ({
                                ...prev,
                                interestedProduct: null,
                              }));
                          }}
                          styles={selectStyles()}
                          placeholder="Select Products..."
                          menuPortalTarget={document.body}
                          maxMenuHeight={450}
                        />
                      </div>
                      {enquiryErrors.interestedProduct && (
                        <div className="text-danger mt-1 small">
                          {enquiryErrors.interestedProduct}
                        </div>
                      )}
                    </div>

                    {/* <div className="col-md-12">
                      <label className="form-label fw-bold">Address</label>
                      <textarea
                        className="form-control radius-8"
                        rows="2"
                        placeholder="Enter Address"
                        value={newEnquiryData.address}
                        onChange={(e) =>
                          setNewEnquiryData({
                            ...newEnquiryData,
                            address: e.target.value,
                          })
                        }
                      ></textarea>
                    </div> */}

                    <div className="col-md-12">
                      <label className="form-label fw-bold">City</label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        placeholder="City"
                        value={newEnquiryData.city}
                        onChange={(e) =>
                          setNewEnquiryData({
                            ...newEnquiryData,
                            city: e.target.value.replace(/[0-9]/g, ""),
                          })
                        }
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label fw-bold">
                        Remark <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className={`form-control radius-8 ${enquiryErrors.remark ? "is-invalid" : ""}`}
                        rows="2"
                        placeholder="Enter Remark"
                        value={newEnquiryData.remark}
                        onChange={(e) => {
                          setNewEnquiryData({
                            ...newEnquiryData,
                            remark: e.target.value,
                          });
                          if (enquiryErrors.remark)
                            setEnquiryErrors((prev) => ({
                              ...prev,
                              remark: null,
                            }));
                        }}
                      ></textarea>
                      {enquiryErrors.remark && (
                        <div className="invalid-feedback">
                          {enquiryErrors.remark}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                  <button
                    type="button"
                    className="btn btn-secondary radius-8 px-24 font-weight-bold"
                    onClick={handleCloseAddEnquiryModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn radius-8 px-40 text-white fw-bold shadow-sm"
                    style={{ backgroundColor: "#ed1c24" }}
                    onClick={handleAddEnquirySubmit}
                  >
                    Create Stall Enquiry
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Move Modal */}
      {showConfirmModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg text-center">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background:
                    "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                }}
              >
                <h6 className="modal-title text-white mb-0">Confirm Move</h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body p-24">
                <Icon
                  icon="solar:question-circle-bold"
                  className="text-warning-600 mb-16"
                  style={{ fontSize: "60px" }}
                />
                <h6 className="mb-8">
                  {confirmAction === "move-back"
                    ? "Move back to Upcoming Expo?"
                    : "Move to Past Expo?"}
                </h6>
                <p className="text-secondary-light">
                  Are you sure you want to perform this transition for{" "}
                  {expoToMove?.name}?
                </p>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16 justify-content-center">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn radius-8 px-24 text-white fw-bold"
                  style={{ backgroundColor: "#ed1c24" }}
                  onClick={() => {
                    const targetExpo = expoToMove || selectedExpo;
                    if (!targetExpo) return;
                    if (confirmAction === "move-back") {
                      if (targetExpo.id?.toString().startsWith("static")) {
                        const hidden = JSON.parse(
                          localStorage.getItem("hidden_from_present") || "[]",
                        );
                        localStorage.setItem(
                          "hidden_from_present",
                          JSON.stringify([...hidden, targetExpo.id]),
                        );
                      }
                      const moved = JSON.parse(
                        localStorage.getItem("moved_expos") || "[]",
                      );
                      localStorage.setItem(
                        "moved_expos",
                        JSON.stringify(
                          moved.filter(
                            (e) =>
                              (e.id || e._id) !==
                              (targetExpo.id || targetExpo._id),
                          ),
                        ),
                      );
                      setPresentExpos((prev) =>
                        prev.filter(
                          (e) =>
                            (e.id || e._id) !==
                            (targetExpo.id || targetExpo._id),
                        ),
                      );
                      setActiveView("grid");
                      toast.success("Moved back to Future");
                    } else {
                      if (targetExpo.id?.toString().startsWith("static")) {
                        const hidden = JSON.parse(
                          localStorage.getItem("hidden_from_present") || "[]",
                        );
                        localStorage.setItem(
                          "hidden_from_present",
                          JSON.stringify([...hidden, targetExpo.id]),
                        );
                      }
                      const past = JSON.parse(
                        localStorage.getItem("past_expos") || "[]",
                      );
                      localStorage.setItem(
                        "past_expos",
                        JSON.stringify([
                          ...past,
                          { ...targetExpo, total: targetExpo.assigned || 0 },
                        ]),
                      );
                      const moved = JSON.parse(
                        localStorage.getItem("moved_expos") || "[]",
                      );
                      localStorage.setItem(
                        "moved_expos",
                        JSON.stringify(
                          moved.filter(
                            (e) =>
                              (e.id || e._id) !==
                              (targetExpo.id || targetExpo._id),
                          ),
                        ),
                      );
                      setPresentExpos((prev) =>
                        prev.filter(
                          (e) =>
                            (e.id || e._id) !==
                            (targetExpo.id || targetExpo._id),
                        ),
                      );
                      setActiveView("grid");
                      toast.success("Moved to Past");
                      navigate("/past-expo");
                    }
                    setShowConfirmModal(false);
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Person History Modal */}
      {showHistoryModal && selectedLead && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg overflow-hidden">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background:
                    "linear-gradient(135deg, #000000, #bb0007, #ed1c24)",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="w-40-px h-40-px rounded-circle bg-white-10 d-flex align-items-center justify-content-center text-white border-white-10">
                    <Icon icon="solar:user-bold" />
                  </div>
                  <div>
                    <h6 className="modal-title text-white mb-0">
                      Lead Details - {selectedLead.name}
                    </h6>
                    <span className="text-white-50 text-xs">
                      {selectedLead.companyName || "No Company Specified"}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowHistoryModal(false);
                    setShowInlinePaymentUpdate(false);
                  }}
                ></button>
              </div>
              <div
                className="modal-body p-24 scroll-sm"
                style={{ maxHeight: "75vh", overflowY: "auto" }}
              >
                {selectedLead.FromType === "Visitor" || selectedLead.FromType?.toLowerCase() === "visitor" ? (
                  <div className="row gy-4">
                    <div className="col-12">
                      <div className="p-20 radius-12 bg-neutral-50 border">
                        <h6 className="text-xs fw-bold text-secondary-light uppercase mb-20 border-bottom pb-8">
                          Visitor Registration Information
                        </h6>
                        <div className="row gy-4">

                          <div className="col-md-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Visitor Name</span>
                            <span className="fw-semibold text-dark text-sm">{selectedLead.name || "-"}</span>
                          </div>
                          <div className="col-md-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Company Name</span>
                            <span className="fw-semibold text-dark text-sm">{selectedLead.companyName || "-"}</span>
                          </div>
                          <div className="col-md-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Mobile Number</span>
                            <span className="fw-semibold text-dark text-sm">{selectedLead.mobileNo || selectedLead.phone || "-"}</span>
                          </div>
                          <div className="col-md-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Email Address</span>
                            <span className="fw-semibold text-dark text-sm">{selectedLead.email || "-"}</span>
                          </div>
                          <div className="col-md-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">City</span>
                            <span className="fw-semibold text-dark text-sm">{selectedLead.city || "-"}</span>
                          </div>
                          <div className="col-md-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Business Category</span>
                            <span className="fw-semibold text-dark text-sm">{selectedLead.businessCategory || selectedLead.segment || "-"}</span>
                          </div>
                          <div className="col-md-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Product of Interest</span>
                            <span className="fw-semibold text-dark text-sm">{selectedLead.productOfInterest || "-"}</span>
                          </div>
                          <div className="col-md-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">How do you know about this Exhibition ?</span>
                            <span className="fw-semibold text-dark text-sm">{selectedLead.knowAboutExhibition || "-"}</span>
                          </div>
                          <div className="col-md-6">
                            <span className="text-xs text-secondary-light uppercase fw-bold d-block mb-4">Date</span>
                            <span className="fw-semibold text-dark text-sm">{formatDateTime(selectedLead.createdAt || selectedLead.date)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="row gy-4">
                    <div className="col-md-6">
                      <div className="p-16 radius-12 bg-neutral-50 border h-100">
                        <h6 className="text-xs fw-bold text-secondary-light uppercase mb-16 border-bottom pb-8">
                          Business Information
                        </h6>
                        <div className="d-flex flex-column gap-3">
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="text-sm text-secondary-light">
                              Company Name:
                            </span>
                            <span className="text-sm fw-bold text-dark text-end ms-2">
                              {selectedLead.companyName || "N/A"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="text-sm text-secondary-light">
                              Exhibition Type:
                            </span>
                            <span className="text-sm fw-bold text-dark">
                              {typeof selectedLead.category === "object"
                                ? selectedLead.category?.categoryName ||
                                selectedLead.category?.name ||
                                "General"
                                : selectedLead.category || "General"}
                            </span>
                          </div>
                          {/* <div className="d-flex justify-content-between align-items-start">
                            <span className="text-sm text-secondary-light">
                              Products:
                            </span>
                            <span className="text-sm fw-bold text-dark text-end ms-2">
                              {Array.isArray(selectedLead.products)
                                ? selectedLead.products
                                  .map((p) => p.productName || p.name || p)
                                  .join(", ")
                                : selectedLead.products ||
                                selectedLead.productNames ||
                                "N/A"}
                            </span>
                          </div> */}
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="text-sm text-secondary-light">
                              Designation:
                            </span>
                            <span className="text-sm fw-bold text-dark text-end ms-2">
                              {selectedLead.designation || "N/A"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="text-sm text-secondary-light">
                              Business Category:
                            </span>
                            <span className="text-sm fw-bold text-dark text-end ms-2">
                              {selectedLead.businessCategory || selectedLead.segment || "N/A"}
                            </span>
                          </div>
                          {/* <div className="d-flex justify-content-between align-items-start pt-8 border-top">
                            <span className="text-sm text-secondary-light">
                              Stall Number:
                            </span>
                            <span className="text-sm fw-bold text-primary-600">
                              {selectedLead.stallNo || "-"}
                            </span>
                          </div> */}
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="text-sm text-secondary-light">
                              Stall Size:
                            </span>
                            <span className="text-sm fw-bold text-primary-600">
                              {selectedLead.sqft || selectedLead.sqm ? (
                                <>
                                  {Number(
                                    selectedLead.sqft || selectedLead.sqm,
                                  ).toLocaleString("en-IN")}{" "}
                                  {selectedLead.sqft ? "SQFT" : "SQM"}
                                </>
                              ) : selectedLead.stallDetails ? (
                                selectedLead.stallDetails
                              ) : (
                                "-"
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="p-16 radius-12 bg-neutral-50 border h-100">
                        <h6 className="text-xs fw-bold text-secondary-light uppercase mb-16 border-bottom pb-8">
                          Contact Details
                        </h6>
                        <div className="d-flex flex-column gap-3">
                          <div className="d-flex justify-content-between">
                            <span className="text-sm text-secondary-light">
                              Primary Mobile:
                            </span>
                            <div className="text-end">
                              <span className="text-sm fw-bold text-dark d-block">
                                {selectedLead.phone ||
                                  selectedLead.mobileNo ||
                                  "N/A"}
                              </span>
                              <a
                                href={`https://wa.me/${selectedLead.phone || selectedLead.mobileNo}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-success fw-bold d-flex align-items-center gap-1 justify-content-end mt-1"
                              >
                                <Icon icon="ic:baseline-whatsapp" /> Chat on
                                WhatsApp
                              </a>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-sm text-secondary-light">
                              Email Address:
                            </span>
                            <span className="text-sm fw-bold text-dark">
                              {selectedLead.email || "N/A"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between pt-8 border-top">
                            <span className="text-sm text-secondary-light">
                              Location:
                            </span>
                            <span className="text-sm text-end fw-semibold text-dark">
                              {[
                                selectedLead.city,
                                selectedLead.state,
                                selectedLead.pincode,
                              ]
                                .filter(Boolean)
                                .join(", ") || "N/A"}
                            </span>
                          </div>
                          {/* <div className="d-flex justify-content-between">
                            <span className="text-sm text-secondary-light">
                              Address:
                            </span>
                            <span className="text-xs text-secondary-light text-end max-w-150-px">
                              {selectedLead.address || "N/A"}
                            </span>
                          </div> */}
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="p-16 radius-12 bg-white border">
                        <h6 className="text-xs fw-bold text-secondary-light uppercase mb-16 border-bottom pb-8 d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <span>Interaction History</span>
                          <span
                            className={`badge radius-pill px-12 py-4 ${selectedLead.leadStatus === "Booked"
                              ? "bg-success-focus text-success-main"
                              : selectedLead.leadStatus === "Followup"
                                ? "bg-info-focus text-info-main"
                                : selectedLead.leadStatus === "Rejected"
                                  ? "bg-danger-focus text-danger-main"
                                  : "bg-primary-focus text-primary-main"
                              }`}
                          >
                            CURRENT STATUS:{" "}
                            {(selectedLead.leadStatus || "Enquiry").toUpperCase()}
                          </span>
                        </h6>

                        <div className="timeline-container position-relative ps-20">
                          <div
                            className="timeline-line position-absolute h-100 bg-neutral-200"
                            style={{ width: "2px", left: "6px", top: "0" }}
                          ></div>

                          {/* Initial Creation Record */}
                          <div className="timeline-item position-relative mb-20">
                            <div
                              className="timeline-marker position-absolute rounded-circle bg-primary-600"
                              style={{
                                width: "12px",
                                height: "12px",
                                left: "-19px",
                                top: "4px",
                                border: "2px solid white",
                              }}
                            ></div>
                            <div className="d-flex justify-content-between mb-4">
                              <span className="text-sm fw-bold text-dark">
                                Initial Enquiry
                              </span>
                              <span className="text-xs text-secondary-light">
                                {selectedLead.createdAt
                                  ? new Date(
                                    selectedLead.createdAt,
                                  ).toLocaleString("en-GB")
                                  : ""}
                              </span>
                            </div>
                            <p className="text-sm text-secondary-light mb-0 bg-neutral-50 p-12 radius-8 border italic">
                              {selectedLead.remark ||
                                selectedLead.remarks ||
                                "No initial remark provided."}
                            </p>
                          </div>

                          {/* Status Update History */}
                          {(
                            selectedLead.statusUpdateDetails ||
                            selectedLead.history ||
                            []
                          ).map((item, idx) => (
                            <div
                              className="timeline-item position-relative mb-20"
                              key={idx}
                            >
                              <div
                                className={`timeline-marker position-absolute rounded-circle ${item.status === "Booked"
                                  ? "bg-success-600"
                                  : item.status === "Rejected"
                                    ? "bg-danger-600"
                                    : "bg-info-600"
                                  }`}
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  left: "-19px",
                                  top: "4px",
                                  border: "2px solid white",
                                }}
                              ></div>
                              <div className="d-flex justify-content-between mb-4">
                                <span
                                  className={`text-sm fw-bold ${item.status === "Booked"
                                    ? "text-success-600"
                                    : item.status === "Rejected"
                                      ? "text-danger-600"
                                      : "text-info-600"
                                    }`}
                                >
                                  {item.status === "Followup"
                                    ? "Next Followup Scheduled"
                                    : `Status: ${item.status}`}
                                </span>
                                <span className="text-xs text-secondary-light">
                                  {formatDate(item.date || item.createdAt)}
                                </span>
                              </div>
                              <p
                                className={`text-sm text-secondary-light mb-0 p-12 radius-8 border ${item.status === "Booked"
                                  ? "bg-success-50 border-success-200"
                                  : item.status === "Rejected"
                                    ? "bg-danger-50 border-danger-200"
                                    : "bg-info-50 border-info-200"
                                  }`}
                              >
                                {item.status === "Followup" && (
                                  <strong>Scheduled Remark: </strong>
                                )}
                                {item.remark ||
                                  item.remarks ||
                                  (item.status === "Followup"
                                    ? "Awaiting followup call."
                                    : "No remark provided.")}
                              </p>
                            </div>
                          ))}

                          {selectedLead.leadStatus === "Booked" && (
                            <div className="timeline-item position-relative mb-20">
                              <div
                                className="timeline-marker position-absolute rounded-circle bg-success-600"
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  left: "-19px",
                                  top: "4px",
                                  border: "2px solid white",
                                }}
                              ></div>
                              <div className="d-flex justify-content-between mb-4">
                                <span className="text-sm fw-bold text-success-600">
                                  Booking Confirmed
                                </span>
                                <span className="text-xs text-secondary-light">
                                  {selectedLead.createdAt
                                    ? new Date(
                                      selectedLead.createdAt,
                                    ).toLocaleDateString("en-GB")
                                    : "N/A"}
                                </span>
                              </div>
                              <div className="p-12 radius-8 bg-success-50 border border-success-200">
                                <div className="row g-2">
                                  <div className="col-6 text-xs">
                                    <span className="text-secondary-light">
                                      Payment Method:
                                    </span>{" "}
                                    <span className="fw-bold">
                                      {selectedLead.paymentMethod || "N/A"}
                                    </span>
                                  </div>
                                  <div className="col-6 text-xs text-end">
                                    <span className="text-secondary-light">
                                      Total Contract:
                                    </span>{" "}
                                    <span className="fw-bold text-dark d-block">
                                      ₹
                                      {(
                                        selectedLead.amount ||
                                        selectedLead.totalAmount ||
                                        0
                                      ).toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                  <div className="col-12 text-xs pt-4 d-flex align-items-center justify-content-between">
                                    <div>
                                      <span className="text-secondary-light">
                                        Status:
                                      </span>{" "}
                                      <span
                                        className={`badge px-8 py-2 radius-4 ms-1 ${selectedLead.paymentStatus === "Partially Paid" || selectedLead.paymentStatus === "Partially" ? "bg-warning text-white" : "bg-success text-white"}`}
                                        style={{ fontSize: "12px" }}
                                      >
                                        {selectedLead.paymentStatus === "Paid" ||
                                          selectedLead.paymentStatus ===
                                          "Fully Paid"
                                          ? "FULLY PAID"
                                          : selectedLead.paymentStatus ===
                                            "Partially" ||
                                            selectedLead.paymentStatus ===
                                            "Partially Paid"
                                            ? "PARTIALLY PAID"
                                            : (
                                              selectedLead.paymentStatus ||
                                              "PAID"
                                            ).toUpperCase()}
                                      </span>
                                    </div>
                                    {(selectedLead.paymentStatus ===
                                      "Partially Paid" ||
                                      selectedLead.paymentStatus ===
                                      "Partially") &&
                                      !showInlinePaymentUpdate && (
                                        <button
                                          className="btn btn-xs btn-outline-warning radius-8 py-2 px-8 fw-bold d-flex align-items-center gap-1"
                                          onClick={() => {
                                            setShowInlinePaymentUpdate(true);
                                            setInlinePaymentData({
                                              amount: "",
                                              paymentMethod: "",
                                              remark: `Collection for balance of ${selectedLead.companyName}`,
                                              screenshot: null,
                                            });
                                          }}
                                        >
                                          <Icon icon="solar:hand-stars-bold" />{" "}
                                          Collect Balance
                                        </button>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Partially Paid - Payment Update Form */}
                        {showInlinePaymentUpdate &&
                          (selectedLead.paymentStatus === "Partially Paid" ||
                            selectedLead.paymentStatus === "Partially") && (
                            <div className="mt-20 p-16 radius-12 bg-warning-50 border border-warning-200 animate-fade-in">
                              <div className="d-flex align-items-center justify-content-between mb-12 pb-8 border-bottom border-warning-100">
                                <h6 className="text-xs fw-bold text-warning-700 uppercase mb-0">
                                  Collect Remaining Balance
                                </h6>
                                <button
                                  className="btn btn-sm text-secondary p-0"
                                  onClick={() =>
                                    setShowInlinePaymentUpdate(false)
                                  }
                                >
                                  <Icon icon="solar:close-circle-bold" />
                                </button>
                              </div>
                              <div className="row gy-3">
                                <div className="col-md-6">
                                  <label className="text-xs fw-bold text-warning-700 mb-4">
                                    Additional Amount{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm radius-8 border-warning-200"
                                    placeholder="Enter Amount"
                                    value={formatIndianNumber(
                                      inlinePaymentData.amount,
                                    )}
                                    onChange={(e) => {
                                      const raw = e.target.value.replace(
                                        /,/g,
                                        "",
                                      );
                                      if (!isNaN(raw) || raw === "") {
                                        setInlinePaymentData({
                                          ...inlinePaymentData,
                                          amount: raw,
                                        });
                                      }
                                    }}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="text-xs fw-bold text-warning-700 mb-4">
                                    Payment Type{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <Select
                                    options={[
                                      { value: "Cash", label: "Cash" },
                                      { value: "UPI", label: "UPI" },
                                    ]}
                                    placeholder="Select Type"
                                    value={
                                      [
                                        { value: "Cash", label: "Cash" },
                                        { value: "UPI", label: "UPI" },
                                      ].find(
                                        (opt) =>
                                          opt.value ===
                                          inlinePaymentData.paymentMethod,
                                      ) || null
                                    }
                                    onChange={(sel) =>
                                      setInlinePaymentData({
                                        ...inlinePaymentData,
                                        paymentMethod: sel.value,
                                        screenshot:
                                          sel.value === "UPI"
                                            ? inlinePaymentData.screenshot
                                            : null,
                                      })
                                    }
                                    styles={selectStyles()}
                                  />
                                </div>
                                {inlinePaymentData.paymentMethod === "UPI" && (
                                  <div className="col-12">
                                    <label className="text-xs fw-bold text-warning-700 mb-4">
                                      Payment Screenshot{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    <input
                                      type="file"
                                      className="form-control form-control-sm radius-8 border-warning-200"
                                      onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          const formData = new FormData();
                                          formData.append("file", file);
                                          formData.append("folder", "payments");
                                          const res = await uploadFile(formData);
                                          if (res.status) {
                                            setInlinePaymentData({
                                              ...inlinePaymentData,
                                              screenshot:
                                                res.response?.data?.path ||
                                                res.response,
                                            });
                                            toast.success(
                                              "Screenshot Uploaded Successfully!",
                                            );
                                          }
                                        }
                                      }}
                                    />
                                  </div>
                                )}
                                <div className="col-12">
                                  <label className="text-xs fw-bold text-warning-700 mb-4">
                                    Remarks <span className="text-danger">*</span>
                                  </label>
                                  <textarea
                                    className="form-control form-control-sm radius-8 border-warning-200"
                                    rows="2"
                                    placeholder="Details about this collection..."
                                    value={inlinePaymentData.remark}
                                    onChange={(e) =>
                                      setInlinePaymentData({
                                        ...inlinePaymentData,
                                        remark: e.target.value,
                                      })
                                    }
                                  ></textarea>
                                </div>
                                <div className="col-12 text-end mt-12">
                                  <button
                                    className="btn btn-sm btn-warning text-white radius-8 px-24 fw-bold shadow-sm"
                                    onClick={async () => {
                                      if (
                                        !inlinePaymentData.amount ||
                                        !inlinePaymentData.paymentMethod ||
                                        !inlinePaymentData.remark
                                      ) {
                                        toast.error(
                                          "Please fill all mandatory fields",
                                        );
                                        return;
                                      }
                                      if (
                                        inlinePaymentData.paymentMethod ===
                                        "UPI" &&
                                        !inlinePaymentData.screenshot
                                      ) {
                                        toast.error(
                                          "Screenshot required for UPI",
                                        );
                                        return;
                                      }

                                      // Logic to update payment - using the same status update API
                                      const payload = {
                                        status: "Booked", // Keep it booked
                                        paymentStatus: "Paid", // Update status
                                        paymentMethod:
                                          inlinePaymentData.paymentMethod,
                                        amount:
                                          Number(selectedLead.amount || 0) +
                                          Number(inlinePaymentData.amount), // Add to previous amount
                                        remark: inlinePaymentData.remark,
                                        paymentScreenshot:
                                          inlinePaymentData.screenshot ||
                                          selectedLead.paymentScreenshot,
                                      };

                                      const res = await updateStallEnquiryStatus(
                                        selectedLead._id || selectedLead.id,
                                        payload,
                                      );
                                      if (res.status) {
                                        toast.success(
                                          "Payment Balance Collected Successfully!",
                                        );
                                        setShowInlinePaymentUpdate(false);
                                        setShowHistoryModal(false); // Close modal to refresh
                                        fetchStallEnquiries(
                                          selectedExpo._id || selectedExpo.id,
                                        );
                                      }
                                    }}
                                  >
                                    Update to Fully Paid
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                        {/* Inline Update Section - removed from enquiry view */}
                        {false && (
                          <div className="mt-20 pt-20 border-top border-neutral-200">
                            <h6 className="text-xs fw-bold text-secondary-light uppercase mb-16">
                              Update Lead Status
                            </h6>

                            <div className="d-flex flex-wrap gap-2 mb-16">
                              <button
                                className={`btn btn-sm d-flex align-items-center gap-2 radius-8 ${inlineAction === "Next Followup" ? "btn-info text-white border-info shadow-sm" : "btn-outline-info"}`}
                                onClick={() =>
                                  setInlineAction(
                                    inlineAction === "Next Followup"
                                      ? ""
                                      : "Next Followup",
                                  )
                                }
                              >
                                <Icon
                                  icon="solar:calendar-add-bold"
                                  className="text-lg"
                                />{" "}
                                Add Follow-up
                              </button>
                              <button
                                className={`btn btn-sm d-flex align-items-center gap-2 radius-8 btn-outline-success`}
                                onClick={() => {
                                  setShowHistoryModal(false);
                                  moveEntry(
                                    selectedLead,
                                    selectedLead.leadStatus,
                                    "Booking",
                                  );
                                }}
                              >
                                <Icon
                                  icon="solar:wallet-bold"
                                  className="text-lg"
                                />{" "}
                                Convert to Booking
                              </button>
                              <button
                                className={`btn btn-sm d-flex align-items-center gap-2 radius-8 btn-outline-danger`}
                                onClick={() => {
                                  setShowHistoryModal(false);
                                  moveEntry(
                                    selectedLead,
                                    selectedLead.leadStatus,
                                    "Rejected",
                                  );
                                }}
                              >
                                <Icon
                                  icon="solar:close-circle-bold"
                                  className="text-lg"
                                />{" "}
                                Reject Lead
                              </button>
                            </div>

                            {inlineAction === "Next Followup" && (
                              <div className="p-16 radius-8 bg-info-50 border border-info-200 mt-12 animate-fade-in">
                                <div className="row gy-3">
                                  <div className="col-md-4">
                                    <label className="text-xs fw-bold text-info-700 mb-4">
                                      Next Date{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    <StandardDatePicker
                                      value={inlineFollowupData.date}
                                      minDate="today"
                                      onChange={(e) =>
                                        setInlineFollowupData({
                                          ...inlineFollowupData,
                                          date: e.target.value,
                                        })
                                      }
                                      placeholder="Select Date"
                                      className="form-control radius-8 border-info-200"
                                    />
                                  </div>
                                  <div className="col-md-8">
                                    <label className="text-xs fw-bold text-info-700 mb-4">
                                      Remarks{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                      className="form-control radius-8 border-info-200"
                                      placeholder="Enter followup points..."
                                      value={inlineFollowupData.remarks}
                                      onChange={(e) =>
                                        setInlineFollowupData({
                                          ...inlineFollowupData,
                                          remarks: e.target.value,
                                        })
                                      }
                                      rows="1"
                                      style={{
                                        height: "44px",
                                        minHeight: "44px",
                                      }}
                                    ></textarea>
                                  </div>
                                  <div className="col-12 text-end mt-16 pt-16 border-top border-info-200">
                                    <button
                                      className="btn btn-info text-white radius-8 px-24 fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                                      onClick={async () => {
                                        if (
                                          !inlineFollowupData.date ||
                                          !inlineFollowupData.remarks
                                        ) {
                                          toast.error(
                                            "Please fill date and remarks for followup",
                                          );
                                          return;
                                        }

                                        const res = await executeMove(
                                          selectedLead,
                                          selectedLead.leadStatus,
                                          "Next Followup",
                                          {
                                            status: "Followup",
                                            date: inlineFollowupData.date,
                                            remark: inlineFollowupData.remarks,
                                          },
                                        );

                                        if (res && res.status) {
                                          const updatedItem =
                                            res.response?.data || res.response;
                                          setSelectedLead({
                                            ...updatedItem,
                                            businessCategory: updatedItem?.businessCategory || updatedItem?.segment || ""
                                          });
                                          setInlineFollowupData({
                                            date: "",
                                            remarks: "",
                                          });
                                          setInlineAction("");
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:check-circle-bold" />{" "}
                                      Save Follow-up
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                <div className="d-flex align-items-center gap-2 me-auto text-secondary-light">
                  <Icon icon="solar:info-circle-bold" />
                  <span className="text-xs">
                    History is based on current lead status.
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24"
                  onClick={() => {
                    setShowHistoryModal(false);
                    setInlineAction("");
                  }}
                >
                  Close Popup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Detail Modal */}
      {showClientDetailModal && selectedClientForModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background:
                    "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                }}
              >
                <h6 className="modal-title text-white mb-0">
                  Client Details: {selectedClientForModal.companyName}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowClientDetailModal(false)}
                ></button>
              </div>
              <div className="modal-body p-24">
                <div className="row gy-4">
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Company Name</span>
                    <div className="p-12 bg-neutral-50 radius-8 border fw-bold text-dark">{selectedClientForModal.companyName || "N/A"}</div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Key Person Name</span>
                    <div className="p-12 bg-neutral-50 radius-8 border fw-semibold text-dark">{selectedClientForModal.keyPerson || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Mobile Number 1</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.mobile1 || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Mobile Number 2</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.mobile2 || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Mobile Number 3</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.mobile3 || "N/A"}</div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Email Address</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.email || "N/A"}</div>
                  </div>
                  <div className="col-md-3">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Ratings</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark fw-bold text-warning-600">{selectedClientForModal.ratings || "N/A"} ★</div>
                  </div>
                  <div className="col-md-3">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Designation</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark fw-semibold">{selectedClientForModal.designation || "N/A"}</div>
                  </div>
                  <div className="col-12">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Address</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">
                      {selectedClientForModal.address || "N/A"}, {selectedClientForModal.city || ""}, {selectedClientForModal.pincode || ""}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Landline</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">{selectedClientForModal.landline || "N/A"}</div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Website Link</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">
                      {selectedClientForModal.websiteLink ? (
                        <a href={selectedClientForModal.websiteLink.startsWith("http") ? selectedClientForModal.websiteLink : `http://${selectedClientForModal.websiteLink}`} target="_blank" rel="noreferrer">
                          {selectedClientForModal.websiteLink}
                        </a>
                      ) : "N/A"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Interested Products</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">
                      {Array.isArray(selectedClientForModal.products)
                        ? selectedClientForModal.products.map((p) => p.name || p.productName || p).join(", ")
                        : "N/A"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <span className="text-secondary-light small fw-bold d-block uppercase mb-4">Interested Locations</span>
                    <div className="p-12 bg-neutral-50 radius-8 border text-dark">
                      {Array.isArray(selectedClientForModal.interestedLocations)
                        ? selectedClientForModal.interestedLocations.map((l) => l.name || l.locationName || l).join(", ")
                        : "N/A"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24 font-weight-bold"
                  onClick={() => setShowClientDetailModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client History Modal */}
      {showClientHistoryModal && selectedClientForModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background:
                    "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                }}
              >
                <h6 className="modal-title text-white mb-0">
                  Followup History: {selectedClientForModal.companyName}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowClientHistoryModal(false)}
                ></button>
              </div>
              <div className="modal-body p-24">


                {/* History Table */}
                <h6 className="text-sm fw-bold mb-12 text-danger-600 uppercase">Followup Log Details</h6>
                <div
                  className="table-responsive shadow-sm radius-8 border scroll-sm"
                  style={{ maxHeight: "250px", overflowY: "auto" }}
                >
                  <table className="table table-sm mb-0">
                    <thead className="bg-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr>
                        <th className="text-xs fw-bold px-12 py-8">S.No</th>
                        <th className="text-xs fw-bold px-12 py-8">Created At</th>
                        <th className="text-xs fw-bold px-12 py-8">Next Followup Date</th>
                        <th className="text-xs fw-bold px-12 py-8">Status</th>
                        <th className="text-xs fw-bold px-12 py-8">Remark</th>
                        <th className="text-xs fw-bold px-12 py-8">Updated By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientFollowupHistory && clientFollowupHistory.length > 0 ? (
                        clientFollowupHistory.map((h, i) => (
                          <tr key={i}>
                            <td className="text-xs px-12 py-8">{i + 1}</td>
                            <td className="text-xs px-12 py-8">{formatDate(h.createdAt || h.date)}</td>
                            <td className="text-xs px-12 py-8">
                              {(h.status === "Todayfolloup" || h.status === "Prospects") && h.date
                                ? <span className="badge bg-info-100 text-info-800 px-8 py-2 radius-4 text-xs">{formatDate(h.date)}</span>
                                : <span className="text-secondary-light">—</span>}
                            </td>
                            <td className="text-xs px-12 py-8">
                              <span className={`badge px-8 py-2 radius-4 text-xs ${h.status === 'Assignes' ? 'bg-primary-100 text-primary-800' :
                                h.status === 'Todayfolloup' ? 'bg-info-100 text-info-800' :
                                  h.status === 'Prospects' ? 'bg-warning-100 text-warning-800' :
                                    h.status === 'Not interested' ? 'bg-danger-100 text-danger-800' :
                                      (h.status === 'Block' || h.status === 'Blocked') ? 'bg-warning-100 text-warning-800' :
                                        h.status === 'Booked' ? 'bg-success-100 text-success-800' :
                                          (h.status === 'Reject' || h.status === 'Rejected') ? 'bg-danger-100 text-danger-800' : 'bg-secondary-100 text-secondary-800'
                                }`}>
                                {h.status === "Assignes" ? "Assigns" :
                                  h.status === "Todayfolloup" ? "Next Followup" :
                                    h.status === "Prospects" ? "Prospect" :
                                      h.status === "Not interested" ? "Not interested" :
                                        (h.status === "Block" || h.status === "Blocked") ? "Blocked Stall" :
                                          h.status === "Booked" ? "Booked" :
                                            (h.status === 'Reject' || h.status === 'Rejected') ? "Rejected" : h.status}
                              </span>
                            </td>
                            <td className="text-xs px-12 py-8">{h.remarks || h.remark || "No comments"}</td>
                            <td className="text-xs px-12 py-8 fw-semibold">{h.createdByName || "Admin"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-xs py-16 text-secondary-light">
                            No previous followup history log details.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24 font-weight-bold"
                  onClick={() => setShowClientHistoryModal(false)}
                >
                  Close History
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Direct Booking Modal */}
      {showBulkAssignModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div className="modal-header border-bottom py-16 px-24">
                <h6 className="modal-title mb-0 d-flex align-items-center gap-2">
                  <Icon icon="solar:user-hand-up-bold" className="text-lg" />
                  Assign to Another Manager
                </h6>
                <button type="button" className="btn-close" onClick={() => setShowBulkAssignModal(false)} />
              </div>
              <div className="modal-body p-24">
                <label className="form-label fw-bold text-secondary-light">Select Manager *</label>
                <Select
                  options={(() => {
                    const managers = Array.isArray(selectedExpo?.eventManager)
                      ? selectedExpo.eventManager
                      : selectedExpo?.eventManager?.name
                        ? [selectedExpo.eventManager]
                        : [];
                    return managers.map((m) => ({
                      value: m._id || m.id,
                      label: m.name
                    }));
                  })()}
                  value={(() => {
                    const managers = Array.isArray(selectedExpo?.eventManager)
                      ? selectedExpo.eventManager
                      : selectedExpo?.eventManager?.name
                        ? [selectedExpo.eventManager]
                        : [];
                    const selectedMgr = managers.find(m => (m._id || m.id) === bulkAssignManagerId);
                    return selectedMgr ? { value: selectedMgr._id || selectedMgr.id, label: selectedMgr.name } : null;
                  })()}
                  onChange={(sel) => setBulkAssignManagerId(sel?.value || "")}
                  placeholder="-- Select Manager --"
                  isSearchable
                  styles={selectStyles()}
                />
                <div className="mt-4 text-end">
                  <button className="btn btn-secondary radius-8 px-24 me-2" onClick={() => setShowBulkAssignModal(false)} disabled={bulkAssigning}>Cancel</button>
                  <button
                    className="btn btn-danger radius-8 px-24 fw-bold text-white shadow-sm"
                    style={{ backgroundColor: "#ed1c24", borderColor: "#ed1c24" }}
                    disabled={bulkAssigning || !bulkAssignManagerId}
                    onClick={async () => {
                      setBulkAssigning(true);
                      const res = await bulkAssignFollowups({
                        expoId: selectedExpo?._id || selectedExpo?.id,
                        followupIds: selectedAssigns,
                        managerId: bulkAssignManagerId
                      });
                      setBulkAssigning(false);
                      if (res.status) {
                        toast.success("Assigned successfully!");
                        setShowBulkAssignModal(false);
                        setSelectedAssigns([]);
                        setBulkAssignManagerId("");
                        fetchClientFollowupsList(selectedExpo?._id || selectedExpo?.id, clientFollowupStatus, clientFollowupsPage, clientFollowupsLimit, clientFollowupSearchTerm, selectedManagerFilter, selectedGroupFilter);
                      } else {
                        toast.error(res.response?.message || "Failed to assign managers");
                      }
                    }}
                  >
                    {bulkAssigning ? "Assigning..." : "Assign"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDirectBookingModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content radius-16 border-0 shadow-lg" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                }}
              >
                <h6 className="modal-title text-white mb-0 d-flex align-items-center gap-2">
                  <Icon icon="solar:calendar-add-bold" className="text-lg" />
                  Direct Stall Booking Form
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDirectBookingModal(false)}
                />
              </div>
              <div className="modal-body p-24 text-start">
                <form onSubmit={handleDirectBookingSubmit} className="row g-3 text-xs">
                  {/* Mobile Number */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Mobile Number *</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter 10-digit mobile number"
                      maxLength="10"
                      required
                      value={newEnquiryData.mobileNo}
                      onChange={handleMobileChange}
                    />
                  </div>

                  {/* Key Person Name */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Key Person Name *</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter Key Person Name"
                      required
                      value={newEnquiryData.name}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, name: e.target.value })}
                    />
                  </div>

                  {/* Company Name */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Company Name *</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter Company Name"
                      required
                      value={newEnquiryData.companyName}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, companyName: e.target.value })}
                    />
                  </div>

                  {/* Designation */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Designation</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter Designation"
                      value={newEnquiryData.designation || ""}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, designation: e.target.value })}
                    />
                  </div>

                  {/* Email */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Email Address</label>
                    <input
                      type="email"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter Email"
                      value={newEnquiryData.email || ""}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, email: e.target.value })}
                    />
                  </div>

                  {/* City */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">City</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter City"
                      value={newEnquiryData.city || ""}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, city: e.target.value.replace(/[0-9]/g, "") })}
                    />
                  </div>

                  {/* Products */}
                  <div className="col-md-12">
                    <label className="form-label fw-bold text-secondary-light">Products</label>
                    <Select
                      isMulti
                      options={productOptions}
                      value={productOptions.filter((opt) => newEnquiryData.interestedProduct?.includes(opt.value))}
                      onChange={(selected) => {
                        const values = selected ? selected.map((opt) => opt.value) : [];
                        setNewEnquiryData({ ...newEnquiryData, interestedProduct: values });
                      }}
                      styles={selectStyles()}
                      placeholder="Select Products..."
                    />
                  </div>

                  {/* Interested Locations */}
                  <div className="col-md-12">
                    <label className="form-label fw-bold text-secondary-light">Interested Locations</label>
                    <Select
                      isMulti
                      options={locationOptions}
                      value={locationOptions.filter((opt) => newEnquiryData.interestedLocations?.includes(opt.value))}
                      onChange={(selected) => {
                        const values = selected ? selected.map((opt) => opt.value) : [];
                        setNewEnquiryData({ ...newEnquiryData, interestedLocations: values });
                      }}
                      styles={selectStyles()}
                      placeholder="Select Locations..."
                    />
                  </div>

                  {/* Created By Selection */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Created By *</label>
                    <Select
                      options={[
                        { value: "Myself", label: "Myself" },
                        { value: "Manager", label: "Manager" },
                        { value: "Overall", label: "Overall" }
                      ]}
                      value={{ value: bookingCreatorType, label: bookingCreatorType }}
                      onChange={(sel) => {
                        setBookingCreatorType(sel.value);
                        setBookingCreatorManagerId("");
                      }}
                      styles={selectStyles()}
                    />
                  </div>

                  {(bookingCreatorType === "Manager" || bookingCreatorType === "Overall") && (
                    <div className="col-md-6">
                      <label className="form-label fw-bold text-secondary-light">Select Manager *</label>
                      <Select
                        options={(() => {
                          if (bookingCreatorType === "Overall") {
                            return managersList.map((m) => ({
                              value: m.value || m._id || m.id,
                              label: m.label || m.name
                            }));
                          }
                          const managers = Array.isArray(selectedExpo?.eventManager)
                            ? selectedExpo.eventManager
                            : selectedExpo?.eventManager?.name
                              ? [selectedExpo.eventManager]
                              : [];
                          return managers.map((m) => ({
                            value: m._id || m.id,
                            label: m.name
                          }));
                        })()}
                        value={(() => {
                          let optionsList = [];
                          if (bookingCreatorType === "Overall") {
                            optionsList = managersList.map((m) => ({
                              value: m.value || m._id || m.id,
                              label: m.label || m.name
                            }));
                          } else {
                            const managers = Array.isArray(selectedExpo?.eventManager)
                              ? selectedExpo.eventManager
                              : selectedExpo?.eventManager?.name
                                ? [selectedExpo.eventManager]
                                : [];
                            optionsList = managers.map((m) => ({
                              value: m._id || m.id,
                              label: m.name
                            }));
                          }
                          const selectedMgr = optionsList.find(m => m.value === bookingCreatorManagerId);
                          return selectedMgr || null;
                        })()}
                        onChange={(sel) => setBookingCreatorManagerId(sel?.value || "")}
                        placeholder="-- Select Manager --"
                        isSearchable
                        styles={selectStyles()}
                      />
                    </div>
                  )}

                  {/* Stall Selection */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Stall Number *</label>
                    {(() => {
                      const availableStalls = stallOptions.filter(opt => opt.status !== 'Blocked' && opt.status !== 'Booked');
                      return (
                        <Select
                          options={availableStalls}
                          value={stallOptions.find((opt) => opt.value === newEnquiryData.stallNo)}
                          onChange={(sel) => {
                            const sqm = sel?.sqm || 0;
                            const totalAmount = sel?.withgstvalue || sel?.stallValue || 0;
                            setNewEnquiryData({
                              ...newEnquiryData,
                              stallNo: sel?.value || "",
                              sqm: sqm,
                              totalAmount: totalAmount,
                              stallType: sel?.type || "",
                            });
                          }}
                          placeholder="Search & Select Stall"
                          isSearchable
                          styles={selectStyles()}
                        />
                      );
                    })()}
                  </div>

                  {/* Stall Type (Auto-fetch) */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Stall Type</label>
                    <input
                      type="text"
                      className="form-control radius-8 bg-light form-control-sm"
                      placeholder="Auto fetch"
                      value={
                        stallTypeOptions.find(
                          (opt) => opt.value === newEnquiryData.stallType,
                        )?.label || ""
                      }
                      readOnly
                    />
                  </div>

                  {/* Sqm (Auto-fetch) */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Sqm</label>
                    <input
                      type="text"
                      className="form-control radius-8 bg-light form-control-sm"
                      placeholder="Auto fetch"
                      value={newEnquiryData.sqm || ""}
                      readOnly
                    />
                  </div>

                  {/* Tariff (Auto-fetch) */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Tariff Rate (with GST)</label>
                    <input
                      type="text"
                      className="form-control radius-8 bg-light form-control-sm"
                      placeholder="Auto fetch"
                      value={formatIndianNumber(newEnquiryData.totalAmount)}
                      readOnly
                    />
                  </div>

                  {/* Final Amount (Discounted / Price to collect) */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Final Amount (After Discount) *</label>
                    <input
                      type="text"
                      className="form-control radius-8 form-control-sm"
                      placeholder="Enter net price to collect"
                      required
                      value={newEnquiryData.discount || ""}
                      onChange={(e) => {
                        const rawVal = e.target.value.replace(/[^0-9.]/g, "");
                        setNewEnquiryData({
                          ...newEnquiryData,
                          discount: rawVal,
                        });
                      }}
                    />
                    {newEnquiryData.discount && newEnquiryData.totalAmount && (
                      <div className="text-xs text-secondary-light mt-1">
                        Discount off: ₹{formatIndianNumber(parseFloat((parseFloat(newEnquiryData.totalAmount) - parseFloat(newEnquiryData.discount)).toFixed(2)))}
                      </div>
                    )}
                  </div>

                  {/* Remarks */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-secondary-light">Remarks</label>
                    <textarea
                      className="form-control radius-8 form-control-sm"
                      rows="2"
                      placeholder="Remarks / Comments"
                      value={newEnquiryData.remark || ""}
                      onChange={(e) => setNewEnquiryData({ ...newEnquiryData, remark: e.target.value })}
                    />
                  </div>

                  <div className="col-12 text-end border-top pt-16">
                    <button
                      type="button"
                      className="btn btn-secondary radius-8 px-24 me-2"
                      onClick={() => setShowDirectBookingModal(false)}
                      disabled={directBookingSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-danger radius-8 px-24 text-white fw-bold shadow-sm"
                      style={{ backgroundColor: "#ed1c24", borderColor: "#ed1c24" }}
                      disabled={directBookingSubmitting}
                    >
                      {directBookingSubmitting ? "Booking..." : "Book Stall"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block Stall Detail Modal */}
      {showBlockDetailModal && selectedBlockItem && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{ background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)" }}
              >
                <h6 className="modal-title text-white mb-0 d-flex align-items-center gap-2">
                  <Icon icon="solar:lock-keyhole-bold" className="text-lg" />
                  Stall Booking Details — {selectedBlockItem.companyName || selectedBlockItem.client?.companyName}
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => { setShowBlockDetailModal(false); setSelectedBlockItem(null); setSelectedBlockPayments([]); }}
                />
              </div>
              <div className="modal-body p-24">
                <div className={`alert mb-20 py-12 px-16 radius-12 d-flex align-items-center gap-3 border-0 ${selectedBlockItem.approvalStatus === "Approved" ? "bg-success-100" :
                  selectedBlockItem.approvalStatus === "Rejected" ? "bg-danger-100" : "bg-warning-100"
                  }`}>
                  <Icon icon={
                    selectedBlockItem.approvalStatus === "Approved" ? "solar:check-circle-bold" :
                      selectedBlockItem.approvalStatus === "Rejected" ? "solar:close-circle-bold" : "solar:clock-circle-bold"
                  } className={`text-xl ${selectedBlockItem.approvalStatus === "Approved" ? "text-success-600" :
                    selectedBlockItem.approvalStatus === "Rejected" ? "text-danger-600" : "text-warning-600"
                    }`} />
                  <div>
                    <div className={`fw-bold text-sm ${selectedBlockItem.approvalStatus === "Approved" ? "text-success-800" :
                      selectedBlockItem.approvalStatus === "Rejected" ? "text-danger-800" : "text-warning-800"
                      }`}>Booking Status: {selectedBlockItem.approvalStatus || "Pending"}</div>
                    {selectedBlockItem.remarks && <div className="text-xs text-secondary-light mt-1">{selectedBlockItem.remarks}</div>}
                  </div>
                </div>
                <div className="row gy-3">
                  <div className="col-12">
                    <h6 className="text-xs fw-bold text-secondary-light uppercase mb-12 border-bottom pb-8">Company Information</h6>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Company Name</div>
                    <div className="text-sm fw-bold text-dark">{selectedBlockItem.companyName || selectedBlockItem.client?.companyName || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Key Person</div>
                    <div className="text-sm text-dark">{selectedBlockItem.name || selectedBlockItem.client?.keyPerson || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Mobile</div>
                    <div className="text-sm text-dark">{selectedBlockItem.mobileNo || selectedBlockItem.client?.mobile1 || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Email</div>
                    <div className="text-sm text-dark">{selectedBlockItem.email || selectedBlockItem.client?.email || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Designation</div>
                    <div className="text-sm text-dark">{selectedBlockItem.designation || "N/A"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-xs text-secondary-light fw-bold mb-4">Manager</div>
                    <div className="text-sm text-dark">{selectedBlockItem.manager?.name || "N/A"}</div>
                  </div>
                  <div className="col-12 mt-8">
                    <h6 className="text-xs fw-bold text-secondary-light uppercase mb-12 border-bottom pb-8">Stall Blocks & Financial Details</h6>
                  </div>
                  <div className="col-12">
                    <div className="d-flex flex-column gap-3">
                      {(() => {
                        const blockDetails = (selectedBlockItem.statusDetails || []).filter(d =>
                          d.status === 'Blocked' || d.status === 'Block' || d.status === 'Booked'
                        );

                        const uniqueStallBlocks = {};
                        blockDetails.forEach(d => {
                          if (d.stallNo) {
                            if (!uniqueStallBlocks[d.stallNo]) {
                              uniqueStallBlocks[d.stallNo] = { ...d };
                            } else {
                              const existing = uniqueStallBlocks[d.stallNo];
                              uniqueStallBlocks[d.stallNo] = {
                                ...existing,
                                status: d.status || existing.status,
                                approvalStatus: d.approvalStatus || existing.approvalStatus,
                                bookedTime: d.bookedTime || existing.bookedTime,
                                remarks: d.remarks || existing.remarks,
                                sqm: d.sqm || existing.sqm,
                                stallType: d.stallType || existing.stallType,
                                totalAmount: d.totalAmount || existing.totalAmount,
                                discount: (d.discount !== undefined && d.discount !== null) ? d.discount : existing.discount,
                                amount: d.amount || existing.amount,
                                blockedDate: d.blockedDate || existing.blockedDate,
                                name: d.name || existing.name,
                                companyName: d.companyName || existing.companyName,
                                mobileNo: d.mobileNo || existing.mobileNo,
                                email: d.email || existing.email,
                                products: (d.products && d.products.length > 0) ? d.products : existing.products,
                                interestedLocations: (d.interestedLocations && d.interestedLocations.length > 0) ? d.interestedLocations : existing.interestedLocations
                              };
                            }
                          }
                        });

                        const uniqueStallList = Object.values(uniqueStallBlocks);
                        const finalStallList = uniqueStallList.length > 0 ? uniqueStallList : [{
                          stallNo: selectedBlockItem.stallNo,
                          stallType: selectedBlockItem.stallType,
                          sqm: selectedBlockItem.sqm,
                          totalAmount: selectedBlockItem.totalAmount,
                          discount: selectedBlockItem.discount,
                          amount: selectedBlockItem.amount,
                          approvalStatus: selectedBlockItem.approvalStatus || 'Pending',
                          blockedDate: selectedBlockItem.blockedDate,
                          bookedTime: selectedBlockItem.bookedTime
                        }];

                        return finalStallList.map((stallBlock, sIdx) => {
                          const displayApprovalStatus = stallBlock.approvalStatus || "Pending";
                          const isCurrentlyEditing = editingStallNo === stallBlock.stallNo;
                          const totalPaidAmount = (selectedBlockPayments || [])
                            .filter(p => p.paymentStatus === 'Completed')
                            .reduce((sum, p) => sum + (Number(p.amountPaid) || 0), 0);
                          return (
                            <div key={sIdx} className="p-16 border radius-12 bg-light shadow-sm">
                              <div className="d-flex justify-content-between align-items-center mb-12 flex-wrap gap-2 pb-8 border-bottom">
                                <div className="d-flex align-items-center gap-2">
                                  <Icon icon="solar:widget-5-bold" className="text-primary-600 text-lg" />
                                  <span className="fw-bold text-dark text-md">Stall {stallBlock.stallNo || "N/A"}</span>
                                  <span className={`badge px-10 py-6 radius-4 text-xs uppercase ${displayApprovalStatus === 'Approved' ? 'bg-success-100 text-success-800' :
                                    displayApprovalStatus === 'Rejected' ? 'bg-danger-100 text-danger-800' : 'bg-warning-100 text-warning-800'
                                    }`}>
                                    {displayApprovalStatus}
                                  </span>
                                </div>
                                {displayApprovalStatus === "Pending" ? (
                                  <div className="d-flex gap-2">
                                    {isCurrentlyEditing ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1"
                                          style={{ backgroundColor: "#1d4ed8", fontSize: "12px" }}
                                          disabled={editSaving}
                                          onClick={async () => {
                                            if (!editAmount || isNaN(Number(editAmount))) {
                                              toast.error("Please enter a valid net amount");
                                              return;
                                            }
                                            setEditSaving(true);
                                            const formattedAddons = editAddons
                                              .filter(a => (a.title && a.title.trim() !== "") || Number(a.amount) > 0)
                                              .map(a => {
                                                const amt = Number(a.amount) || 0;
                                                const gst = a.hasGst ? Math.round(amt * 0.18) : 0;
                                                return {
                                                  title: (a.title || "").trim() || "Add-on",
                                                  amount: amt,
                                                  gst: gst,
                                                  total: amt + gst
                                                };
                                              });

                                            const res = await editStallFinancials({
                                              followupId: selectedBlockItem._id,
                                              stallNo: stallBlock.stallNo,
                                              amount: Number(editAmount),
                                              remarks: editRemarks,
                                              addons: formattedAddons
                                            });
                                            setEditSaving(false);
                                            if (res.status) {
                                              toast.success("Stall financials updated successfully");
                                              setEditingStallNo(null);
                                              const updatedFollowup = res.response?.data || res.response;
                                              if (updatedFollowup) {
                                                const mergedFollowup = {
                                                  ...updatedFollowup,
                                                  products: (updatedFollowup.products && typeof updatedFollowup.products[0] === 'object')
                                                    ? updatedFollowup.products
                                                    : (selectedBlockItem?.products || updatedFollowup.products),
                                                  interestedLocations: (updatedFollowup.interestedLocations && typeof updatedFollowup.interestedLocations[0] === 'object')
                                                    ? updatedFollowup.interestedLocations
                                                    : (selectedBlockItem?.interestedLocations || updatedFollowup.interestedLocations)
                                                };
                                                setSelectedBlockItem(mergedFollowup);
                                              }
                                              fetchClientFollowupsList(selectedExpo?._id || selectedExpo?.id, clientFollowupStatus, clientFollowupsPage, clientFollowupsLimit, clientFollowupSearchTerm, selectedManagerFilter, selectedGroupFilter);
                                            } else {
                                              toast.error(res.response?.message || "Failed to update stall financials");
                                            }
                                          }}
                                        >
                                          {editSaving ? "Saving..." : "Save"}
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1 btn-secondary"
                                          style={{ fontSize: "12px" }}
                                          disabled={editSaving}
                                          onClick={() => {
                                            setEditingStallNo(null);
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1"
                                          style={{ backgroundColor: "#0284c7", fontSize: "12px" }}
                                          onClick={() => {
                                            setEditingStallNo(stallBlock.stallNo);
                                            const currentAddons = (stallBlock.addons && stallBlock.addons.length > 0)
                                              ? stallBlock.addons
                                              : (selectedBlockItem.addons || []);
                                            const addonBase = currentAddons.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
                                            const addonGst = currentAddons.reduce((sum, a) => sum + (Number(a.gst) !== undefined ? Number(a.gst) : 0), 0);
                                            const addonTotal = addonBase + addonGst;
                                            const overallTotal = Number(stallBlock.amount) || 0;
                                            const stallTotalWithGst = Math.max(0, overallTotal - addonTotal);
                                            const initialStallBase = stallTotalWithGst > 0 ? Math.round(stallTotalWithGst / 1.18) : 0;

                                            setEditAmount(initialStallBase || "");
                                            setEditRemarks(stallBlock.remarks || "");
                                            setEditAddons(
                                              currentAddons.map(a => ({
                                                title: a.title || "",
                                                amount: a.amount !== undefined ? a.amount : "",
                                                hasGst: a.gst !== undefined ? (Number(a.gst) > 0) : false
                                              }))
                                            );
                                          }}
                                        >
                                          <Icon icon="solar:pen-bold" /> Edit
                                        </button>
                                        {totalPaidAmount === 0 && (
                                          <button
                                            type="button"
                                            className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1"
                                            style={{ backgroundColor: "#dc2626", fontSize: "12px" }}
                                            onClick={() => {
                                              setShowStallDetailConfirm(true);
                                              setStallDetailConfirmData({
                                                stallNo: stallBlock.stallNo,
                                                action: "Rejected"
                                              });
                                            }}
                                          >
                                            <Icon icon="solar:close-circle-bold" /> Reject
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1"
                                          style={{ backgroundColor: "#16a34a", fontSize: "12px" }}
                                          onClick={() => {
                                            setShowStallDetailConfirm(true);
                                            setStallDetailConfirmData({
                                              stallNo: stallBlock.stallNo,
                                              action: "Approved"
                                            });
                                          }}
                                        >
                                          <Icon icon="solar:check-circle-bold" /> Approve
                                        </button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="d-flex gap-2">
                                    {totalPaidAmount === 0 && (
                                      <button
                                        type="button"
                                        className="btn btn-sm px-16 radius-6 text-white fw-bold d-flex align-items-center gap-1"
                                        style={{ backgroundColor: "#dc2626", fontSize: "12px" }}
                                        onClick={() => {
                                          setShowForceRejectModal(true);
                                          setForceRejectData({ stallNo: stallBlock.stallNo, followupId: selectedBlockItem._id });
                                          setForceRejectInput("");
                                          setForceRejectReason("");
                                        }}
                                      >
                                        <Icon icon="solar:close-circle-bold" /> Reject
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="row gy-2 text-xs">
                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Stall Type:</span> <span className="text-dark fw-semibold">{
                                    typeof stallBlock.stallType === "object"
                                      ? stallBlock.stallType?.stallTypeName
                                      : (stallTypeOptions.find(o => o.value === stallBlock.stallType)?.label || stallBlock.stallType || "N/A")
                                  }</span>
                                </div>
                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Size:</span> <span className="text-dark fw-semibold">{stallBlock.sqm || 0} sqm</span>
                                </div>
                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Tariff:</span> <span className="text-dark fw-semibold">₹{formatIndianNumber(stallBlock.totalAmount || 0)}</span>
                                </div>
                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Discount:</span> <span className="text-danger fw-semibold">₹{
                                    (() => {
                                      if (!isCurrentlyEditing) return formatIndianNumber(stallBlock.discount || 0);
                                      const activeAddonsForDisc = editAddons.map(a => ({ amount: Number(a.amount) || 0, gst: a.hasGst ? Math.round((Number(a.amount) || 0) * 0.18) : 0 }));
                                      const addBaseDisc = activeAddonsForDisc.reduce((s, a) => s + a.amount, 0);
                                      const addGstDisc = activeAddonsForDisc.reduce((s, a) => s + a.gst, 0);
                                      const stBaseDisc = Number(editAmount) || 0;
                                      const stGstDisc = Math.round(stBaseDisc * 0.18);
                                      const currentEditNetTotal = stBaseDisc + stGstDisc + addBaseDisc + addGstDisc;
                                      return formatIndianNumber(Math.max(0, (stallBlock.totalAmount || 0) - currentEditNetTotal));
                                    })()
                                  }</span>
                                </div>
                                <div className="col-md-3 d-flex align-items-center gap-1">
                                  <span className="text-secondary-light fw-bold">Requested Base Rate:</span>
                                  {isCurrentlyEditing ? (
                                    <input
                                      type="number"
                                      className="form-control px-8 py-2 text-xs"
                                      style={{ width: "90px", height: "24px" }}
                                      value={editAmount}
                                      onChange={(e) => setEditAmount(e.target.value)}
                                    />
                                  ) : (
                                    <span className="text-success fw-bold">₹{formatIndianNumber(
                                      (() => {
                                        const currentAddons = (stallBlock.addons && stallBlock.addons.length > 0)
                                          ? stallBlock.addons
                                          : (selectedBlockItem.addons || []);
                                        const addonBase = currentAddons.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
                                        const addonGst = currentAddons.reduce((sum, a) => sum + (Number(a.gst) !== undefined ? Number(a.gst) : Math.round((Number(a.amount) || 0) * 0.18)), 0);
                                        const addonTotal = addonBase + addonGst;
                                        const overallTotal = Number(stallBlock.amount) || 0;
                                        const stallTotalWithGst = Math.max(0, overallTotal - addonTotal);
                                        return stallTotalWithGst > 0 ? Math.round(stallTotalWithGst / 1.18) : 0;
                                      })()
                                    )}</span>
                                  )}
                                </div>
                                <div className="col-md-3">
                                  <span className="text-secondary-light fw-bold">Blocked Date:</span> <span className="text-dark">{stallBlock.blockedDate ? formatDate(stallBlock.blockedDate) : (selectedBlockItem.createdAt ? formatDate(selectedBlockItem.createdAt) : "—")}</span>
                                </div>
                                {stallBlock.bookedTime && (
                                  <div className="col-md-3">
                                    <span className="text-secondary-light fw-bold">Booked Time:</span> <span className="text-dark">{formatDateTime(stallBlock.bookedTime)}</span>
                                  </div>
                                )}

                                {/* Financial Summary Box */}
                                {(() => {
                                  const baseRate = selectedExpo?.sqmRate || 0;
                                  const rateWithGst = Math.round(baseRate * 1.18);

                                  const activeAddons = isCurrentlyEditing
                                    ? editAddons.map(a => {
                                      const amt = Number(a.amount) || 0;
                                      const gst = a.hasGst ? Math.round(amt * 0.18) : 0;
                                      return { title: a.title || '', amount: amt, gst: gst, total: amt + gst };
                                    })
                                    : ((stallBlock.addons && stallBlock.addons.length > 0) ? stallBlock.addons : (selectedBlockItem.addons || []));

                                  const addonBaseTotal = activeAddons.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
                                  const addonGstTotal = activeAddons.reduce((sum, a) => sum + (Number(a.gst) !== undefined ? Number(a.gst) : Math.round((Number(a.amount) || 0) * 0.18)), 0);
                                  const addonTotalWithGst = addonBaseTotal + addonGstTotal;

                                  const hasAddons = activeAddons.length > 0;

                                  let stallBaseAmount = 0;
                                  let stallGst = 0;
                                  let stallTotalWithGst = 0;

                                  if (isCurrentlyEditing) {
                                    stallBaseAmount = Number(editAmount) || 0;
                                    stallGst = Math.round(stallBaseAmount * 0.18);
                                    stallTotalWithGst = stallBaseAmount + stallGst;
                                  } else {
                                    const overallTotal = Number(stallBlock.amount) || 0;
                                    stallTotalWithGst = Math.max(0, overallTotal - addonTotalWithGst);
                                    stallBaseAmount = stallTotalWithGst > 0 ? Math.round(stallTotalWithGst / 1.18) : 0;
                                    stallGst = Math.max(0, stallTotalWithGst - stallBaseAmount);
                                  }

                                  const sqmSize = Number(stallBlock.sqm) || 0;
                                  const managerRatePerSqm = sqmSize > 0 ? Math.round(stallBaseAmount / sqmSize) : 0;

                                  const grossAmount = stallBaseAmount + addonBaseTotal;
                                  const totalGst = stallGst + addonGstTotal;
                                  const finalTotalAmount = grossAmount + totalGst;

                                  return (
                                    <div className="col-12 mt-16">
                                      <div className="p-12 radius-8 d-flex flex-column gap-3" style={{ backgroundColor: "#f8fafc", border: "1px dashed #cbd5e1" }}>
                                        {/* Admin Reference Rates */}
                                        <div className="d-flex flex-wrap gap-4 align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <span className="text-secondary-light fw-bold">Admin Base Rate/sqm:</span>
                                            <span className="text-dark fw-bold">₹{formatIndianNumber(Math.round(baseRate))}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-4">
                                            <span className="text-secondary-light fw-bold">Admin Rate/sqm (+18% GST):</span>
                                            <span className="text-primary fw-bold">₹{formatIndianNumber(rateWithGst)}</span>
                                          </div>
                                        </div>

                                        {/* Stall Financial Breakdown */}
                                        <div className="pt-8 border-top d-flex flex-wrap gap-4 align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <span className="text-secondary-light fw-bold">Manager Rate/sqm:</span>
                                            <span className="text-dark fw-bold">₹{formatIndianNumber(managerRatePerSqm)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-2">
                                            <span className="text-secondary-light fw-bold">Stall Base:</span>
                                            <span className="text-dark fw-bold">₹{formatIndianNumber(stallBaseAmount)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-2">
                                            <span className="text-secondary-light fw-bold">Stall GST (18%):</span>
                                            <span className="text-warning-700 fw-bold">₹{formatIndianNumber(stallGst)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-2">
                                            <span className="text-secondary-light fw-bold">Stall Total:</span>
                                            <span className="text-primary fw-bold">₹{formatIndianNumber(stallTotalWithGst)}</span>
                                          </div>
                                        </div>

                                        {/* Add-ons Financial Breakdown (if present) */}
                                        {hasAddons && (
                                          <div className="pt-6 border-top d-flex flex-wrap gap-4 align-items-center">
                                            <div className="d-flex align-items-center gap-2">
                                              <span className="text-secondary-light fw-bold">Add-ons Base:</span>
                                              <span className="text-dark fw-bold">₹{formatIndianNumber(addonBaseTotal)}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2 ms-md-2">
                                              <span className="text-secondary-light fw-bold">Add-ons GST (18%):</span>
                                              <span className="text-warning-700 fw-bold">₹{formatIndianNumber(addonGstTotal)}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2 ms-md-2">
                                              <span className="text-secondary-light fw-bold">Add-ons Total:</span>
                                              <span className="text-info-700 fw-bold">₹{formatIndianNumber(addonTotalWithGst)}</span>
                                            </div>
                                          </div>
                                        )}

                                        {/* Grand Overall Totals */}
                                        <div className="pt-8 border-top d-flex flex-wrap gap-4 align-items-center bg-white p-8 radius-6">
                                          <div className="d-flex align-items-center gap-2">
                                            <span className="text-secondary-light fw-bold">Gross Amount (Base Total):</span>
                                            <span className="text-dark fw-bold">₹{formatIndianNumber(grossAmount)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-3">
                                            <span className="text-secondary-light fw-bold">Total GST (18%):</span>
                                            <span className="text-warning-700 fw-bold">₹{formatIndianNumber(totalGst)}</span>
                                          </div>
                                          <div className="d-flex align-items-center gap-2 ms-md-3">
                                            <span className="text-secondary-light fw-bold">Total Amount:</span>
                                            <span className="text-success-600 fw-bold fs-6">₹{formatIndianNumber(finalTotalAmount)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* Add-ons View Mode Table */}
                                {!isCurrentlyEditing && (() => {
                                  const currentAddons = (stallBlock.addons && stallBlock.addons.length > 0)
                                    ? stallBlock.addons
                                    : (selectedBlockItem.addons || []);

                                  if (!currentAddons || currentAddons.length === 0) return null;

                                  const addonBaseSum = currentAddons.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
                                  const addonGstSum = currentAddons.reduce((sum, a) => sum + (Number(a.gst) !== undefined ? Number(a.gst) : Math.round((Number(a.amount) || 0) * 0.18)), 0);
                                  const addonTotalSum = currentAddons.reduce((sum, a) => sum + (Number(a.total) !== undefined ? Number(a.total) : Math.round((Number(a.amount) || 0) * 1.18)), 0);

                                  return (
                                    <div className="col-12 mt-12 pt-12 border-top">
                                      <div className="d-flex justify-content-between align-items-center mb-8">
                                        <span className="text-secondary-light fw-bold text-xs uppercase tracking-wider">
                                          <Icon icon="solar:box-minimalistic-bold" className="me-1 text-primary-600" />
                                          Add-ons ({currentAddons.length})
                                        </span>
                                      </div>
                                      <div className="table-responsive radius-8 border" style={{ backgroundColor: "#ffffff" }}>
                                        <table className="table table-sm text-xs mb-0">
                                          <thead className="bg-light">
                                            <tr>
                                              <th className="py-6 px-12 text-secondary-light fw-semibold">Add-on Item</th>
                                              <th className="py-6 px-12 text-end text-secondary-light fw-semibold">Base Price</th>
                                              <th className="py-6 px-12 text-end text-secondary-light fw-semibold">GST (18%)</th>
                                              <th className="py-6 px-12 text-end text-secondary-light fw-semibold">Total (with GST)</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {currentAddons.map((item, aIdx) => {
                                              const base = Number(item.amount) || 0;
                                              const gst = Number(item.gst) !== undefined ? Number(item.gst) : Math.round(base * 0.18);
                                              const tot = Number(item.total) !== undefined ? Number(item.total) : (base + gst);
                                              return (
                                                <tr key={aIdx}>
                                                  <td className="py-6 px-12 text-dark fw-medium">{item.title || "Add-on Item"}</td>
                                                  <td className="py-6 px-12 text-end text-dark">₹{formatIndianNumber(base)}</td>
                                                  <td className="py-6 px-12 text-end text-warning-700">₹{formatIndianNumber(gst)}</td>
                                                  <td className="py-6 px-12 text-end text-success-600 fw-bold">₹{formatIndianNumber(tot)}</td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                          <tfoot className="bg-light border-top">
                                            <tr className="fw-bold">
                                              <td className="py-6 px-12 text-dark">Add-ons Subtotal</td>
                                              <td className="py-6 px-12 text-end text-dark">₹{formatIndianNumber(addonBaseSum)}</td>
                                              <td className="py-6 px-12 text-end text-warning-700">₹{formatIndianNumber(Math.round(addonGstSum))}</td>
                                              <td className="py-6 px-12 text-end text-success-600">₹{formatIndianNumber(Math.round(addonTotalSum))}</td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* Add-ons Edit Mode Management */}
                                {isCurrentlyEditing && (
                                  <div className="col-12 mt-12 pt-12 border-top">
                                    <div className="d-flex justify-content-between align-items-center mb-8">
                                      <span className="text-secondary-light fw-bold text-xs uppercase tracking-wider">
                                        <Icon icon="solar:box-minimalistic-bold" className="me-1 text-primary-600" />
                                        Manage Add-ons
                                      </span>
                                      <button
                                        type="button"
                                        className="btn btn-xs btn-outline-primary radius-4 d-flex align-items-center gap-1 py-1 px-8 text-xs fw-bold"
                                        onClick={() => {
                                          setEditAddons(prev => [...prev, { title: "", amount: "", hasGst: false }]);
                                        }}
                                      >
                                        <Icon icon="solar:add-circle-bold" /> Add Add-on
                                      </button>
                                    </div>

                                    {editAddons.length === 0 ? (
                                      <div className="text-xs text-muted fst-italic p-8 bg-white radius-6 border text-center">
                                        No Add-ons added yet. Click "+ Add Add-on" to include custom charges.
                                      </div>
                                    ) : (
                                      <div className="d-flex flex-column gap-2">
                                        {editAddons.map((addon, aIdx) => {
                                          const itemAmount = Number(addon.amount) || 0;
                                          const itemGst = addon.hasGst ? Math.round(itemAmount * 0.18) : 0;
                                          const itemTotal = itemAmount + itemGst;

                                          return (
                                            <div key={aIdx} className="p-8 radius-6 border bg-white d-flex flex-column gap-2">
                                              <div className="d-flex align-items-center gap-2">
                                                <input
                                                  type="text"
                                                  className="form-control text-xs py-2 px-8"
                                                  placeholder="Add-on Title (e.g. Registration Fees)"
                                                  value={addon.title}
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    setEditAddons(prev => prev.map((item, idx) => idx === aIdx ? { ...item, title: val } : item));
                                                  }}
                                                />
                                                <div className="input-group input-group-sm" style={{ width: "130px" }}>
                                                  <span className="input-group-text px-6 text-xs">₹</span>
                                                  <input
                                                    type="number"
                                                    className="form-control text-xs py-2 px-6"
                                                    placeholder="Price"
                                                    value={addon.amount}
                                                    onChange={(e) => {
                                                      const val = e.target.value;
                                                      setEditAddons(prev => prev.map((item, idx) => idx === aIdx ? { ...item, amount: val } : item));
                                                    }}
                                                  />
                                                </div>
                                                <button
                                                  type="button"
                                                  className="btn btn-sm btn-outline-danger p-4 d-flex align-items-center justify-content-center radius-4"
                                                  style={{ width: "28px", height: "28px" }}
                                                  onClick={() => {
                                                    setEditAddons(prev => prev.filter((_, idx) => idx !== aIdx));
                                                  }}
                                                  title="Remove Add-on"
                                                >
                                                  <Icon icon="solar:trash-bin-trash-bold" />
                                                </button>
                                              </div>

                                              <div className="d-flex align-items-center justify-content-between text-xs ps-4">
                                                <div className="form-check d-flex align-items-center gap-2 m-0 p-0">
                                                  <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0"
                                                    id={`edit-addon-gst-${aIdx}`}
                                                    checked={!!addon.hasGst}
                                                    onChange={(e) => {
                                                      const checked = e.target.checked;
                                                      setEditAddons(prev => prev.map((item, idx) => idx === aIdx ? { ...item, hasGst: checked } : item));
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                  />
                                                  <label htmlFor={`edit-addon-gst-${aIdx}`} className="form-check-label text-secondary-light fw-semibold" style={{ cursor: "pointer" }}>
                                                    {addon.hasGst ? "GST Applied (18%)" : "Apply GST (18%)"}
                                                  </label>
                                                </div>

                                                {itemAmount > 0 && (
                                                  <div className="text-secondary-light">
                                                    <span>GST: <strong className={addon.hasGst ? "text-warning-700" : "text-dark"}>₹{formatIndianNumber(itemGst)}</strong></span>
                                                    <span className="ms-3">Total: <strong className="text-success-600">₹{formatIndianNumber(itemTotal)}</strong></span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {isCurrentlyEditing ? (
                                  <div className="col-12 mt-8 pt-8 border-top">
                                    <span className="text-secondary-light fw-bold d-block mb-4">Remarks:</span>
                                    <textarea
                                      className="form-control text-xs"
                                      rows="2"
                                      placeholder="Enter remarks..."
                                      value={editRemarks}
                                      onChange={(e) => setEditRemarks(e.target.value)}
                                    ></textarea>
                                  </div>
                                ) : (
                                  stallBlock.remarks && (
                                    <div className="col-12 mt-8 pt-8 border-top">
                                      <span className="text-secondary-light fw-bold">Remarks:</span> <span className="text-dark fw-medium">{stallBlock.remarks}</span>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                  {selectedBlockItem.products && selectedBlockItem.products.length > 0 && (
                    <div className="col-md-6 mt-12">
                      <div className="text-xs text-secondary-light fw-bold mb-8">Products</div>
                      <div className="d-flex flex-wrap gap-2">
                        {selectedBlockItem.products.map((p, pi) => (
                          <span key={pi} className="badge bg-primary-100 text-primary-800 px-8 py-4 radius-4 text-xs">
                            {typeof p === "object" ? (p.productName || p.name || p._id) : p}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedBlockItem.interestedLocations && selectedBlockItem.interestedLocations.length > 0 && (
                    <div className="col-md-6 mt-12">
                      <div className="text-xs text-secondary-light fw-bold mb-8">Interested Locations</div>
                      <div className="d-flex flex-wrap gap-2">
                        {selectedBlockItem.interestedLocations.map((l, li) => (
                          <span key={li} className="badge bg-info-100 text-info-800 px-8 py-4 radius-4 text-xs">
                            {typeof l === "object" ? (l.name || l.locationName || l._id) : l}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Payment Details Section */}
                  {(() => {
                    const hasPendingPayments = selectedBlockPayments && selectedBlockPayments.some(p => p.paymentStatus === 'Pending');

                    const getUniqueStallAmount = () => {
                      const bDetails = (selectedBlockItem.statusDetails || []).filter(d =>
                        d.status === 'Blocked' || d.status === 'Block' || d.status === 'Booked'
                      );
                      const uStalls = {};
                      let hasStall = false;
                      bDetails.forEach(d => {
                        if (d.stallNo) {
                          hasStall = true;
                          if (!uStalls[d.stallNo]) uStalls[d.stallNo] = d.amount || 0;
                          else uStalls[d.stallNo] = d.amount || uStalls[d.stallNo];
                        }
                      });
                      if (hasStall) {
                        return Object.values(uStalls).reduce((sum, val) => sum + Number(val || 0), 0);
                      }
                      const lastBooked = [...(selectedBlockItem.statusDetails || [])].reverse().find(d => d.status === 'Booked' || d.status === 'Blocked' || d.status === 'Block');
                      return lastBooked ? Number(lastBooked.amount || 0) : 0;
                    };
                    const totalPaymentAmount = getUniqueStallAmount();
                    const paidAmount = (selectedBlockPayments || [])
                      .filter(p => p.paymentStatus === 'Completed')
                      .reduce((sum, p) => sum + (Number(p.amountPaid) || 0), 0);
                    const balancePendingAmount = totalPaymentAmount - paidAmount;

                    return (
                      <div className="col-12 mt-16">
                        <h6 className="text-xs fw-bold text-secondary-light uppercase mb-12 border-bottom pb-8">Stall Payment History</h6>

                        <div className="d-flex align-items-center justify-content-between mb-16 bg-light p-12 radius-8 border">
                          <div className="text-center w-100 border-end">
                            <div className="text-xs text-secondary-light fw-bold mb-4">Total Amount</div>
                            <div className="text-sm fw-bold text-dark">₹{totalPaymentAmount.toLocaleString('en-IN')}</div>
                          </div>
                          <div className="text-center w-100 border-end">
                            <div className="text-xs text-secondary-light fw-bold mb-4">Paid Amount</div>
                            <div className="text-sm fw-bold text-success">₹{paidAmount.toLocaleString('en-IN')}</div>
                          </div>
                          <div className="text-center w-100">
                            <div className="text-xs text-secondary-light fw-bold mb-4">Balance Pending</div>
                            <div className="text-sm fw-bold text-danger">₹{balancePendingAmount.toLocaleString('en-IN')}</div>
                          </div>
                        </div>

                        <div className="table-responsive shadow-sm radius-8 border scroll-sm" style={{ maxHeight: "200px", overflowY: "auto" }}>
                          <table className="table table-sm mb-0">
                            <thead className="bg-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                              <tr>
                                <th className="text-xs fw-bold px-12 py-8">S.No</th>
                                <th className="text-xs fw-bold px-12 py-8">Date</th>
                                <th className="text-xs fw-bold px-12 py-8">Payment Method</th>
                                <th className="text-xs fw-bold px-12 py-8">Transaction ID / Proof</th>
                                <th className="text-xs fw-bold px-12 py-8 text-end">Amount Paid</th>
                                <th className="text-xs fw-bold px-12 py-8" style={{ minWidth: "150px" }}>Remarks</th>
                                <th className="text-xs fw-bold px-12 py-8 text-center">Status</th>
                                {hasPendingPayments && <th className="text-xs fw-bold px-12 py-8 text-center" style={{ width: "160px" }}>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {selectedBlockPayments && selectedBlockPayments.length > 0 ? (
                                selectedBlockPayments.map((p, idx) => (
                                  <tr key={idx}>
                                    <td className="text-xs px-12 py-8">{idx + 1}</td>
                                    <td className="text-xs px-12 py-8">{formatDate(p.createdAt)}</td>
                                    <td className="text-xs px-12 py-8 fw-semibold">{p.paymentMethod}</td>
                                    <td className="text-xs px-12 py-8">
                                      {p.paymentMethod === 'Cheque' ? (
                                        <div className="d-flex flex-column" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                          <span><strong>Cheque No:</strong> {p.transactionId || "—"}</span>
                                          <span><strong>Bank:</strong> {p.bankName || "—"}</span>
                                          <span><strong>Branch:</strong> {p.branchName || "—"}</span>
                                          {p.proofUrl && (
                                            <a href={getImageUrl(p.proofUrl)} target="_blank" rel="noreferrer" className="text-primary fw-bold text-decoration-underline mt-4 d-inline-block">
                                              View Cheque Copy
                                            </a>
                                          )}
                                        </div>
                                      ) : p.proofUrl ? (
                                        <a href={getImageUrl(p.proofUrl)} target="_blank" rel="noreferrer" className="text-primary fw-bold text-decoration-underline text-sm">
                                          View Proof
                                        </a>
                                      ) : (
                                        p.transactionId || "—"
                                      )}
                                    </td>
                                    <td className="text-xs px-12 py-8 text-end fw-bold text-success-600">₹{formatIndianNumber(p.amountPaid)}</td>
                                    <td className="text-xs px-12 py-8" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{p.remarks || "—"}</td>
                                    <td className="text-xs px-12 py-8 text-center">
                                      <span className={`badge px-8 py-2 radius-4 text-xs ${p.paymentStatus === 'Completed' ? 'bg-success-100 text-success-800' :
                                        p.paymentStatus === 'Pending' ? 'bg-warning-100 text-warning-800' : 'bg-danger-100 text-danger-800'
                                        }`}>
                                        {p.paymentStatus}
                                      </span>
                                    </td>
                                    {hasPendingPayments && (
                                      <td className="text-xs px-12 py-8 text-center">
                                        {p.paymentStatus === 'Pending' ? (
                                          <div className="d-flex align-items-center justify-content-center gap-2">
                                            <button
                                              type="button"
                                              className="btn btn-xs btn-success radius-4 py-4 px-8 text-xs fw-bold border-0 d-flex align-items-center gap-1"
                                              style={{ backgroundColor: "#10b981", color: "#fff" }}
                                              onClick={async () => {
                                                const verifyRes = await verifyPaymentStatus(p._id, 'Completed');
                                                if (verifyRes.status) {
                                                  const res = await getClientFollowupPayments(selectedBlockItem._id);
                                                  if (res.status) {
                                                    setSelectedBlockPayments(res.response?.data || res.response || []);
                                                  }
                                                  fetchClientFollowupsList(
                                                    selectedExpo?._id,
                                                    clientFollowupStatus,
                                                    clientFollowupsPage,
                                                    clientFollowupsLimit,
                                                    clientSearchInput,
                                                    selectedManagerFilter,
                                                    selectedGroupFilter
                                                  );
                                                }
                                              }}
                                            >
                                              Approve
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-xs btn-danger radius-4 py-4 px-8 text-xs fw-bold border-0 d-flex align-items-center gap-1"
                                              style={{ backgroundColor: "#ef4444", color: "#fff" }}
                                              onClick={async () => {
                                                const verifyRes = await verifyPaymentStatus(p._id, 'Failed');
                                                if (verifyRes.status) {
                                                  const res = await getClientFollowupPayments(selectedBlockItem._id);
                                                  if (res.status) {
                                                    setSelectedBlockPayments(res.response?.data || res.response || []);
                                                  }
                                                  fetchClientFollowupsList(
                                                    selectedExpo?._id,
                                                    clientFollowupStatus,
                                                    clientFollowupsPage,
                                                    clientFollowupsLimit,
                                                    clientSearchInput,
                                                    selectedManagerFilter,
                                                    selectedGroupFilter
                                                  );
                                                }
                                              }}
                                            >
                                              Reject
                                            </button>
                                          </div>
                                        ) : (
                                          <span className="text-xs text-secondary-light fw-semibold">—</span>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={hasPendingPayments ? "8" : "7"} className="text-center text-xs py-16 text-secondary-light">
                                    No payment transaction history logged yet.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Collect Payment Form Section */}
                  {selectedBlockItem.approvalStatus === "Approved" && (
                    <div className="col-12 mt-16">
                      <div className="p-16 border radius-12 bg-light shadow-sm">
                        <h6 className="text-xs fw-bold text-secondary-light uppercase mb-12 border-bottom pb-8 d-flex align-items-center gap-2">
                          <Icon icon="solar:wallet-money-bold" className="text-primary-600 text-lg" />
                          Collect Payment (Admin Direct Entry)
                        </h6>
                        <form onSubmit={handleAdminPaymentSubmit} className="row g-3 text-xs">
                          <div className="col-md-3">
                            <label className="form-label fw-bold text-secondary-light mb-4">Payment Method *</label>
                            <select
                              className="form-select form-select-sm radius-6"
                              value={adminPaymentMethod}
                              onChange={(e) => setAdminPaymentMethod(e.target.value)}
                              required
                            >
                              <option value="">Select Method</option>
                              <option value="UPI">UPI</option>
                              <option value="Cash">Cash</option>
                              <option value="Bank Transfer">Bank Transfer</option>
                              <option value="Net Banking">Net Banking</option>
                              <option value="Cheque">Cheque</option>
                            </select>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-bold text-secondary-light mb-4">Amount *</label>
                            <input
                              type="number"
                              className="form-control form-control-sm radius-6"
                              placeholder="Enter amount"
                              value={adminPaymentAmount}
                              onChange={(e) => setAdminPaymentAmount(e.target.value)}
                              required
                              min="1"
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-bold text-secondary-light mb-4">
                              Payment Proof {adminPaymentMethod === "UPI" && "*"}
                            </label>
                            <input
                              id="adminPaymentProofInput"
                              type="file"
                              className="form-control form-control-sm radius-6"
                              accept="image/*"
                              onChange={(e) => setAdminPaymentProofFile(e.target.files[0])}
                              required={adminPaymentMethod === "UPI"}
                              disabled={adminPaymentMethod === ""}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-bold text-secondary-light mb-4">
                              Remarks {adminPaymentMethod === "Cash" && "*"}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm radius-6"
                              placeholder="Remarks"
                              value={adminPaymentRemarks}
                              onChange={(e) => setAdminPaymentRemarks(e.target.value)}
                              required={adminPaymentMethod === "Cash"}
                            />
                          </div>
                          {adminPaymentMethod === "Cheque" && (
                            <>
                              <div className="col-md-3">
                                <label className="form-label fw-bold text-secondary-light mb-4">Cheque No / Transaction ID *</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm radius-6"
                                  placeholder="Enter Cheque No"
                                  value={adminPaymentTransactionId}
                                  onChange={(e) => setAdminPaymentTransactionId(e.target.value)}
                                  required
                                />
                              </div>
                              <div className="col-md-3">
                                <label className="form-label fw-bold text-secondary-light mb-4">Bank Name *</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm radius-6"
                                  placeholder="Enter Bank Name"
                                  value={adminPaymentBankName}
                                  onChange={(e) => setAdminPaymentBankName(e.target.value)}
                                  required
                                />
                              </div>
                              <div className="col-md-3">
                                <label className="form-label fw-bold text-secondary-light mb-4">Branch Name *</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm radius-6"
                                  placeholder="Enter Branch Name"
                                  value={adminPaymentBranchName}
                                  onChange={(e) => setAdminPaymentBranchName(e.target.value)}
                                  required
                                />
                              </div>
                            </>
                          )}
                          <div className="col-12 text-end">
                            <button
                              type="submit"
                              className="btn btn-sm text-white fw-bold px-16 radius-6"
                              style={{ backgroundColor: "#16a34a" }}
                              disabled={adminPaymentSubmitting}
                            >
                              {adminPaymentSubmitting ? "Submitting..." : "Submit Payment"}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16 d-flex justify-content-between align-items-center">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24"
                  onClick={() => { setShowBlockDetailModal(false); setSelectedBlockItem(null); setSelectedBlockPayments([]); }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const customStyles = `
    .glassmorphic-action-pill {
      display: inline-flex;
      gap: 46px;
      transition: all 0.3s ease;
    }

    .glass-action-btn {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      text-decoration: none;
      cursor: pointer;
    }
    .glass-action-btn:hover {
      transform: translateY(-2px) scale(1.08);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
    .glass-btn-view {
      background-color: #eef2ff;
      color: #4f46e5;
      border: 1px solid #e0e7ff;
    }
    .glass-btn-view:hover {
      background-color: #4f46e5;
      color: #ffffff;
      border-color: #4f46e5;
    }
    .glass-btn-layout {
      background-color: #fff5f5;
      color: #e03131;
      border: 1px solid #ffe3e3;
    }
    .glass-btn-layout:hover {
      background-color: #e03131;
      color: #ffffff;
      border-color: #e03131;
    }
    .glass-btn-edit {
      background-color: #fff9db;
      color: #f08c00;
      border: 1px solid #fff3bf;
    }
    .glass-btn-edit:hover {
      background-color: #f08c00;
      color: #ffffff;
      border-color: #f08c00;
    }
    .glass-btn-delete {
      background-color: #ffd6d6;
      color: #ed1c24;
      border: 1px solid #ffc9c9;
    }
    .glass-btn-delete:hover {
      background-color: #ed1c24;
      color: #ffffff;
      border-color: #ed1c24;
    }

    .clickable-stat-card {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    }
    .clickable-stat-card:hover {
      transform: translateY(-4px) scale(1.04) !important;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08) !important;
      border: 1px solid rgba(0, 0, 0, 0.05) !important;
    }
    .stat-card-assigns {
      background-color: #f5f3ff !important;
      color: #7c3aed !important;
    }
    .stat-card-enquires {
      background-color: #fffbeb !important;
      color: #d97706 !important;
    }
    .stat-card-today-followups {
      background-color: #f0f9ff !important;
      color: #0284c7 !important;
    }
    .stat-card-prospect {
      background-color: #fdf2f8 !important;
      color: #db2777 !important;
    }
    .stat-card-not-interested {
      background-color: #fef2f2 !important;
      color: #dc2626 !important;
    }
    .stat-card-rnr {
      background-color: #f1f5f9 !important;
      color: #475569 !important;
    }
    .stat-card-block-stall {
      background-color: #f0fdf4 !important;
      color: #16a34a !important;
    }
    .stat-card-rejected {
      background-color: #fef2f2 !important;
      color: #b91c1c !important;
    }
  `;

  return (
    <>
      <style>{customStyles}</style>
      {activeView === "grid" ? (
        <div className="p-24">
          <div className="d-flex align-items-center justify-content-between mb-24 flex-wrap gap-3">
            <h5 className="mb-0 fw-bold">Current Expos</h5>
            <div className="d-flex align-items-center gap-3">
              <form
                className="navbar-search position-relative"
                onSubmit={(e) => e.preventDefault()}
              >
                <input
                  type="text"
                  className="bg-white h-40-px w-auto radius-8 shadow-sm border-0"
                  placeholder="Search expos..."
                  value={searchTerm}
                  onChange={(e) => handleMainSearch(e.target.value)}
                  style={{ paddingLeft: "40px" }}
                />
                <Icon
                  icon="ion:search-outline"
                  className="position-absolute text-secondary-light text-xl"
                  style={{
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              </form>
              {hasPermission("present_expo", "add") ||
                hasPermission("upcoming_expo", "add") ? (
                <Link
                  to="/upcoming-expo/add"
                  state={{ from: "/present-expo" }}
                  className="btn text-sm btn-sm px-16 radius-8 d-flex align-items-center gap-2 h-40-px shadow-sm"
                  style={{
                    backgroundColor: "#f7e7da",
                    color: "black",
                    fontWeight: "600",
                  }}
                >
                  <Icon icon="ic:baseline-plus" className="icon text-xl" />
                  Add Expo
                </Link>
              ) : null}
            </div>
          </div>
          <div className="row gy-4">
            {filteredExpos.length > 0 ? (
              filteredExpos.map((expo) => (
                <div className="col-xxl-4 col-md-6" key={expo._id || expo.id}>
                  <div className="card h-100 radius-16 border-0 shadow-sm overflow-hidden transition-all hover-shadow-md bg-white">
                    {/* Brand Red Gradient Header */}
                    <div
                      className="p-20"
                      style={{
                        background:
                          "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                      }}
                    >
                      <div className="d-flex align-items-start justify-content-between gap-3 mb-8">
                        <p
                          className="text-white mb-0 fw-bold letter-spacing-05 text-sm"
                          style={{
                            flex: "1 1 auto",
                            minWidth: "0",
                            wordBreak: "break-word",
                            minHeight: "40px",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            lineHeight: "1.4",
                          }}
                        >
                          {typeof (expo.expoName || expo.name) === "object"
                            ? (expo.expoName || expo.name)?.name
                            : expo.expoName || expo.name}
                        </p>
                        <div className="flex-shrink-0 d-flex justify-content-end align-items-center gap-2">
                          <span
                            className={`badge ${expo.isActive !== false ? "bg-success" : "bg-warning"} text-white px-8 radius-4 text-xs fw-bold shadow-sm`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              height: "24px",
                            }}
                          >
                            {expo.isActive !== false ? "Active" : "Inactive"}
                          </span>
                          {(() => {
                            const managers = Array.isArray(expo.eventManager)
                              ? expo.eventManager
                                .map((m) => m.name)
                                .filter(Boolean)
                              : expo.eventManager?.name
                                ? [expo.eventManager.name]
                                : [];

                            if (managers.length === 0) {
                              return (
                                <div
                                  className="bg-white-10 px-8 py-4 radius-8 backdrop-blur d-flex align-items-center gap-2"
                                  style={{
                                    border: "1px solid rgba(255,255,255,0.2)",
                                  }}
                                >
                                  <Icon
                                    icon="solar:user-circle-bold"
                                    className="text-white text-xs"
                                  />
                                  <span
                                    className="text-white text-xs fw-bold text-uppercase"
                                    style={{ fontSize: "10px" }}
                                  >
                                    No Manager
                                  </span>
                                </div>
                              );
                            }

                            if (managers.length === 1) {
                              return (
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="position-relative"
                                    onMouseEnter={() =>
                                      setHoveredExpoId(expo._id || expo.id)
                                    }
                                    onMouseLeave={() => setHoveredExpoId(null)}
                                  >
                                    <div
                                      className="bg-white-10 px-8 py-4 radius-8 backdrop-blur d-flex align-items-center gap-2 cursor-pointer transition-all hover-scale-105"
                                      style={{
                                        border:
                                          "1px solid rgba(255,255,255,0.2)",
                                        maxWidth: "120px",
                                      }}
                                    >
                                      <Icon
                                        icon="solar:user-circle-bold"
                                        className="text-white text-xs flex-shrink-0"
                                      />
                                      <span
                                        className="text-white text-xs fw-bold text-uppercase text-truncate"
                                        style={{ fontSize: "10px" }}
                                      >
                                        {managers[0]}
                                      </span>
                                    </div>
                                    {hoveredExpoId ===
                                      (expo._id || expo.id) && (
                                        <div
                                          className="position-absolute p-12 radius-8 shadow-lg z-3 text-start animate__animated animate__fadeIn"
                                          style={{
                                            top: "30px",
                                            right: "0",
                                            minWidth: "170px",
                                            background:
                                              "linear-gradient(135deg, rgba(18, 0, 2, 0.98) 0%, rgba(48, 0, 3, 0.98) 100%)",
                                            backdropFilter: "blur(12px)",
                                            border:
                                              "1px solid rgba(237, 28, 36, 0.4)",
                                            boxShadow:
                                              "0 10px 30px rgba(0, 0, 0, 0.5)",
                                          }}
                                        >
                                          {/* Glassmorphic Arrow Indicator */}
                                          <div
                                            style={{
                                              position: "absolute",
                                              top: "-6px",
                                              right: "20px",
                                              width: "0",
                                              height: "0",
                                              borderLeft: "6px solid transparent",
                                              borderRight:
                                                "6px solid transparent",
                                              borderBottom:
                                                "6px solid rgba(18, 0, 2, 0.98)",
                                            }}
                                          ></div>
                                          <div
                                            className="text-uppercase fw-bold border-bottom pb-6 mb-8"
                                            style={{
                                              color: "#ff8e94",
                                              fontSize: "9px",
                                              letterSpacing: "0.8px",
                                              borderColor:
                                                "rgba(255, 255, 255, 0.15)",
                                            }}
                                          >
                                            Managers List
                                          </div>
                                          <div className="d-flex align-items-center gap-2 py-4">
                                            <Icon
                                              icon="solar:user-bold"
                                              style={{
                                                color: "#ff3b30",
                                                fontSize: "11px",
                                              }}
                                            />
                                            <span
                                              className="fw-semibold text-uppercase text-white"
                                              style={{ fontSize: "10px" }}
                                            >
                                              {managers[0]}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="position-relative"
                                  onMouseEnter={() =>
                                    setHoveredExpoId(expo._id || expo.id)
                                  }
                                  onMouseLeave={() => setHoveredExpoId(null)}
                                >
                                  <span
                                    className="badge bg-white text-danger-600 px-8 py-6 radius-8 fw-bold cursor-pointer transition-all hover-scale-105 shadow-sm d-flex align-items-center gap-1"
                                    style={{
                                      fontSize: "10px",
                                      border: "1px solid rgba(255,255,255,0.4)",
                                      color: "#bb0007",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    <Icon
                                      icon="solar:user-circle-bold"
                                      className="text-danger text-xs"
                                    />
                                    +{managers.length} managers
                                  </span>
                                  {hoveredExpoId === (expo._id || expo.id) && (
                                    <div
                                      className="position-absolute p-12 radius-8 shadow-lg z-3 text-start animate__animated animate__fadeIn"
                                      style={{
                                        top: "30px",
                                        right: "0",
                                        minWidth: "170px",
                                        background:
                                          "linear-gradient(135deg, rgba(18, 0, 2, 0.98) 0%, rgba(48, 0, 3, 0.98) 100%)",
                                        backdropFilter: "blur(12px)",
                                        border:
                                          "1px solid rgba(237, 28, 36, 0.4)",
                                        boxShadow:
                                          "0 10px 30px rgba(0, 0, 0, 0.5)",
                                      }}
                                    >
                                      {/* Glassmorphic Arrow Indicator */}
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-6px",
                                          right: "20px",
                                          width: "0",
                                          height: "0",
                                          borderLeft: "6px solid transparent",
                                          borderRight: "6px solid transparent",
                                          borderBottom:
                                            "6px solid rgba(18, 0, 2, 0.98)",
                                        }}
                                      ></div>
                                      <div
                                        className="text-uppercase fw-bold border-bottom pb-6 mb-8"
                                        style={{
                                          color: "#ff8e94",
                                          fontSize: "9px",
                                          letterSpacing: "0.8px",
                                          borderColor:
                                            "rgba(255, 255, 255, 0.15)",
                                        }}
                                      >
                                        Managers List
                                      </div>
                                      {managers.map((name, idx) => (
                                        <div
                                          key={idx}
                                          className="d-flex align-items-center gap-2 py-4"
                                        >
                                          <Icon
                                            icon="solar:user-bold"
                                            style={{
                                              color: "#ff3b30",
                                              fontSize: "11px",
                                            }}
                                          />
                                          <span
                                            className="fw-semibold text-uppercase text-white"
                                            style={{ fontSize: "10px" }}
                                          >
                                            {name}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex align-items-center gap-2 text-white-50">
                          <Icon
                            icon="solar:map-point-bold-duotone"
                            className="text-lg text-white"
                          />
                          <span className="text-sm fw-medium text-white">
                            {typeof expo.expoLocation === "object"
                              ? expo.expoLocation?.name
                              : expo.expoLocation || expo.venue || "N/A"}
                          </span>
                          <div
                            className="position-relative d-inline-flex align-items-center"
                            onMouseEnter={() =>
                              setHoveredVenueExpoId(expo._id || expo.id)
                            }
                            onMouseLeave={() => setHoveredVenueExpoId(null)}
                          >
                            <Icon
                              icon="solar:info-circle-bold-duotone"
                              className="text-white cursor-pointer hover-scale-110 transition-all text-sm"
                              style={{ opacity: 0.7 }}
                            />
                            {hoveredVenueExpoId === (expo._id || expo.id) && (
                              <div
                                className="position-absolute p-12 radius-8 shadow-lg z-3 text-start animate__animated animate__fadeIn"
                                style={{
                                  top: "24px",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  minWidth: "220px",
                                  background:
                                    "linear-gradient(135deg, rgba(18, 0, 2, 0.98) 0%, rgba(48, 0, 3, 0.98) 100%)",
                                  backdropFilter: "blur(12px)",
                                  border: "1px solid rgba(237, 28, 36, 0.4)",
                                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
                                }}
                              >
                                {/* Arrow pointing up */}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "-6px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "0",
                                    height: "0",
                                    borderLeft: "6px solid transparent",
                                    borderRight: "6px solid transparent",
                                    borderBottom:
                                      "6px solid rgba(18, 0, 2, 0.98)",
                                  }}
                                ></div>
                                <div
                                  className="text-uppercase fw-bold border-bottom pb-6 mb-8"
                                  style={{
                                    color: "#ff8e94",
                                    fontSize: "9px",
                                    letterSpacing: "0.8px",
                                    borderColor: "rgba(255, 255, 255, 0.15)",
                                  }}
                                >
                                  Venue Details
                                </div>
                                <div className="d-flex align-items-start gap-2 py-4 text-start">
                                  <Icon
                                    icon="solar:map-point-bold"
                                    style={{
                                      color: "#ff3b30",
                                      fontSize: "14px",
                                      marginTop: "2px",
                                    }}
                                  />
                                  <span
                                    className="fw-semibold text-white"
                                    style={{
                                      fontSize: "11px",
                                      lineHeight: "1.4",
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    {expo.venue}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 text-white-50">
                          <Icon
                            icon="solar:calendar-date-bold-duotone"
                            className="text-lg text-white"
                          />
                          <span className="text-sm fw-medium text-white">
                            {expo.startDate && expo.endDate
                              ? `${formatDate(expo.startDate)} - ${formatDate(expo.endDate)}`
                              : expo.date || "Date N/A"}
                          </span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between gap-2 text-white-50 mt-1">
                          <div className="d-flex align-items-center gap-2">
                            <Icon
                              icon="solar:clock-circle-bold-duotone"
                              className="text-lg text-white"
                            />
                            <span className="text-sm fw-medium text-white">
                              {expo.startTime || "10:00 AM"} -{" "}
                              {expo.endTime || "07:00 PM"}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="badge bg-white text-dark px-8 radius-4 text-xs fw-bold shadow-sm d-inline-flex align-items-center gap-1 cursor-pointer transition-all hover-scale-105"
                              style={{
                                height: "24px",
                                border: "1px solid rgba(255,255,255,0.2)",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleView(expo);
                                setMainTab("visitor_enquiry");
                              }}
                            >
                              <Icon icon="solar:users-group-two-rounded-bold" className="text-danger-600" />
                              Visitors: {(expo.stats?.visitorEnquiry ?? 0) + (expo.stats?.visitorFollowup ?? 0) + (expo.stats?.visitorBooked ?? 0)}
                            </span>
                            {expo.entryType && (
                              <span
                                className={`badge ${expo.entryType === "Free"
                                  ? "bg-info"
                                  : expo.entryType === "Paid"
                                    ? "bg-success"
                                    : "bg-primary"
                                  } text-white px-8 radius-4 text-xs fw-bold shadow-sm d-inline-flex align-items-center`}
                                style={{
                                  height: "24px",
                                  border: "1px solid rgba(255,255,255,0.2)",
                                }}
                              >
                                {expo.entryType}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-body p-0">
                      <div className="p-20">
                        {/* 6 Custom Statistics Layout */}
                        <div className="row g-3 mb-16 border-bottom border-neutral-100 pb-16">
                          {[
                            {
                              label: "Assigns",
                              val: expo.stats?.clientAssigns ?? 0,
                              icon: "solar:user-hand-up-bold",
                              className: "stat-card-assigns",
                              onClick: () => {
                                setSelectedExpo(expo);
                                setClientFollowupStatus("Assignes");
                                setClientFollowupsPage(0);
                                setClientSearchInput("");
                                setClientFollowupSearchTerm("");
                                setSelectedManagerFilter(null);
                                setSelectedGroupFilter(null);
                                setActiveView("client-followup-list");
                              },
                            },
                            {
                              label: "Enquires",
                              val: expo.stats?.stallEnquiry ?? 0,
                              icon: "solar:chat-round-dots-bold",
                              className: "stat-card-enquires",
                              onClick: () => {
                                handleView(expo);
                                setMainTab("stall_enquiry");
                              },
                            },
                            {
                              label: "Today followups",
                              val: expo.stats?.clientTodayFollowups ?? 0,
                              icon: "solar:calendar-date-bold",
                              className: "stat-card-today-followups",
                              onClick: () => {
                                setSelectedExpo(expo);
                                setClientFollowupStatus("Todayfolloup");
                                setClientFollowupsPage(0);
                                setClientSearchInput("");
                                setClientFollowupSearchTerm("");
                                setSelectedManagerFilter(null);
                                setSelectedGroupFilter(null);
                                setActiveView("client-followup-list");
                              },
                            },
                            {
                              label: "Prospect",
                              val: expo.stats?.clientProspects ?? 0,
                              icon: "solar:hand-stars-bold",
                              className: "stat-card-prospect",
                              onClick: () => {
                                setSelectedExpo(expo);
                                setClientFollowupStatus("Prospects");
                                setClientFollowupsPage(0);
                                setClientSearchInput("");
                                setClientFollowupSearchTerm("");
                                setSelectedManagerFilter(null);
                                setSelectedGroupFilter(null);
                                setActiveView("client-followup-list");
                              },
                            },
                            {
                              label: "Not interest",
                              val: expo.stats?.clientNotInterested ?? 0,
                              icon: "solar:close-circle-bold",
                              className: "stat-card-not-interested",
                              onClick: () => {
                                setSelectedExpo(expo);
                                setClientFollowupStatus("Not interested");
                                setClientFollowupsPage(0);
                                setClientSearchInput("");
                                setClientFollowupSearchTerm("");
                                setSelectedManagerFilter(null);
                                setSelectedGroupFilter(null);
                                setActiveView("client-followup-list");
                              },
                            },
                            {
                              label: "RNR",
                              val: expo.stats?.clientRNR ?? 0,
                              icon: "solar:phone-calling-bold",
                              className: "stat-card-rnr",
                              onClick: () => {
                                setSelectedExpo(expo);
                                setClientFollowupStatus("RNR");
                                setClientFollowupsPage(0);
                                setClientSearchInput("");
                                setClientFollowupSearchTerm("");
                                setSelectedManagerFilter(null);
                                setSelectedGroupFilter(null);
                                setActiveView("client-followup-list");
                              },
                            },
                            {
                              label: "Block your stall",
                              val: expo.stats?.clientBlock ?? 0,
                              icon: "solar:lock-keyhole-bold",
                              className: "stat-card-block-stall",
                              colClass: "col-6 mt-2",
                              onClick: () => {
                                setSelectedExpo(expo);
                                setClientFollowupStatus("Blocked");
                                setClientFollowupsPage(0);
                                setClientSearchInput("");
                                setClientFollowupSearchTerm("");
                                setSelectedManagerFilter(null);
                                setSelectedGroupFilter(null);
                                setActiveView("client-followup-list");
                              },
                            },
                            {
                              label: "Rejected",
                              val: expo.stats?.clientReject ?? 0,
                              icon: "solar:shield-cross-bold",
                              className: "stat-card-rejected",
                              colClass: "col-6 mt-2",
                              onClick: () => {
                                setSelectedExpo(expo);
                                setClientFollowupStatus("Rejected");
                                setClientFollowupsPage(0);
                                setClientSearchInput("");
                                setClientFollowupSearchTerm("");
                                setSelectedManagerFilter(null);
                                setSelectedGroupFilter(null);
                                setActiveView("client-followup-list");
                              },
                            },
                          ].map((st, i) => (
                            <div className={st.colClass || "col-6"} key={i}>
                              <div
                                className={`p-12 radius-12 border-0 transition-all hover-shadow-sm cursor-pointer clickable-stat-card ${st.className}`}
                                style={{ minHeight: "82px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}
                                onClick={st.onClick}
                              >
                                <div className="d-flex align-items-start justify-content-between">
                                  <div
                                    className="w-32-px h-32-px rounded-circle bg-white d-flex align-items-center justify-content-center shadow-sm"
                                  >
                                    <Icon
                                      icon={st.icon}
                                      className="text-md"
                                      style={{ color: "inherit" }}
                                    />
                                  </div>
                                  <p
                                    className="mb-0 fw-bold"
                                    style={{ fontSize: "16px", color: "inherit" }}
                                  >
                                    {st.val}
                                  </p>
                                </div>
                                <span
                                  className="fw-bold"
                                  style={{
                                    fontSize: "11px",
                                    letterSpacing: "0.3px",
                                    color: "inherit",
                                    marginTop: "6px"
                                  }}
                                >
                                  {st.label}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Actions Row - Glassmorphic Icons */}
                        <div className="text-center w-100">
                          <div className="glassmorphic-action-pill">
                            {hasPermission("present_expo", "view") && (
                              <Link
                                to={`/upcoming-expo/view/${expo._id || expo.id}`}
                                state={{ from: "/present-expo" }}
                                className="glass-action-btn glass-btn-view"
                                title="View Expo"
                              >
                                <Icon icon="lucide:eye" className="text-lg" />
                              </Link>
                            )}
                            {hasPermission("present_expo", "view") && (
                              <Link
                                to={`/upcoming-expo/exhibition-layout/${expo._id || expo.id}`}
                                className="glass-action-btn glass-btn-layout"
                                title="Exhibition Layout"
                              >
                                <Icon
                                  icon="solar:box-bold"
                                  className="text-lg"
                                />
                              </Link>
                            )}
                            {(hasPermission("present_expo", "edit") ||
                              hasPermission("upcoming_expo", "edit")) && (
                                <Link
                                  to={`/upcoming-expo/edit/${expo._id || expo.id}`}
                                  state={{ from: "/present-expo" }}
                                  className="glass-action-btn glass-btn-edit"
                                  title="Edit Expo"
                                >
                                  <Icon icon="lucide:edit" className="text-lg" />
                                </Link>
                              )}
                            {(hasPermission("present_expo", "delete") ||
                              hasPermission("upcoming_expo", "delete")) && (
                                <button
                                  type="button"
                                  className="glass-action-btn glass-btn-delete"
                                  title="Delete Expo"
                                  onClick={() => handleDeleteClick(expo)}
                                >
                                  <Icon
                                    icon="lucide:trash-2"
                                    className="text-lg"
                                  />
                                </button>
                              )}
                          </div>
                        </div>

                        {/* <button
                          className="btn w-100 d-flex align-items-center justify-content-center gap-2 fw-bold py-12 radius-12 text-sm transition-all shadow-sm"
                          style={{
                            background:
                              "linear-gradient(135deg, #ed1c24, #bb0007)",
                            color: "white",
                          }}
                          onClick={() => {
                            handleView(expo);
                            setActiveView("detailed-booked");
                          }}
                        >
                          View Booked Dashboard
                          <Icon
                            icon="solar:alt-arrow-right-bold-duotone"
                            className="text-lg"
                          />
                        </button> */}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-80">
                <div className="bg-white p-40 radius-24 shadow-sm d-inline-block border border-neutral-100">
                  <Icon
                    icon="solar:folder-error-bold-duotone"
                    className="text-secondary-light mb-16"
                    style={{ fontSize: "64px" }}
                  />
                  <h5 className="text-dark fw-bold mb-8">
                    No Present Expos Found
                  </h5>
                  <p className="text-secondary-light mb-0">
                    There are currently no active exhibitions to display in this
                    section.
                  </p>
                </div>
              </div>
            )}
          </div>

          {loading && (
            <div className="text-center mt-32">
              <div className="spinner-border text-danger" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
        </div>
      ) : activeView === "client-followup-list" ? (
        <div className="p-24 bg-neutral-50 min-vh-100">
          <div
            className="p-24 radius-16 mb-24 position-relative overflow-hidden shadow-sm"
            style={{
              background: "linear-gradient(to right, #000000, #bb0007, #ed1c24)",
            }}
          >
            <div className="d-flex align-items-center justify-content-between position-relative z-1 flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-white w-40-px h-40-px rounded d-flex align-items-center justify-content-center shadow-sm border-0 bg-white"
                  onClick={() => setActiveView("grid")}
                >
                  <Icon
                    icon="solar:alt-arrow-left-linear"
                    className="text-xl text-dark"
                  />
                </button>
                <div>
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <h5 className="mb-0 fw-bold text-white">
                      {typeof (selectedExpo?.expoName || selectedExpo?.name) === "object"
                        ? (selectedExpo?.expoName || selectedExpo?.name)?.name
                        : selectedExpo?.expoName || selectedExpo?.name}
                    </h5>
                    <span className="badge bg-white text-danger-600 px-12 py-6 radius-8 fw-bold text-uppercase">
                      {clientFollowupStatus === "Assignes" ? "Assigns" :
                        clientFollowupStatus === "Todayfolloup" ? "Next Followups" :
                          clientFollowupStatus === "Prospects" ? "Prospects" :
                            clientFollowupStatus === "Not interested" ? "Not interested" :
                              (clientFollowupStatus === "Block" || clientFollowupStatus === "Blocked") ? "Block / Booking" : clientFollowupStatus}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-4 text-white opacity-80 text-sm">
                    <div className="d-flex align-items-center gap-2">
                      <Icon icon="solar:map-point-bold" />
                      <span>{selectedExpo?.venue || "N/A"}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Icon icon="solar:calendar-date-bold" />
                      <span>{selectedExpo?.startDate ? `${formatDate(selectedExpo.startDate)} - ${formatDate(selectedExpo.endDate)}` : ""}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card radius-16 border-0 shadow-sm mb-24 p-24 bg-white">
            <div className="row align-items-center gy-3">
              <div className="col-md-4">
                <label className="form-label fw-bold text-secondary-light small uppercase">Search Lead</label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control radius-8 bg-neutral-50 h-40-px border-neutral-200"
                    placeholder="Search by Company, Person, Mobile..."
                    value={clientSearchInput}
                    onChange={(e) => handleClientFollowupSearch(e.target.value)}
                    style={{ paddingLeft: "40px" }}
                  />
                  <Icon
                    icon="ion:search-outline"
                    className="position-absolute text-secondary-light text-xl"
                    style={{ left: "12px", top: "50%", transform: "translateY(-50%)" }}
                  />
                </div>
              </div>

              <div className="col-md-3">
                <label className="form-label fw-bold text-secondary-light small uppercase">Filter by Manager</label>
                <Select
                  options={[
                    { value: "", label: "All Managers" },
                    ...((Array.isArray(selectedExpo?.eventManager) ? selectedExpo.eventManager : (selectedExpo?.eventManager?.name ? [selectedExpo.eventManager] : []))
                      .map(m => ({ value: m._id || m.id, label: m.name })))
                  ]}
                  value={{
                    value: selectedManagerFilter || "",
                    label: selectedManagerFilter ? (
                      (Array.isArray(selectedExpo?.eventManager) ? selectedExpo.eventManager : (selectedExpo?.eventManager?.name ? [selectedExpo.eventManager] : []))
                        .find(m => (m._id || m.id) === selectedManagerFilter)?.name || "All Managers"
                    ) : "All Managers"
                  }}
                  onChange={(sel) => setSelectedManagerFilter(sel.value || null)}
                  styles={selectStyles()}
                />
              </div>

              {(clientFollowupStatus === "Assignes" ||
                clientFollowupStatus === "Todayfolloup" ||
                clientFollowupStatus === "Prospects" ||
                clientFollowupStatus === "Not interested" ||
                clientFollowupStatus === "RNR") && (
                  <div className="col-md-5 text-end align-self-end">
                    <button
                      className="btn btn-primary radius-8 px-24 h-40-px text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                      onClick={() => setShowBulkAssignModal(true)}
                      disabled={selectedAssigns.length === 0}
                    >
                      <Icon icon="solar:user-hand-up-bold" className="text-lg" />
                      Assign to another manager ({selectedAssigns.length})
                    </button>
                  </div>
                )}

              {(clientFollowupStatus === "Blocked" || clientFollowupStatus === "Block") && (
                <div className="col-md-5 text-end align-self-end">
                  <button
                    className="btn btn-danger radius-8 px-24 h-40-px text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                    style={{ backgroundColor: "#ed1c24", borderColor: "#ed1c24" }}
                    onClick={() => {
                      fetchExpoStalls("");
                      setNewEnquiryData({
                        name: "",
                        companyName: "",
                        mobileNo: "",
                        email: "",
                        category: selectedExpo?.category?._id || selectedExpo?.category || "",
                        interestedProduct: [],
                        segment: "B2B",
                        designation: "",
                        interestedLocations: [],
                        address: "",
                        city: "",
                        state: "",
                        pincode: "",
                        status: "Booked",
                        remark: "",
                        followUpDate: "",
                        stallNo: "",
                        sqft: "",
                        sqm: "",
                        totalAmount: "",
                        amountPaid: "",
                        stallType: "",
                        manualCost: "",
                        paymentMethod: "",
                        paymentStatus: "",
                        paymentScreenshot: null,
                        discount: 0,
                      });
                      setEnquiryErrors({});
                      setBookingCreatorType("Myself");
                      setBookingCreatorManagerId("");
                      setShowDirectBookingModal(true);
                    }}
                  >
                    <Icon icon="solar:calendar-add-bold" className="text-lg" />
                    Book a Stall
                  </button>
                </div>
              )}
              {clientFollowupStatus === "Todayfolloup" && (
                <div className="col-12 d-flex align-items-center gap-3 mt-3">
                  <span className="text-sm text-secondary-light fw-bold">Filter by Date:</span>
                  <button
                    className={`btn btn-sm radius-8 px-16 ${nextFollowupDateFilter === new Date().toISOString().split("T")[0] ? "btn-danger text-white" : "btn-outline-secondary"}`}
                    style={nextFollowupDateFilter === new Date().toISOString().split("T")[0] ? { backgroundColor: "#ed1c24", borderColor: "#ed1c24" } : {}}
                    onClick={() => setNextFollowupDateFilter(
                      nextFollowupDateFilter === new Date().toISOString().split("T")[0] ? "" : new Date().toISOString().split("T")[0]
                    )}
                  >
                    <Icon icon="solar:calendar-mark-bold" className="me-1" />
                    Today ({new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" })})
                  </button>
                  {nextFollowupDateFilter && (
                    <button
                      className="btn btn-sm btn-light radius-8 px-12"
                      onClick={() => setNextFollowupDateFilter("")}
                    >
                      <Icon icon="lucide:x" className="me-1" />Clear
                    </button>
                  )}
                  {nextFollowupDateFilter && (
                    <span className="text-xs text-secondary-light">Showing followups due: {new Date(nextFollowupDateFilter).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="card radius-16 border-0 shadow-sm overflow-hidden bg-white">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0" style={{ minWidth: (clientFollowupStatus === "Block" || clientFollowupStatus === "Blocked") ? "1500px" : clientFollowupStatus === "Rejected" ? "1400px" : "1200px" }}>
                  <thead style={{ background: "linear-gradient(135deg, #bb0007, #ed1c24)" }}>
                    {(clientFollowupStatus === "Block" || clientFollowupStatus === "Blocked") ? (
                      <tr>
                        <th className="py-16 px-20 text-xs fw-bold text-white uppercase border-0" style={{ width: "60px" }}>Sl.No</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "200px" }}>Company Name</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "160px" }}>Key Person</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "130px" }}>Mobile</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "100px" }}>Stall No</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "150px" }}>Manager</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "180px" }}>Blocked / Booked Date</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0 text-center" style={{ width: "130px" }}>Payment Status</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0 text-center" style={{ width: "130px" }}>Status</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0 text-end" style={{ width: "120px" }}>Actions</th>
                      </tr>
                    ) : clientFollowupStatus === "Rejected" ? (
                      <tr>
                        <th className="py-16 px-20 text-xs fw-bold text-white uppercase border-0" style={{ width: "60px" }}>Sl.No</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "200px" }}>Company Name</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "160px" }}>Key Person</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "100px" }}>Stall No</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "150px" }}>Manager</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "150px" }}>Rejected By</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "180px" }}>Reject Reason</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "130px" }}>Rejected Date</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "130px" }}>History</th>
                      </tr>
                    ) : (
                      <tr>
                        <th className="py-16 px-20 text-xs fw-bold text-white uppercase border-0" style={{ width: "80px" }}>
                          <input type="checkbox" className="form-check-input me-2"
                            checked={clientFollowupsList.length > 0 && clientFollowupsList.every(item => selectedAssigns.includes(item._id || item.id))}
                            onChange={(e) => {
                              const pageItemIds = clientFollowupsList.map(item => item._id || item.id);
                              if (e.target.checked) {
                                setSelectedAssigns(prev => Array.from(new Set([...prev, ...pageItemIds])));
                              } else {
                                setSelectedAssigns(prev => prev.filter(id => !pageItemIds.includes(id)));
                              }
                            }}
                          />
                          Sl.No
                        </th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "250px" }}>Company Name</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "220px" }}>Key Person Name</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "180px" }}>Mobile Number</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0" style={{ width: "180px" }}>Manager</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0 text-center" style={{ width: "120px" }}>Rating</th>
                        <th className="py-16 text-xs fw-bold text-white uppercase border-0 text-end" style={{ width: "120px" }}>Actions</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {clientFollowupsLoading ? (
                      <tr>
                        <td colSpan={(clientFollowupStatus === "Block" || clientFollowupStatus === "Blocked") ? "10" : clientFollowupStatus === "Rejected" ? "9" : "7"} className="text-center py-80">
                          <div className="spinner-border text-danger" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : (() => {
                      const displayList = (clientFollowupStatus === "Todayfolloup" && nextFollowupDateFilter)
                        ? clientFollowupsList.filter(item => {
                          // Find the last Todayfolloup statusDetail and check its date
                          const lastDetail = [...(item.statusDetails || [])].reverse().find(d => d.status === "Todayfolloup");
                          if (!lastDetail?.date) return false;
                          return new Date(lastDetail.date).toISOString().split("T")[0] === nextFollowupDateFilter;
                        })
                        : clientFollowupsList;
                      return displayList.length > 0 ? (
                        displayList.map((item, idx) => (
                          <tr key={item._id || idx}>
                            {(clientFollowupStatus === "Block" || clientFollowupStatus === "Blocked") ? (
                              <>
                                <td className="py-16 px-20 text-sm fw-semibold text-dark">
                                  {clientFollowupsPage * clientFollowupsLimit + idx + 1}
                                </td>
                                <td className="py-16 text-sm fw-bold text-dark">
                                  {item.companyName || item.client?.companyName || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {item.name || item.client?.keyPerson || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {item.mobileNo || item.client?.mobile1 || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark fw-bold">
                                  {(() => {
                                    if (!item.statusDetails || item.statusDetails.length === 0) {
                                      return item.stallNo || "N/A";
                                    }
                                    const blockedBookedStalls = item.statusDetails
                                      .filter(d => d.status === 'Blocked' || d.status === 'Block' || d.status === 'Booked')
                                      .map(d => d.stallNo)
                                      .filter(Boolean);
                                    return blockedBookedStalls.length > 0
                                      ? Array.from(new Set(blockedBookedStalls)).join(", ")
                                      : (item.stallNo || "N/A");
                                  })()}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {item.manager?.name || item.managerId?.name || item.managerName || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {item.status === 'Booked' ? (
                                    item.bookedTime ? formatDateTime(item.bookedTime) : (item.updatedAt ? formatDateTime(item.updatedAt) : "—")
                                  ) : (
                                    item.blockedDate ? formatDate(item.blockedDate) : (item.createdAt ? formatDate(item.createdAt) : "—")
                                  )}
                                </td>
                                <td className="py-16 text-sm text-center">
                                  <span className={`badge px-10 py-6 radius-4 fw-bold uppercase ${item.paymentStatus === 'Paid' ? 'bg-success-100 text-success-800' :
                                    item.paymentStatus === 'Partially Paid' ? 'bg-warning-100 text-warning-800' : 'bg-danger-100 text-danger-800'
                                    }`}>
                                    {item.paymentStatus || 'Unpaid'}
                                  </span>
                                </td>
                                <td className="py-16 text-sm text-center">
                                  <span className={`badge px-10 py-6 radius-4 fw-bold uppercase ${item.approvalStatus === 'Approved' ? 'bg-success-100 text-success-800' :
                                    item.approvalStatus === 'Rejected' ? 'bg-danger-100 text-danger-800' : 'bg-warning-100 text-warning-800'
                                    }`}>
                                    {item.approvalStatus || 'Pending'}
                                  </span>
                                </td>
                                <td className="py-16 text-end">
                                  <div className="d-flex align-items-center justify-content-end gap-2 px-16">
                                    <button
                                      type="button"
                                      className="fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                      style={{ backgroundColor: "#eef2ff", color: "#4338ca", border: "1px solid #e0e7ff" }}
                                      title="View Full Details"
                                      onClick={async () => {
                                        setSelectedBlockItem(item);
                                        setShowBlockDetailModal(true);
                                        const res = await getClientFollowupPayments(item._id);
                                        if (res.status) {
                                          setSelectedBlockPayments(res.response?.data || res.response || []);
                                        } else {
                                          setSelectedBlockPayments([]);
                                        }
                                      }}
                                    >
                                      <Icon icon="lucide:eye" className="text-xl" />
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : clientFollowupStatus === "Rejected" ? (
                              <>
                                <td className="py-16 px-20 text-sm fw-semibold text-dark">
                                  {clientFollowupsPage * clientFollowupsLimit + idx + 1}
                                </td>
                                <td className="py-16 text-sm fw-bold text-dark">
                                  {item.companyName || item.client?.companyName || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {item.name || item.client?.keyPerson || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark fw-bold">
                                  {(() => {
                                    if (item.statusDetails && item.statusDetails.length > 0) {
                                      const rejectDetail = [...item.statusDetails].reverse().find(d => d.approvalStatus === 'Rejected' || d.status === 'Rejected' || d.status === 'Reject');
                                      return rejectDetail?.stallNo || item.stallNo || "N/A";
                                    }
                                    return item.stallNo || "N/A";
                                  })()}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {item.manager?.name || item.managerId?.name || item.managerName || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {(() => {
                                    if (item.statusDetails && item.statusDetails.length > 0) {
                                      const rejectDetail = [...item.statusDetails].reverse().find(d => d.approvalStatus === 'Rejected' || d.status === 'Rejected' || d.status === 'Reject');
                                      if (rejectDetail && rejectDetail.createdBy) {
                                        return rejectDetail.createdBy.name || "Admin";
                                      }
                                    }
                                    return "Admin";
                                  })()}
                                </td>
                                <td className="py-16 text-sm text-dark" style={{ maxWidth: "200px", whiteSpace: "normal" }}>
                                  {(() => {
                                    if (item.statusDetails && item.statusDetails.length > 0) {
                                      const rejectDetail = [...item.statusDetails].reverse().find(d => d.approvalStatus === 'Rejected' || d.status === 'Rejected' || d.status === 'Reject');
                                      return rejectDetail?.remarks || "No reason provided";
                                    }
                                    return "No reason provided";
                                  })()}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {(() => {
                                    if (item.statusDetails && item.statusDetails.length > 0) {
                                      const rejectDetail = [...item.statusDetails].reverse().find(d => d.approvalStatus === 'Rejected' || d.status === 'Rejected' || d.status === 'Reject');
                                      return rejectDetail?.date ? new Date(rejectDetail.date).toLocaleDateString('en-GB') : (item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-GB') : "—");
                                    }
                                    return item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-GB') : "—";
                                  })()}
                                </td>
                                <td className="py-16 text-center">
                                  <button
                                    type="button"
                                    className="fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                    style={{ backgroundColor: "#f3e8ff", color: "#7c3aed", border: "1px solid #ddd6fe" }}
                                    title="Followup History"
                                    onClick={async () => {
                                      const clientData = item.client && item.clientId ? item.client : {};
                                      setSelectedClientForModal({ ...clientData, _id: clientData._id || item._id, id: clientData.id || item.id, companyName: clientData.companyName || item.companyName || "N/A", keyPerson: clientData.keyPerson || item.name || "N/A" });
                                      const targetClientId = item.client?._id || item.clientId?._id || item.clientId?.id || item.clientId;
                                      if (targetClientId) {
                                        const historyRes = await getClientFollowupHistory(targetClientId, {
                                          expoId: selectedExpo?._id || selectedExpo?.id,
                                          managerId: item.managerId
                                        });
                                        if (historyRes.status) {
                                          setClientFollowupHistory(historyRes.response?.data || historyRes.response || []);
                                        }
                                      } else {
                                        const mappedHistory = (item.statusDetails || []).map(d => ({
                                          status: d.status,
                                          remarks: d.remarks,
                                          date: d.date,
                                          createdAt: d.createdAt,
                                          createdByName: d.createdByName || (d.createdBy === item.managerId ? item.manager?.name : 'Admin') || 'Manager'
                                        })).sort((a, b) => new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime());
                                        setClientFollowupHistory(mappedHistory);
                                      }
                                      setShowClientHistoryModal(true);
                                    }}
                                  >
                                    <Icon icon="lucide:history" className="text-xl" />
                                  </button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="py-16 px-20 text-sm fw-semibold text-dark">
                                  <input type="checkbox" className="form-check-input me-2"
                                    checked={selectedAssigns.includes(item._id || item.id)}
                                    onChange={(e) => {
                                      const itemId = item._id || item.id;
                                      if (e.target.checked) {
                                        setSelectedAssigns(prev => Array.from(new Set([...prev, itemId])));
                                      } else {
                                        setSelectedAssigns(prev => prev.filter(id => id !== itemId));
                                      }
                                    }}
                                  />
                                  {clientFollowupsPage * clientFollowupsLimit + idx + 1}
                                </td>
                                <td className="py-16 text-sm fw-bold text-dark">
                                  {item.companyName || item.client?.companyName || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {item.name || item.client?.keyPerson || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {item.mobileNo || item.client?.mobile1 || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-dark">
                                  {item.manager?.name || item.managerId?.name || item.managerName || "N/A"}
                                </td>
                                <td className="py-16 text-sm text-center">
                                  <span className="badge bg-warning-100 text-warning-800 px-10 py-4 radius-4 fw-bold">
                                    {item.client?.ratings || item.ratings || "N/A"} ★
                                  </span>
                                </td>
                                <td className="py-16 text-end">
                                  <div className="d-flex align-items-center justify-content-end gap-2 px-16">
                                    {/* <button
                                      type="button"
                                      className="fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                      style={{
                                        backgroundColor: "#eef2ff",
                                        color: "#4338ca",
                                        border: "1px solid #e0e7ff",
                                      }}
                                      title="View Details"
                                      onClick={() => {
                                        const clientData = item.client && item.clientId ? item.client : {};
                                        setSelectedClientForModal({
                                          ...clientData,
                                          _id: clientData._id || item._id,
                                          id: clientData.id || item.id,
                                          companyName: clientData.companyName || item.companyName || "N/A",
                                          keyPerson: clientData.keyPerson || item.name || "N/A",
                                          mobile1: clientData.mobile1 || item.mobileNo || "N/A",
                                          mobile2: clientData.mobile2 || "",
                                          mobile3: clientData.mobile3 || "",
                                          email: clientData.email || item.email || "N/A",
                                          ratings: clientData.ratings || item.ratings || "OTHER EXHIBITOR",
                                          designation: clientData.designation || item.designation || "N/A",
                                          address: clientData.address || item.address || "N/A",
                                          city: clientData.city || item.city || "",
                                          pincode: clientData.pincode || item.pincode || "",
                                          landline: clientData.landline || item.landline || "N/A",
                                          websiteLink: clientData.websiteLink || item.websiteLink || "",
                                          products: (clientData.products && clientData.products.length > 0) ? clientData.products : (item.products || []),
                                          interestedLocations: (clientData.interestedLocations && clientData.interestedLocations.length > 0) ? clientData.interestedLocations : (item.interestedLocations || []),
                                          segment: clientData.segment || item.segment || ""
                                        });
                                        setShowClientDetailModal(true);
                                      }}
                                    >
                                      <Icon icon="lucide:eye" className="text-xl" />
                                    </button> */}
                                    <button
                                      type="button"
                                      className="fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                      style={{
                                        backgroundColor: "#f3e8ff",
                                        color: "#7c3aed",
                                        border: "1px solid #ddd6fe",
                                      }}
                                      title="Followup History"
                                      onClick={async () => {
                                        const clientData = item.client && item.clientId ? item.client : {};
                                        setSelectedClientForModal({
                                          ...clientData,
                                          _id: clientData._id || item._id,
                                          id: clientData.id || item.id,
                                          companyName: clientData.companyName || item.companyName || "N/A",
                                          keyPerson: clientData.keyPerson || item.name || "N/A",
                                          mobile1: clientData.mobile1 || item.mobileNo || "N/A",
                                          mobile2: clientData.mobile2 || "",
                                          mobile3: clientData.mobile3 || "",
                                          email: clientData.email || item.email || "N/A",
                                          ratings: clientData.ratings || item.ratings || "OTHER EXHIBITOR",
                                          designation: clientData.designation || item.designation || "N/A",
                                          address: clientData.address || item.address || "N/A",
                                          city: clientData.city || item.city || "",
                                          pincode: clientData.pincode || item.pincode || "",
                                          landline: clientData.landline || item.landline || "N/A",
                                          websiteLink: clientData.websiteLink || item.websiteLink || "",
                                          products: (clientData.products && clientData.products.length > 0) ? clientData.products : (item.products || []),
                                          interestedLocations: (clientData.interestedLocations && clientData.interestedLocations.length > 0) ? clientData.interestedLocations : (item.interestedLocations || []),
                                          segment: clientData.segment || item.segment || ""
                                        });
                                        const targetClientId = item.clientId?._id || item.clientId?.id || item.clientId;
                                        if (targetClientId) {
                                          const historyRes = await getClientFollowupHistory(targetClientId, {
                                            expoId: selectedExpo?._id || selectedExpo?.id,
                                            managerId: item.managerId
                                          });
                                          if (historyRes.status) {
                                            setClientFollowupHistory(historyRes.response?.data || historyRes.response || []);
                                          }
                                        } else {
                                          // Standalone followup - map statusDetails to history format expected by the modal
                                          const mappedHistory = (item.statusDetails || []).map(d => ({
                                            status: d.status,
                                            remarks: d.remarks,
                                            date: d.date,
                                            createdAt: d.createdAt,
                                            createdByName: d.createdByName || (d.createdBy === item.managerId ? item.manager?.name : 'Admin') || 'Manager'
                                          })).sort((a, b) => new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime());
                                          setClientFollowupHistory(mappedHistory);
                                        }
                                        setNewClientFollowupData({
                                          remarks: "",
                                          status: clientFollowupStatus,
                                        });
                                        setShowClientHistoryModal(true);
                                      }}
                                    >
                                      <Icon icon="lucide:history" className="text-xl" />
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={(clientFollowupStatus === "Block" || clientFollowupStatus === "Blocked") ? "10" : clientFollowupStatus === "Rejected" ? "9" : "7"} className="text-center py-80 text-secondary-light">
                            <Icon icon="solar:folder-error-bold-duotone" className="text-muted mb-16" style={{ fontSize: "48px" }} />
                            <h6 className="mb-0 text-dark fw-bold">No followups found.</h6>
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {clientFollowupsTotal > 0 && (
                <div className="px-24 pb-24 border-top">
                  <TablePagination
                    currentPage={clientFollowupsPage}
                    totalPages={Math.ceil(clientFollowupsTotal / clientFollowupsLimit)}
                    onPageChange={(page) => setClientFollowupsPage(page)}
                    rowsPerPage={clientFollowupsLimit}
                    onRowsPerPageChange={(e) => {
                      setClientFollowupsLimit(parseInt(e.target.value));
                      setClientFollowupsPage(0);
                    }}
                    totalRecords={clientFollowupsTotal}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : activeView === "detailed-booked" ? (
        <div className="p-24 bg-neutral-50 min-vh-100">
          {/* Header Section - Exact Match */}
          <div
            className="p-24 radius-16 mb-24 position-relative overflow-hidden"
            style={{
              background:
                "linear-gradient(to right, #000000, #bb0007, #ed1c24)",
            }}
          >
            <div className="d-flex align-items-center justify-content-between position-relative z-1">
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-white w-40-px h-40-px rounded d-flex align-items-center justify-content-center shadow-sm border-0"
                  onClick={() => setActiveView("grid")}
                >
                  <Icon
                    icon="solar:alt-arrow-left-linear"
                    className="text-xl text-secondary-light"
                  />
                </button>
                <div>
                  <div className="d-flex align-items-center gap-3 mb-8">
                    <h5 className="mb-0 fw-bold text-white">
                      {typeof selectedExpo.name === "object"
                        ? selectedExpo.name?.name
                        : selectedExpo.name}
                    </h5>
                    <span className="badge bg-white text-danger-600 px-12 py-6 radius-8 fw-bold">
                      {selectedExpo.entryType || "Paid Entry"}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-4 text-white opacity-80">
                    <div className="d-flex align-items-center gap-2">
                      <Icon
                        icon="solar:calendar-date-bold"
                        className="text-lg"
                      />
                      <span className="text-sm fw-medium">
                        {selectedExpo.date}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Icon icon="solar:map-point-bold" className="text-lg" />
                      <span className="text-sm fw-medium">
                        {selectedExpo.venue}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <button
                className="btn btn-success radius-8 px-20 py-10 fw-bold d-flex align-items-center gap-2 border-0 shadow-sm"
                style={{ backgroundColor: "#2ecc71" }}
              >
                <Icon icon="solar:file-download-bold" className="text-xl" />
                Download Report
              </button>
            </div>
          </div>

          {/* Stats Summary Row - Exact Match */}
          <div className="card radius-16 border-0 shadow-sm mb-24 overflow-hidden">
            <div className="card-body p-0">
              <div className="row g-0">
                {[
                  {
                    label: "Manager",
                    val: Array.isArray(selectedExpo.eventManager)
                      ? selectedExpo.eventManager
                        .map((m) => m.name)
                        .filter(Boolean)
                        .join(", ")
                      : selectedExpo.eventManager?.name || "N/A",
                    icon: "solar:user-bold-duotone",
                    c: "danger",
                  },
                  {
                    label: "Stalls",
                    val: `${bookingList.length} Booked`,
                    icon: "solar:shop-2-bold-duotone",
                    c: "primary",
                  },
                  {
                    label: "Visitors",
                    val: `${selectedExpo.stats?.visitorBooked ?? 0} Registered`,
                    icon: "solar:users-group-two-rounded-bold-duotone",
                    c: "success",
                  },
                  {
                    label: "Revenue",
                    val: "₹0",
                    icon: "solar:wallet-money-bold-duotone",
                    c: "warning",
                  },
                ].map((st, i) => (
                  <div
                    className={`col-md-3 p-24 ${i < 3 ? "border-end" : ""}`}
                    key={i}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className={`w-48-px h-48-px rounded-circle bg-${st.c}-100 d-flex align-items-center justify-content-center`}
                      >
                        <Icon
                          icon={st.icon}
                          className={`text-2xl text-${st.c}-600`}
                        />
                      </div>
                      <div>
                        <span className="text-secondary-light text-xs fw-bold d-block mb-4">
                          {st.label}
                        </span>
                        <h6 className="mb-0 fw-bold text-dark">{st.val}</h6>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs Section - Exact Match */}
          <div className="card radius-16 border-0 shadow-sm overflow-hidden">
            <div className="card-header bg-white p-0">
              <div className="d-flex w-100">
                <button
                  className={`w-100 py-20 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${activeTab === "stalls" ? "text-danger-600 bg-white" : "text-secondary-light bg-neutral-50"}`}
                  onClick={() => setActiveTab("stalls")}
                  style={{
                    border:
                      activeTab === "stalls"
                        ? "1px solid #ed1c24"
                        : "1px solid #e9ecef",
                    borderTop:
                      activeTab === "stalls"
                        ? "4px solid #ed1c24"
                        : "1px solid #e9ecef",
                    borderBottom:
                      activeTab === "stalls" ? "0" : "1px solid #e9ecef",
                    zIndex: activeTab === "stalls" ? 2 : 1,
                  }}
                >
                  <Icon icon="solar:home-2-bold" />
                  Stall Booked List ({bookingList.length})
                </button>
                <button
                  className={`w-100 py-20 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${activeTab === "visitors" ? "text-danger-600 bg-white" : "text-secondary-light bg-neutral-50"}`}
                  onClick={() => setActiveTab("visitors")}
                  style={{
                    border:
                      activeTab === "visitors"
                        ? "1px solid #ed1c24"
                        : "1px solid #e9ecef",
                    borderTop:
                      activeTab === "visitors"
                        ? "4px solid #ed1c24"
                        : "1px solid #e9ecef",
                    borderBottom:
                      activeTab === "visitors" ? "0" : "1px solid #e9ecef",
                    zIndex: activeTab === "visitors" ? 2 : 1,
                  }}
                >
                  <Icon icon="solar:users-group-two-rounded-bold" />
                  Visitor Booked List ({selectedExpo.stats?.visitorBooked ?? 0})
                </button>
              </div>
            </div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table
                  className="table table-hover mb-0"
                  style={{ minWidth: "1200px" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#ed1c24" }}>
                      {activeTab === "stalls" ? (
                        <>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0">
                            Stall No
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Name
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Company
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Source
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Exhibition Type
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Products
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Amount
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-end">
                            Status
                          </th>
                        </>
                      ) : (
                        <>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0">
                            Visitor ID
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Name
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Organization
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Designation
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-center">
                            Contact Info
                          </th>
                          <th className="p-16 text-xs fw-bold text-white uppercase border-0 text-end">
                            Status
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {activeTab === "stalls" ? (
                      bookingList.length > 0 ? (
                        bookingList.map((item, idx) => (
                          <tr key={idx}>
                            <td className="p-16 text-sm fw-bold text-dark">
                              {item.stallNo}
                            </td>
                            <td className="p-16 text-sm text-center">
                              {item.name}
                            </td>
                            <td className="p-16 text-sm text-center">
                              {item.companyName}
                            </td>
                            <td className="p-16 text-sm text-center">
                              {typeof item.source === "object"
                                ? item.source?.name
                                : item.source || "N/A"}
                            </td>
                            <td className="p-16 text-sm text-center">
                              {typeof item.category === "object"
                                ? item.category?.name
                                : item.category || "N/A"}
                            </td>
                            <td className="p-16 text-sm text-center">
                              {typeof item.product === "object"
                                ? item.product?.name
                                : item.product || "N/A"}
                            </td>
                            <td className="p-16 text-sm text-center">
                              ₹{item.totalAmount || 0}
                            </td>
                            <td className="p-16 text-end">
                              <span
                                className={`badge px-12 py-6 radius-pill ${item.paymentStatus === "Paid" ? "bg-success text-white" : "bg-warning text-dark"}`}
                              >
                                {item.paymentStatus || "Pending"}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center py-80 text-secondary-light"
                          >
                            No stall bookings found for this past expo.
                          </td>
                        </tr>
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="text-center py-80 text-secondary-light"
                        >
                          No visitor bookings found for this past expo.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Placeholder */}
              <div className="p-24 border-top d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <span className="text-sm text-secondary-light">Show</span>
                  <select
                    className="form-select form-select-sm radius-8"
                    style={{ width: "80px" }}
                  >
                    <option>10</option>
                    <option>25</option>
                    <option>50</option>
                  </select>
                </div>
                <div className="text-sm text-secondary-light">
                  Showing 0 to 0 of 0 Entries
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-light px-12 radius-8 border-0">
                    <Icon icon="solar:alt-arrow-left-linear" />
                  </button>
                  <button
                    className="btn btn-sm btn-danger px-16 radius-8 border-0"
                    style={{ backgroundColor: "#ed1c24" }}
                  >
                    1
                  </button>
                  <button className="btn btn-sm btn-light px-12 radius-8 border-0">
                    <Icon icon="solar:alt-arrow-right-linear" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
          <div
            className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <button
                className="btn btn-sm btn-light d-flex align-items-center justify-content-center w-36-px h-36-px rounded-circle p-0"
                onClick={() => setActiveView("grid")}
              >
                <Icon
                  icon="solar:arrow-left-linear"
                  className="text-xl text-dark"
                />
              </button>
              <div>
                <div className="d-flex align-items-center gap-2 mb-4">
                  <h6 className="text-white mb-0 fw-bold">
                    {selectedExpo.expoName || selectedExpo.name || "N/A"}
                  </h6>
                  <span
                    className={`badge ${selectedExpo.entryType === "Free" ? "bg-info" : "bg-success"} text-white px-8 py-2 radius-4 text-xs`}
                  >
                    {selectedExpo.entryType || "Paid"}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-3 text-white opacity-75 text-xs">
                  <span className="d-flex align-items-center gap-1">
                    <Icon icon="solar:map-point-bold" /> {selectedExpo.venue}
                  </span>
                  <span className="d-flex align-items-center gap-1">
                    <Icon icon="solar:clock-circle-bold" />{" "}
                    {selectedExpo.startTime || "10:00 AM"} -{" "}
                    {selectedExpo.endTime || "07:00 PM"}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                className="btn btn-light d-inline-flex align-items-center gap-2 radius-8 px-16 h-40-px fw-bold shadow-sm"
                style={{ color: "#ed1c24", border: "1px solid rgba(255,255,255,0.4)" }}
                onClick={() => {
                  setShowBulkUploadModal(true);
                  setSelectedExcelFile(null);
                  setUploadResult(null);
                }}
              >
                <Icon icon="solar:upload-minimalistic-bold" className="text-xl" />
                <span>Upload Enquiries Data</span>
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="d-flex w-100 mb-24">
              {[
                { id: "stall_enquiry", label: "Stall Enquiries" },
                { id: "visitor_enquiry", label: "Visitor Enquiries" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  className={`w-100 py-16 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${mainTab === tab.id ? "text-danger-600 bg-white" : "text-secondary-light bg-neutral-50"}`}
                  onClick={() => {
                    setMainTab(tab.id);
                    setSourceFilter("All");
                    setKnownSourceFilter("All");
                    setCurrentPage(0);
                  }}
                  style={{
                    border:
                      mainTab === tab.id
                        ? "1px solid #ed1c24"
                        : "1px solid #e9ecef",
                    borderTop:
                      mainTab === tab.id
                        ? "4px solid #ed1c24"
                        : "1px solid #e9ecef",
                    borderBottom:
                      mainTab === tab.id ? "0" : "1px solid #e9ecef",
                    zIndex: mainTab === tab.id ? 2 : 1,
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-24 pt-0">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-24">
                <div className="navbar-search position-relative">
                  <input
                    type="text"
                    className="bg-neutral-50 h-40-px radius-8 border-0"
                    placeholder="Search leads..."
                    value={detailSearchTerm}
                    onChange={(e) => handleDetailSearch(e.target.value)}
                    style={{ paddingLeft: "40px", width: "250px" }}
                  />
                  <Icon
                    icon="ion:search-outline"
                    className="position-absolute text-secondary-light text-xl"
                    style={{
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>

                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="text-sm fw-bold text-secondary-light uppercase me-12">
                      Source Filters:
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      {[
                        { id: "All", label: `All: ${sourceCounts.all}`, colorClass: "bg-dark" },
                        { id: "Website", label: `Website: ${sourceCounts.website}`, colorClass: "bg-primary-600" },
                        // { id: "Admin-app", label: `Admin-app: ${sourceCounts.adminApp}`, colorClass: "bg-warning-600" },
                        { id: "User-app", label: `User-app: ${sourceCounts.userApp}`, colorClass: "bg-info-600" }
                      ].map((filt) => (
                        <button
                          key={filt.id}
                          className={`d-flex align-items-center gap-2 px-12 py-6 border radius-8 transition-all ${sourceFilter === filt.id ? "bg-primary-50 border-primary-600 shadow-sm" : "bg-neutral-50"}`}
                          onClick={() => {
                            setSourceFilter(filt.id);
                            setCurrentPage(0);
                          }}
                        >
                          <div className={`w-8-px h-8-px rounded-circle ${filt.colorClass}`}></div>
                          <span className={`text-sm fw-bold uppercase ${sourceFilter === filt.id ? "text-primary-600" : "text-dark"}`}>
                            {filt.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {mainTab === "visitor_enquiry" && (
                    <div className="d-flex align-items-center gap-2">
                      {isPropertyExpo && (
                        <>
                          <button
                            type="button"
                            className="btn d-inline-flex align-items-center gap-2 radius-8 px-16 h-40-px fw-bold text-white shadow-sm"
                            style={{ backgroundColor: "#25d366", borderColor: "#25d366" }}
                            onClick={() => {
                              if (selectedVisitorIds.length === 0) {
                                toast.warning("Please select at least one visitor");
                                return;
                              }
                              toast.info("WhatsApp API integration is in progress");
                            }}
                            title="Send WhatsApp Message"
                          >
                            <Icon icon="ic:baseline-whatsapp" className="text-xl" />
                            <span>WhatsApp {selectedVisitorIds.length > 0 ? `(${selectedVisitorIds.length})` : ""}</span>
                          </button>

                          <button
                            type="button"
                            className="btn d-inline-flex align-items-center gap-2 radius-8 px-16 h-40-px fw-bold text-white shadow-sm"
                            style={{ backgroundColor: "#0284c7", borderColor: "#0284c7" }}
                            onClick={handleSendBulkSMS}
                            disabled={sendingSMS}
                            title="Send Bulk SMS"
                          >
                            <Icon icon="solar:chat-round-line-bold" className="text-xl" />
                            <span>{sendingSMS ? "Sending SMS..." : `Send SMS ${selectedVisitorIds.length > 0 ? `(${selectedVisitorIds.length})` : ""}`}</span>
                          </button>
                        </>
                      )}

                      <button
                        type="button"
                        className="btn btn-success d-inline-flex align-items-center gap-2 radius-8 px-16 h-40-px fw-bold text-white shadow-sm"
                        onClick={handleExportVisitorExcel}
                        disabled={exportingExcel}
                        style={{ backgroundColor: "#21a366", borderColor: "#21a366" }}
                      >
                        <Icon icon="ri:file-excel-2-line" className="text-xl" />
                        <span>{exportingExcel ? "Exporting..." : "Export Excel"}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {mainTab === "visitor_enquiry" && (
                <div className="d-flex align-items-center gap-2 flex-wrap mb-24 p-16 radius-8 bg-neutral-50 border">
                  <div className="text-sm fw-bold text-secondary-light uppercase me-12 d-flex align-items-center gap-2">
                    <Icon icon="solar:filter-bold-duotone" className="text-danger-600 text-lg" />
                    Known Source:
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {knownSourceList.map((ksName) => {
                      const count = visitorKnownSourceCounts[ksName] || 0;
                      const isSelected = knownSourceFilter === ksName;
                      return (
                        <button
                          key={ksName}
                          type="button"
                          className={`d-flex align-items-center gap-2 px-12 py-6 border radius-8 transition-all ${
                            isSelected
                              ? "bg-danger-50 border-danger-600 shadow-sm text-danger-600 fw-bold"
                              : "bg-white text-dark fw-medium hover-bg-neutral-100"
                          }`}
                          onClick={() => {
                            setKnownSourceFilter(ksName);
                            setCurrentPage(0);
                          }}
                        >
                          <div
                            className={`w-8-px h-8-px rounded-circle ${
                              isSelected ? "bg-danger-600" : "bg-secondary-400"
                            }`}
                          ></div>
                          <span className="text-xs">
                            {ksName}: <span className="fw-bold">{count}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div
                className="table-responsive shadow-sm radius-8 border scroll-sm"
              >
                <table
                  className="table table-hover mb-0"
                  style={{ minWidth: "1100px" }}
                >
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      background: "linear-gradient(135deg, #bb0007, #ed1c24)",
                    }}
                  >
                    <tr>
                      {mainTab === "visitor_enquiry" && isPropertyExpo && (
                        <th className="text-white fw-bold py-12 px-16 text-center" style={{ width: "45px" }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={
                              visitorEnquiries.length > 0 &&
                              visitorEnquiries.every((v) => selectedVisitorIds.includes(v._id || v.id))
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedVisitorIds(visitorEnquiries.map((v) => v._id || v.id));
                              } else {
                                setSelectedVisitorIds([]);
                              }
                            }}
                          />
                        </th>
                      )}
                      <th
                        style={{
                          color: "white",
                          fontWeight: "bold",
                          width: "70px",
                        }}
                        className="py-12 px-16"
                      >
                        Sl.No
                      </th>
                      {mainTab === "stall_enquiry" ? (
                        <>
                          <th style={{ color: "white", fontWeight: "bold", width: "160px" }} className="py-12">Mobile</th>
                          <th style={{ color: "white", fontWeight: "bold", width: "220px" }} className="py-12">Name</th>
                          <th style={{ color: "white", fontWeight: "bold", width: "220px" }} className="py-12">Company</th>
                          <th style={{ color: "white", fontWeight: "bold", width: "200px" }} className="py-12">Remarks</th>
                          <th style={{ color: "white", fontWeight: "bold", width: "130px" }} className="py-12 text-center">Source</th>
                        </>
                      ) : (
                        <>
                          <th style={{ color: "white", fontWeight: "bold", width: "160px" }} className="py-12">Date</th>
                          <th style={{ color: "white", fontWeight: "bold", width: "180px" }} className="py-12">Name</th>
                          <th style={{ color: "white", fontWeight: "bold", width: "180px" }} className="py-12">Company</th>
                          <th style={{ color: "white", fontWeight: "bold", width: "180px" }} className="py-12">Contact Info</th>
                          <th style={{ color: "white", fontWeight: "bold", width: "180px" }} className="py-12">Known Source</th>
                          <th style={{ color: "white", fontWeight: "bold", width: "130px" }} className="py-12 text-center">Source</th>
                        </>
                      )}
                      <th
                        style={{
                          color: "white",
                          fontWeight: "bold",
                          textAlign: "center",
                          width: "120px",
                        }}
                        className="py-12"
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedList.map((item, index) => (
                      <tr key={item.id || index}>
                        {mainTab === "visitor_enquiry" && isPropertyExpo && (
                          <td className="px-16 text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedVisitorIds.includes(item._id || item.id)}
                              onChange={(e) => {
                                const id = item._id || item.id;
                                if (e.target.checked) {
                                  setSelectedVisitorIds((prev) => [...prev, id]);
                                } else {
                                  setSelectedVisitorIds((prev) => prev.filter((x) => x !== id));
                                }
                              }}
                            />
                          </td>
                        )}
                        <td className="px-16 py-12">{currentPage * rowsPerPage + index + 1}</td>
                        {mainTab === "stall_enquiry" ? (
                          <>
                            <td>{item.mobileNo || item.phone || "-"}</td>
                            <td>{item.name}</td>
                            <td>{item.companyName || "N/A"}</td>
                            <td className="text-secondary-light" style={{ maxWidth: "250px", fontSize: "12px" }}>
                              <div
                                title={item.remarks || ""}
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "normal",
                                  lineHeight: "1.5"
                                }}
                              >
                                {item.remarks || "-"}
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="fw-semibold text-secondary-light" style={{ fontSize: "13px" }}>
                              {formatDateTime(item.createdAt || item.date)}
                            </td>
                            <td>{item.name}</td>
                            <td>{item.companyName || "N/A"}</td>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="fw-semibold">{item.mobileNo || item.phone || "N/A"}</span>
                                <span className="text-secondary-light text-xs">{item.email || ""}</span>
                              </div>
                            </td>
                            <td className="text-secondary-light" style={{ fontSize: "13px" }}>
                              {item.knowAboutExhibition || "-"}
                            </td>
                          </>
                        )}
                        <td className="text-center">
                          <span
                            className="badge px-12 py-6 radius-4 fw-bold uppercase"
                            style={{
                              letterSpacing: "0.5px",
                              backgroundColor:
                                item.source &&
                                  item.source.toLowerCase().trim() === "website"
                                  ? "#ff7828"
                                  : item.source &&
                                    item.source.toLowerCase().trim() === "mobile-admin"
                                    ? "#e6f2ff"
                                    : "#f0fdf4",
                              color:
                                item.source &&
                                  item.source.toLowerCase().trim() === "website"
                                  ? "#ffffff"
                                  : item.source &&
                                    item.source.toLowerCase().trim() === "mobile-admin"
                                    ? "#0066cc"
                                    : "#16a34a",
                              minWidth: "80px",
                              display: "inline-block",
                            }}
                          >
                            {item.source && item.source.trim() !== ""
                              ? item.source === "mobile-admin"
                                ? "Admin-App"
                                : item.source === "user-app"
                                  ? "User-app"
                                  : item.source.charAt(0).toUpperCase() + item.source.slice(1)
                              : "Admin"}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center gap-8">
                            <button
                              type="button"
                              className="glass-action-btn glass-btn-view"
                              title="View Details"
                              style={{ width: "30px", height: "30px" }}
                              onClick={() => {
                                setSelectedLead({
                                  ...item,
                                  businessCategory: item.businessCategory || item.segment || "",
                                  leadStatus: item.leadStatus || item.status || "Enquiry"
                                });
                                setShowHistoryModal(true);
                              }}
                            >
                              <Icon
                                icon="solar:eye-bold-duotone"
                                style={{ fontSize: "16px" }}
                              />
                            </button>
                            {item.paymentScreenshot && (
                              <a
                                href={getImageUrl(item.paymentScreenshot)}
                                target="_blank"
                                rel="noreferrer"
                                className="glass-action-btn glass-btn-edit"
                                title="View Receipt"
                                style={{ width: "30px", height: "30px" }}
                              >
                                <Icon
                                  icon="solar:gallery-bold"
                                  style={{ fontSize: "16px" }}
                                />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(mainTab === "visitor_enquiry" ? visitorTotal : stallTotal) === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center py-40">
                          <div className="text-secondary-light">
                            No records found
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-24 pb-24">
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => setCurrentPage(page)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value));
                    setCurrentPage(0);
                  }}
                  totalRecords={mainTab === "visitor_enquiry" ? visitorTotal : stallTotal}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {sharedModals}

      {/* Stall Booking Payment Modal */}
      {showPaymentModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div
                className="modal-header border-bottom py-16 px-24"
                style={{
                  background:
                    "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                }}
              >
                <h6 className="modal-title text-white mb-0">
                  STALL BOOKING PAYMENT
                </h6>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowPaymentModal(false)}
                ></button>
              </div>
              <div className="modal-body p-24">
                <div className="row gy-4">
                  <div className="col-12 text-center pb-12">
                    <h6 className="mb-0 text-dark">
                      Confirming booking for:{" "}
                      <span className="text-danger fw-bold">
                        {pendingMove?.item?.name}
                      </span>
                    </h6>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      Payment Status <span className="text-danger">*</span>
                    </label>
                    <div
                      className={
                        paymentErrors.paymentStatus ? "is-invalid" : ""
                      }
                    >
                      <Select
                        options={[
                          { value: "Paid", label: "Fully Paid" },
                          { value: "Partially", label: "Partially Paid" },
                        ]}
                        value={
                          [
                            { value: "Paid", label: "Fully Paid" },
                            {
                              value: "Partially",
                              label: "Partially Paid",
                            },
                          ].find(
                            (opt) =>
                              opt.value === bookingPaymentInfo.paymentStatus,
                          ) || null
                        }
                        onChange={(sel) => {
                          setBookingPaymentInfo({
                            ...bookingPaymentInfo,
                            paymentStatus: sel.value,
                          });
                          if (paymentErrors.paymentStatus)
                            setPaymentErrors((prev) => ({
                              ...prev,
                              paymentStatus: null,
                            }));
                        }}
                        styles={selectStyles()}
                      />
                    </div>
                    {paymentErrors.paymentStatus && (
                      <div className="text-danger mt-1 small">
                        {paymentErrors.paymentStatus}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Stall No</label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={bookingPaymentInfo.stallNo}
                      readOnly={!!pendingMove?.item?.stallNo}
                      onChange={(e) =>
                        setBookingPaymentInfo({
                          ...bookingPaymentInfo,
                          stallNo: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      Stall Area (SQFT) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      name="sqft"
                      className={`form-control radius-8 ${paymentErrors.sqft ? "is-invalid" : ""}`}
                      placeholder="e.g. 100"
                      min="0"
                      value={bookingPaymentInfo.sqft}
                      readOnly={
                        !!(pendingMove?.item?.sqft || pendingMove?.item?.sqm)
                      }
                      onChange={(e) =>
                        setBookingPaymentInfo({
                          ...bookingPaymentInfo,
                          sqft: e.target.value,
                        })
                      }
                    />
                    {paymentErrors.sqft && (
                      <div className="invalid-feedback">
                        {paymentErrors.sqft}
                      </div>
                    )}
                  </div>

                  {(bookingPaymentInfo.paymentStatus === "Paid" ||
                    bookingPaymentInfo.paymentStatus === "Fully Paid" ||
                    bookingPaymentInfo.paymentStatus === "Partially" ||
                    bookingPaymentInfo.paymentStatus === "Partially Paid") && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            Payment Type <span className="text-danger">*</span>
                          </label>
                          <div
                            className={
                              paymentErrors.paymentMethod ? "is-invalid" : ""
                            }
                          >
                            <Select
                              options={[
                                { value: "Cash", label: "Cash" },
                                { value: "UPI", label: "UPI" },
                              ]}
                              value={
                                [
                                  { value: "Cash", label: "Cash" },
                                  { value: "UPI", label: "UPI" },
                                ].find(
                                  (opt) =>
                                    opt.value ===
                                    bookingPaymentInfo.paymentMethod,
                                ) || null
                              }
                              onChange={(sel) => {
                                setBookingPaymentInfo({
                                  ...bookingPaymentInfo,
                                  paymentMethod: sel.value,
                                  screenshot:
                                    sel.value === "UPI"
                                      ? bookingPaymentInfo.screenshot
                                      : null,
                                });
                                if (paymentErrors.paymentMethod)
                                  setPaymentErrors((prev) => ({
                                    ...prev,
                                    paymentMethod: null,
                                  }));
                              }}
                              styles={selectStyles()}
                            />
                          </div>
                          {paymentErrors.paymentMethod && (
                            <div className="text-danger mt-1 small">
                              {paymentErrors.paymentMethod}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            Amount Paid <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="amount"
                            className={`form-control radius-8 ${paymentErrors.amount ? "is-invalid" : ""}`}
                            placeholder="Enter Amount"
                            value={formatIndianNumber(bookingPaymentInfo.amount)}
                            onChange={(e) => {
                              const rawVal = e.target.value.replace(/,/g, "");
                              if (!isNaN(rawVal) || rawVal === "") {
                                setBookingPaymentInfo({
                                  ...bookingPaymentInfo,
                                  amount: rawVal,
                                });
                                if (paymentErrors.amount)
                                  setPaymentErrors((prev) => ({
                                    ...prev,
                                    amount: null,
                                  }));
                              }
                            }}
                          />
                          {paymentErrors.amount && (
                            <div className="invalid-feedback">
                              {paymentErrors.amount}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                  {(bookingPaymentInfo.paymentStatus === "Paid" ||
                    bookingPaymentInfo.paymentStatus === "Fully Paid" ||
                    bookingPaymentInfo.paymentStatus === "Partially" ||
                    bookingPaymentInfo.paymentStatus === "Partially Paid") &&
                    bookingPaymentInfo.paymentMethod === "UPI" && (
                      <div className="col-12 border-top pt-16 mt-16 text-start">
                        <label className="form-label fw-bold">
                          Upload Payment Screenshot{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="p-20 border rounded bg-light text-center position-relative cursor-pointer">
                          <input
                            type="file"
                            className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
                            accept="image/*"
                            onChange={handleScreenShotUpload}
                          />
                          <Icon
                            icon="solar:camera-bold"
                            className="text-3xl text-secondary-light mb-8"
                          />
                          <p className="mb-0 text-sm">
                            {bookingPaymentInfo.screenshot
                              ? "Screenshot Uploaded ✓"
                              : "Click to upload screenshot"}
                          </p>{" "}
                        </div>
                        {paymentErrors.screenshot && (
                          <div className="invalid-feedback d-block">
                            {paymentErrors.screenshot}
                          </div>
                        )}
                        {bookingPaymentInfo.screenshot && (
                          <div className="mt-12 text-center">
                            <img
                              src={bookingPaymentInfo.screenshot}
                              alt="Preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "150px",
                                borderRadius: "8px",
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  <div className="col-12 mt-16">
                    <label className="form-label fw-bold">
                      Booking Remark{" "}
                      {(bookingPaymentInfo.paymentStatus === "Paid" ||
                        bookingPaymentInfo.paymentStatus === "Fully Paid" ||
                        bookingPaymentInfo.paymentStatus === "Partially" ||
                        bookingPaymentInfo.paymentStatus ===
                        "Partially Paid") && (
                          <span className="text-danger">*</span>
                        )}
                    </label>
                    <textarea
                      className={`form-control radius-8 ${paymentErrors.remark ? "is-invalid" : ""}`}
                      rows="2"
                      placeholder="Enter remark for booking..."
                      value={bookingPaymentInfo.remark}
                      onChange={(e) => {
                        setBookingPaymentInfo({
                          ...bookingPaymentInfo,
                          remark: e.target.value,
                        });
                        if (paymentErrors.remark)
                          setPaymentErrors((prev) => ({
                            ...prev,
                            remark: null,
                          }));
                      }}
                    />
                    {paymentErrors.remark && (
                      <div className="invalid-feedback">
                        {paymentErrors.remark}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 px-24 font-weight-bold"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentErrors({});
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn radius-8 px-40 text-white fw-bold"
                  style={{ backgroundColor: "#ed1c24" }}
                  onClick={handleBookingConfirm}
                >
                  Confirm Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectConfirm && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg text-center">
              <div className="modal-body p-32">
                <Icon
                  icon="solar:danger-triangle-bold"
                  className="text-danger mb-16"
                  style={{ fontSize: "70px" }}
                />
                <h5 className="mb-12 fw-bold">Are you sure?</h5>
                <p className="text-secondary-light mb-24">
                  Do you really want to move this{" "}
                  {pendingMove?.from === "Booking" ? "booking" : "enquiry"} to
                  the Rejected list?
                </p>
                <div className="mb-24 text-start">
                  <label className="form-label fw-bold text-secondary-light mb-8">
                    Reason for Rejection <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control radius-8 border-secondary-light ${rejectError ? "is-invalid" : ""}`}
                    rows="3"
                    placeholder="Enter reason for rejection..."
                    value={rejectRemark}
                    onChange={(e) => {
                      setRejectRemark(e.target.value);
                      if (rejectError) setRejectError("");
                    }}
                    style={{ resize: "none" }}
                  />
                  {rejectError && (
                    <div className="invalid-feedback d-block">{rejectError}</div>
                  )}
                </div>
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <button
                    className="btn btn-secondary radius-8 px-32"
                    onClick={() => setShowRejectConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger radius-8 px-32"
                    style={{ backgroundColor: "#ed1c24", border: "none" }}
                    onClick={handleRejectConfirm}
                  >
                    Yes, Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg text-center">
              <div className="modal-body p-32">
                <div className="d-flex justify-content-center mb-3">
                  <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
                    <Icon
                      icon="solar:trash-bin-trash-bold"
                      className="text-danger-600 text-xxl"
                      style={{ fontSize: "32px", color: "#dc3545" }}
                    />
                  </div>
                </div>
                <h5 className="mb-12 fw-bold">Are you sure?</h5>
                <p className="text-secondary-light mb-24">
                  Do you want to delete expo "
                  {expoToDelete?.expoName || expoToDelete?.name}"?
                </p>
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary radius-8 px-32"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setExpoToDelete(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger radius-8 px-32 text-white fw-bold"
                    style={{ backgroundColor: "#ed1c24", border: "none" }}
                    onClick={confirmDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Confirm"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {showApproveConfirm && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1100 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg text-center">
              <div className="modal-body p-32">
                <div className="d-flex justify-content-center mb-3">
                  <div className="bg-success-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
                    <Icon
                      icon="solar:check-circle-bold"
                      className="text-success text-xxl"
                      style={{ fontSize: "32px", color: "#198754" }}
                    />
                  </div>
                </div>
                <h5 className="mb-12 fw-bold">Confirm Approval</h5>
                <p className="text-secondary-light mb-24">
                  Are you sure you want to approve this booking?
                </p>
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary radius-8 px-32"
                    onClick={() => {
                      setShowApproveConfirm(false);
                      setPendingApproveItem(null);
                    }}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-success radius-8 px-32 text-white fw-bold"
                    style={{ backgroundColor: "#198754", border: "none" }}
                    onClick={async () => {
                      setShowApproveConfirm(false);
                      if (pendingApproveItem) {
                        const res = await updateClientFollowupStatus({
                          followupId: pendingApproveItem._id,
                          clientId: pendingApproveItem.clientId?._id || pendingApproveItem.clientId?.id || pendingApproveItem.clientId,
                          expoId: selectedExpo?._id || selectedExpo?.id,
                          status: "Blocked",
                          approvalStatus: "Approved",
                          remarks: "Booking Approved by Admin"
                        });
                        if (res.status) {
                          toast.success("Booking Approved Successfully!");
                          setSelectedBlockItem(prev => ({ ...prev, approvalStatus: "Approved" }));
                          fetchClientFollowupsList(selectedExpo?._id || selectedExpo?.id, clientFollowupStatus, clientFollowupsPage, clientFollowupsLimit, clientFollowupSearchTerm, selectedManagerFilter, selectedGroupFilter);
                        } else {
                          toast.error(res.response?.message || "Failed to approve");
                        }
                      }
                      setPendingApproveItem(null);
                    }}
                  >
                    Yes, Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showStallDetailConfirm && stallDetailConfirmData && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1200 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg text-center">
              <div className="modal-body p-32">
                <div className="d-flex justify-content-center mb-3">
                  <div className={`rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px ${stallDetailConfirmData.action === 'Approved' ? 'bg-success-focus' : 'bg-danger-focus'
                    }`}>
                    <Icon
                      icon={stallDetailConfirmData.action === 'Approved' ? "solar:check-circle-bold" : "solar:close-circle-bold"}
                      className={`text-xxl ${stallDetailConfirmData.action === 'Approved' ? 'text-success' : 'text-danger'}`}
                      style={{ fontSize: "32px" }}
                    />
                  </div>
                </div>
                <h5 className="mb-12 fw-bold">Confirm {stallDetailConfirmData.action === 'Approved' ? 'Approval' : 'Rejection'}</h5>
                <p className="text-secondary-light mb-24">
                  Are you sure you want to {stallDetailConfirmData.action === 'Approved' ? 'approve' : 'reject'} Stall <strong>{stallDetailConfirmData.stallNo}</strong>?
                </p>
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary radius-8 px-32"
                    onClick={() => {
                      setShowStallDetailConfirm(false);
                      setStallDetailConfirmData(null);
                    }}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className={`btn radius-8 px-32 text-white fw-bold ${stallDetailConfirmData.action === 'Approved' ? 'btn-success' : 'btn-danger'
                      }`}
                    style={{
                      backgroundColor: stallDetailConfirmData.action === 'Approved' ? "#16a34a" : "#dc2626",
                      border: "none"
                    }}
                    onClick={async () => {
                      setShowStallDetailConfirm(false);
                      const action = stallDetailConfirmData.action;
                      const stallNo = stallDetailConfirmData.stallNo;
                      setStallDetailConfirmData(null);

                      const res = await updateClientFollowupStatus({
                        followupId: selectedBlockItem._id,
                        clientId: selectedBlockItem.clientId?._id || selectedBlockItem.clientId?.id || selectedBlockItem.clientId,
                        expoId: selectedExpo?._id || selectedExpo?.id,
                        status: "Blocked",
                        stallNo: stallNo,
                        approvalStatus: action,
                        remarks: action === 'Approved' ? "Booking Approved by Admin" : "Booking Rejected by Admin"
                      });
                      if (res.status) {
                        toast.success(`Stall ${stallNo} ${action === 'Approved' ? 'Approved Successfully' : 'Rejected'}`);
                        const updatedFollowup = res.response?.data || res.response;
                        if (updatedFollowup) {
                          setSelectedBlockItem(updatedFollowup);
                        }
                        fetchClientFollowupsList(selectedExpo?._id || selectedExpo?.id, clientFollowupStatus, clientFollowupsPage, clientFollowupsLimit, clientFollowupSearchTerm, selectedManagerFilter, selectedGroupFilter);
                      } else {
                        toast.error(res.response?.message || `Failed to ${action.toLowerCase()}`);
                      }
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Force Reject Stall Modal */}
      {showForceRejectModal && forceRejectData && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1200 }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 bg-base">
              <div className="modal-header py-16 px-24 border-bottom">
                <h5 className="modal-title fw-bold text-dark">Force Reject Stall {forceRejectData.stallNo}</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowForceRejectModal(false);
                  setForceRejectData(null);
                }}></button>
              </div>
              <div className="modal-body p-24">
                <p className="text-secondary-light mb-16">
                  You are about to force reject a stall that is already blocked or booked. This action will release the stall back to the inventory.
                </p>
                <div className="mb-16">
                  <label className="form-label fw-bold text-dark">
                    Type <strong>Reject the block</strong> to confirm <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="Type 'Reject the block'"
                    value={forceRejectInput}
                    onChange={(e) => setForceRejectInput(e.target.value)}
                  />
                </div>
                <div className="mb-24">
                  <label className="form-label fw-bold text-dark">
                    Reason for Rejection <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control radius-8"
                    rows="3"
                    placeholder="Enter reason..."
                    value={forceRejectReason}
                    onChange={(e) => setForceRejectReason(e.target.value)}
                  ></textarea>
                </div>
                <div className="d-flex align-items-center justify-content-end gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary radius-8 px-24"
                    onClick={() => {
                      setShowForceRejectModal(false);
                      setForceRejectData(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger radius-8 px-24 text-white fw-bold"
                    onClick={async () => {
                      if (forceRejectInput !== "Reject the block") {
                        toast.error("Please type the exact confirmation phrase: Reject the block");
                        return;
                      }
                      if (!forceRejectReason.trim()) {
                        toast.error("Please enter a reason for rejection");
                        return;
                      }

                      const stallNo = forceRejectData.stallNo;
                      setShowForceRejectModal(false);
                      setForceRejectData(null);

                      const res = await updateClientFollowupStatus({
                        followupId: selectedBlockItem._id,
                        clientId: selectedBlockItem.clientId?._id || selectedBlockItem.clientId?.id || selectedBlockItem.clientId,
                        expoId: selectedExpo?._id || selectedExpo?.id,
                        status: "Blocked",
                        stallNo: stallNo,
                        approvalStatus: "Rejected",
                        remarks: forceRejectReason.trim()
                      });

                      if (res.status) {
                        toast.success(`Stall ${stallNo} Force Rejected Successfully`);
                        const updatedFollowup = res.response?.data || res.response;
                        if (updatedFollowup) {
                          setSelectedBlockItem(updatedFollowup);
                        }
                        fetchClientFollowupsList(selectedExpo?._id || selectedExpo?.id, clientFollowupStatus, clientFollowupsPage, clientFollowupsLimit, clientFollowupSearchTerm, selectedManagerFilter, selectedGroupFilter);
                      } else {
                        toast.error(res.response?.message || "Failed to force reject stall");
                      }
                    }}
                  >
                    Reject Stall
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showBulkUploadModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1150 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg overflow-hidden">
              <div
                className="modal-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
                style={{
                  background:
                    "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
                }}
              >
                <div>
                  <h6 className="modal-title text-white mb-2 fw-bold d-flex align-items-center gap-2">
                    <Icon icon="solar:upload-minimalistic-bold" className="text-xl" />
                    Upload {mainTab === "visitor_enquiry" ? "Visitor Enquiries" : "Stall Enquiries"} Data
                  </h6>
                  <p className="text-white opacity-75 text-xs mb-0">
                    Expo: <span className="fw-semibold">{selectedExpo?.expoName || selectedExpo?.name}</span>
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowBulkUploadModal(false);
                    setSelectedExcelFile(null);
                    setUploadResult(null);
                  }}
                ></button>
              </div>

              <div className="modal-body p-24">
                {/* Active Mode Notice */}
                <div className="d-flex align-items-center justify-content-between p-12 radius-8 mb-20 bg-neutral-50 border">
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge px-10 py-6 radius-4 fw-bold uppercase text-white bg-danger-600">
                      Target: {mainTab === "visitor_enquiry" ? "Visitor Enquiries" : "Stall Enquiries"}
                    </span>
                    <span className="text-xs text-secondary-light">
                      Data will be stored as enquiries for this exhibition.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm radius-8 d-inline-flex align-items-center gap-2 fw-bold"
                    onClick={handleDownloadSampleExcel}
                  >
                    <Icon icon="solar:download-bold" className="text-base" />
                    <span>Download Sample Excel</span>
                  </button>
                </div>

                {/* Sample Header & Data Preview Table */}
                <div className="mb-20">
                  <label className="form-label text-xs fw-bold text-secondary-light uppercase mb-8 d-flex align-items-center gap-1">
                    <Icon icon="solar:document-text-bold" className="text-danger-600" />
                    Header & Sample Row Preview:
                  </label>
                  <div className="table-responsive border radius-8 scroll-sm" style={{ maxHeight: "150px" }}>
                    <table className="table table-sm table-bordered mb-0 text-xs">
                      <thead style={{ background: "#f8fafc", color: "#1e293b", fontWeight: "700" }}>
                        <tr>
                          {mainTab === "stall_enquiry" ? (
                            <>
                              <th className="py-8 px-12">Name *</th>
                              <th className="py-8 px-12">Company Name *</th>
                              <th className="py-8 px-12">Mobile Number *</th>
                              <th className="py-8 px-12">Email</th>
                              <th className="py-8 px-12">Business Category</th>
                              <th className="py-8 px-12">Stall Size</th>
                              <th className="py-8 px-12">City</th>
                              <th className="py-8 px-12">Remarks</th>
                            </>
                          ) : (
                            <>
                              <th className="py-8 px-12">Name *</th>
                              <th className="py-8 px-12">Company Name</th>
                              <th className="py-8 px-12">Mobile Number *</th>
                              <th className="py-8 px-12">Email</th>
                              <th className="py-8 px-12">City</th>
                              <th className="py-8 px-12">Business Category</th>
                              <th className="py-8 px-12">Product of Interest</th>
                              <th className="py-8 px-12">Known Source</th>
                              <th className="py-8 px-12">Remarks</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="text-secondary-light">
                          {mainTab === "stall_enquiry" ? (
                            <>
                              <td className="py-8 px-12 fw-medium text-dark">John Doe</td>
                              <td className="py-8 px-12">ABC Interiors</td>
                              <td className="py-8 px-12 text-primary-600 fw-bold">9876543210</td>
                              <td className="py-8 px-12">john@example.com</td>
                              <td className="py-8 px-12">Furniture</td>
                              <td className="py-8 px-12">3x3</td>
                              <td className="py-8 px-12">Coimbatore</td>
                              <td className="py-8 px-12">Interested in stall booking</td>
                            </>
                          ) : (
                            <>
                              <td className="py-8 px-12 fw-medium text-dark">Ramesh Kumar</td>
                              <td className="py-8 px-12">RK Design Studio</td>
                              <td className="py-8 px-12 text-primary-600 fw-bold">9876543210</td>
                              <td className="py-8 px-12">ramesh@example.com</td>
                              <td className="py-8 px-12">Chennai</td>
                              <td className="py-8 px-12">Interior Design</td>
                              <td className="py-8 px-12">Wooden Furniture</td>
                              <td className="py-8 px-12">Newspaper</td>
                              <td className="py-8 px-12">Visitor registration</td>
                            </>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Upload Drag & Drop Area */}
                <div
                  className="border-dashed radius-12 p-24 text-center cursor-pointer transition-all mb-16"
                  style={{
                    borderColor: "#ed1c24",
                    backgroundColor: "rgba(237, 28, 36, 0.02)",
                    border: "2px dashed #ed1c24",
                  }}
                  onClick={() => document.getElementById("enquiryBulkFileInput").click()}
                >
                  <input
                    type="file"
                    id="enquiryBulkFileInput"
                    className="d-none"
                    accept=".xlsx, .xls, .csv"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setSelectedExcelFile(e.target.files[0]);
                        setUploadResult(null);
                      }
                      e.target.value = "";
                    }}
                  />
                  {selectedExcelFile ? (
                    <div className="d-flex align-items-center justify-content-center gap-3">
                      <Icon icon="solar:file-check-bold" className="text-success text-3xl" />
                      <div className="text-start">
                        <div className="text-sm fw-bold text-dark">{selectedExcelFile.name}</div>
                        <div className="text-xs text-secondary-light">
                          {(selectedExcelFile.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <Icon
                        icon="line-md:close"
                        className="text-danger cursor-pointer ms-12 text-xl"
                        title="Remove file"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedExcelFile(null);
                          setUploadResult(null);
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <Icon icon="solar:cloud-upload-bold" className="text-danger-600 text-4xl mb-8" />
                      <p className="text-sm fw-semibold text-dark mb-4">
                        Click to select or drag and drop your Excel file here
                      </p>
                      <p className="text-xs text-secondary-light mb-0">
                        Supports: .xlsx, .xls, .csv
                      </p>
                    </>
                  )}
                </div>

                {/* Validation Note */}
                <div className="p-12 radius-8 bg-info-50 border border-info-200 mb-16">
                  <div className="d-flex gap-2">
                    <Icon icon="solar:info-circle-bold" className="text-info-600 text-lg flex-shrink-0 mt-2" />
                    <div className="text-xs text-info-800">
                      <div className="fw-bold mb-2">Validation Rules:</div>
                      <ul className="ps-16 mb-0">
                        <li><strong>Mobile Number:</strong> Must be exactly 10 digits.</li>
                        <li><strong>Duplicate Protection:</strong> Numbers already existing in this expo or duplicated in the file will be skipped automatically.</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Result Display */}
                {uploadResult && (
                  <div className="p-16 radius-8 border mb-16 bg-neutral-50">
                    <div className="d-flex align-items-center gap-3 mb-12 flex-wrap">
                      <span className="badge bg-success-600 text-white px-12 py-6 radius-4 fw-bold">
                        Inserted: {uploadResult.inserted || 0}
                      </span>
                      <span className="badge bg-warning-600 text-white px-12 py-6 radius-4 fw-bold">
                        Skipped (Duplicates): {uploadResult.skipped || 0}
                      </span>
                      {uploadResult.errorCount > 0 && (
                        <span className="badge bg-danger-600 text-white px-12 py-6 radius-4 fw-bold">
                          Errors: {uploadResult.errorCount}
                        </span>
                      )}
                    </div>

                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <div className="border radius-8 p-12 bg-white" style={{ maxHeight: "120px", overflowY: "auto" }}>
                        <div className="text-xs fw-bold text-danger mb-4">Failed Rows:</div>
                        <ul className="text-xs text-danger ps-16 mb-0">
                          {uploadResult.errors.map((err, idx) => (
                            <li key={idx}>Row {err.row}: {err.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer border-top p-16 bg-neutral-50 d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary radius-8 px-24 fw-bold"
                  onClick={() => {
                    setShowBulkUploadModal(false);
                    setSelectedExcelFile(null);
                    setUploadResult(null);
                  }}
                  disabled={isBulkUploading}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-danger radius-8 px-24 fw-bold d-inline-flex align-items-center gap-2"
                  disabled={!selectedExcelFile || isBulkUploading}
                  onClick={handleBulkUploadEnquiriesSubmit}
                  style={{ backgroundColor: "#ed1c24", borderColor: "#ed1c24" }}
                >
                  {isBulkUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="solar:upload-minimalistic-bold" className="text-lg" />
                      <span>Upload & Process</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PresentExpoLayer;
